import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import api from "../../utils/api";
import LiveStrip from "../../components/LiveStrip";
import ReviewsBar from "../../components/ReviewsBar";
import EmaLiveOpsSection from "../../components/ema/EmaLiveOpsSection";
import "../../styles/parkingVsTaxi.css";

const formatTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London" });
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" });
};

const formatDuration = (mins) => {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  if (h <= 0) return `${m} mins`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatUkPostcode = (pc) => {
  const s = String(pc || "").toUpperCase().replace(/\s+/g, "");
  if (!s) return "";
  if (s.length <= 3) return s;
  return `${s.slice(0, -3)} ${s.slice(-3)}`;
};

const useCompactMobile = (maxWidth = 768) => {
  const getMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  };
  const [isCompact, setIsCompact] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIsCompact(!!e.matches);
    setIsCompact(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [maxWidth]);

  return isCompact;
};

const normalizeReturnPlanToView = (data, form) => {
  const taxiMode = data?.modes?.taxi || {};
  const driveMode = data?.modes?.self_drive || {};
  const railMode = data?.modes?.rail || {};
  const googleTransit = railMode?.google_transit || null;
  const railSegments = Array.isArray(railMode?.segments) ? railMode.segments : [];
  const gatewaySeg = railSegments.find((s) => s.id === "terminal_to_gateway") || null;
  const railSeg = railSegments.find((s) => s.id === "rail") || null;
  const lastMileSeg = railSegments.find((s) => s.id === "last_mile") || null;
  const railJourney = railMode?.best_candidate?.journey || null;
  const railFarePence = railMode?.best_candidate?.fare?.total_pence;
  const railFareGbp = Number.isFinite(Number(railFarePence)) ? Number((Number(railFarePence) / 100).toFixed(2)) : null;
  const gatewayCrs = gatewaySeg?.to || railSeg?.from || "EMD";
  const destCrs = railSeg?.to || "Home station";
  const homePostcode = form?.pickup_postcode || "Home";
  const terminalExitAt = data?.return_guidance?.terminal_exit_at || null;

  const normalizedRailJourney = railJourney
    ? {
        ...railJourney,
        target_delta_mins: 0,
        fare: railFareGbp != null ? { total_gbp: railFareGbp } : null,
      }
    : null;

  return {
    flight: data?.flight || null,
    recommendation: data?.recommendation || null,
    return_guidance: data?.return_guidance || null,
    input_prefill: {
      pickup_postcode: homePostcode,
      flight_number: form?.flight_number || "",
      flight_date: form?.flight_date || "",
      flight_direction: "Arrival",
      flight_anchor_utc: data?.flight?.arrival || data?.flight?.scheduled_arrival || form?.flight_anchor_utc || "",
      travel_direction: "from_ema",
      bags: Number(form?.bags || 0),
      parking_duration_days: Number(form?.parking_duration_days || 4),
    },
    rail: {
      request: {
        terminal_exit_at: terminalExitAt,
        terminal_arrive_by: terminalExitAt,
        station_depart_after: terminalExitAt,
        gateway_crs: gatewayCrs,
        gateway_depart_after: data?.modes?.rail?.request?.gateway_depart_after || null,
        selected_gateway_depart_at: data?.modes?.rail?.request?.selected_gateway_depart_at || null,
        resolved_dest_crs: gatewayCrs,
        dest_crs: gatewayCrs,
      },
      segments: [
        {
          id: "first_mile",
          from: "EMA",
          to: gatewayCrs,
          mode: "road",
          eta_safe_mins: gatewaySeg?.eta_safe_mins ?? null,
          status: railMode?.overall_disruption?.level || "unknown",
          headline: gatewaySeg?.headline || "Terminal to gateway",
        },
        {
          id: "rail",
          from: gatewayCrs,
          to: destCrs,
          mode: "rail",
          status: railMode?.overall_disruption?.level || "unknown",
          journeys: normalizedRailJourney ? [normalizedRailJourney] : [],
          checked_sources: ["nr_journey", "nr_incidents"],
          last_checked: new Date().toISOString(),
        },
        {
          id: "airport_hop",
          from: destCrs,
          to: homePostcode,
          mode: lastMileSeg?.mode || "walk",
          eta_safe_mins: lastMileSeg?.walk_mins ?? null,
          status: "unknown",
          headline: "Station to home",
        },
      ],
      best_candidate: {
        selected_station: gatewayCrs,
        meets_target: true,
        target_best_delta_mins: 0,
        target_band: "green",
        fare: railFareGbp != null ? { total_gbp: railFareGbp } : null,
        journeys: normalizedRailJourney ? [normalizedRailJourney] : [],
      },
      overall_disruption: railMode?.overall_disruption || { level: "unknown", reasons: ["no_data"] },
      weather: railMode?.weather || null,
      no_rail_reason: normalizedRailJourney ? null : (railMode?.no_rail_reason || "No rail options found for return journey."),
      google_transit: googleTransit,
      alternate_transit_available: !!railMode?.alternate_transit_available,
    },
    taxi: {
      road: taxiMode?.road || null,
      fare: {
        price: taxiMode?.cost_gbp ?? null,
        source: taxiMode?.cost_source || null,
        distance_km: taxiMode?.road?.distance_km ?? null,
        vehicle: null,
      },
      weather: taxiMode?.weather || null,
    },
    drive: {
      road: driveMode?.road || null,
      fuel: {
        fuel: { fuelCost: driveMode?.fuel_cost_gbp ?? driveMode?.cost_gbp ?? null },
        distance_km: driveMode?.road?.distance_km ?? null,
      },
      parking: { all: [{ product_name: "EMA exit", transfer_minutes: 0, price_gbp: 0 }] },
      parking_warning: null,
      weather: driveMode?.weather || null,
    },
    details: null,
    diagnostics: data?.diagnostics || null,
  };
};

const applyUiProjectionPreferred = (baseResult, projection) => {
  if (!projection || typeof projection !== "object") return baseResult;
  const base = baseResult && typeof baseResult === "object" ? JSON.parse(JSON.stringify(baseResult)) : {};
  const finiteOrNull = (v) => {
    if (v == null) return null;
    if (typeof v === "string" && v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const hero = projection?.hero || {};
  const cards = projection?.cards || {};
  const timelines = projection?.timelines || {};
  const sourceLevels = projection?.source_levels || {};
  const flow = String(projection?.flow || "").toLowerCase();

  if (!base.recommendation || typeof base.recommendation !== "object") base.recommendation = {};
  if (hero?.recommendation_summary) base.recommendation.summary = hero.recommendation_summary;
  if (projection?.recommended_mode) base.recommendation.recommended_mode = projection.recommended_mode;
  if (projection?.overall_level) {
    base.recommendation.overall_disruption = {
      ...(base.recommendation.overall_disruption || {}),
      level: String(projection.overall_level || "").toLowerCase(),
    };
  }

  if (!base.input_prefill || typeof base.input_prefill !== "object") base.input_prefill = {};
  if (projection?.flight_number && !base.input_prefill.flight_number) base.input_prefill.flight_number = projection.flight_number;
  if (projection?.flight_date && !base.input_prefill.flight_date) base.input_prefill.flight_date = projection.flight_date;
  if (projection?.direction && !base.input_prefill.flight_direction) base.input_prefill.flight_direction = projection.direction;
  if (projection?.anchor_utc && !base.input_prefill.flight_anchor_utc) base.input_prefill.flight_anchor_utc = projection.anchor_utc;
  if (projection?.postcode && !base.input_prefill.pickup_postcode) base.input_prefill.pickup_postcode = projection.postcode;
  if (!base.input_prefill.travel_direction) {
    base.input_prefill.travel_direction = flow === "return" ? "from_ema" : "to_ema";
  }

  if (!base.flight || typeof base.flight !== "object") base.flight = {};
  if (hero?.flight_status) base.flight.status = hero.flight_status;
  if (projection?.anchor_status) base.flight.status = projection.anchor_status;
  if (projection?.anchor_utc) {
    if (String(projection?.direction || "").toLowerCase().startsWith("arr")) {
      if (!base.flight.scheduled_arrival_utc) base.flight.scheduled_arrival_utc = projection.anchor_utc;
    } else {
      if (!base.flight.scheduled_departure_utc) base.flight.scheduled_departure_utc = projection.anchor_utc;
    }
  }
  if (projection?.flight_number && !base.flight.number) base.flight.number = projection.flight_number;

  if (!base.modes || typeof base.modes !== "object") base.modes = {};
  if (!base.details || typeof base.details !== "object") base.details = {};
  const modeKeys = ["train", "self_drive", "taxi"];
  modeKeys.forEach((m) => {
    const card = cards?.[m] || {};
    const tl = timelines?.[m] || {};
    if (!base.modes[m] || typeof base.modes[m] !== "object") base.modes[m] = {};
    if (card?.summary) base.modes[m].summary = card.summary;
    if (card?.leave_home_at) base.modes[m].leave_by = card.leave_home_at;
    if (finiteOrNull(card?.cost_gbp) != null) base.modes[m].cost_gbp = Number(card.cost_gbp);
    if (finiteOrNull(card?.duration_mins) != null) base.modes[m].duration_mins = Number(card.duration_mins);
    if (card?.status) {
      base.modes[m].overall_disruption = {
        ...(base.modes[m].overall_disruption || {}),
        level: String(card.status || "").toLowerCase(),
      };
    }

    if (!base.details[m] || typeof base.details[m] !== "object") base.details[m] = {};
    if (Array.isArray(tl?.steps) && tl.steps.length) {
      base.details[m].steps = tl.steps.map((s) => ({
        id: s?.id || null,
        at: s?.at || null,
        title: s?.title || null,
        from: s?.from || null,
        to: s?.to || null,
        mode: s?.mode || null,
        duration_mins: finiteOrNull(s?.duration_mins),
        status: s?.status || null,
        status_reason: s?.status_reason || null,
        notes: Array.isArray(s?.notes) ? s.notes : [],
        confidence: s?.confidence || null,
        source: s?.source || null,
        disclaimer: s?.disclaimer || null,
      }));
    }
    if (Array.isArray(tl?.terminal_common_steps)) {
      base.details[m].terminal_common_steps = tl.terminal_common_steps.map((s) => ({
        id: s?.id || null,
        at: s?.at || null,
        title: s?.title || null,
        from: s?.from || null,
        to: s?.to || null,
        mode: s?.mode || null,
        duration_mins: finiteOrNull(s?.duration_mins),
        status: s?.status || null,
        status_reason: s?.status_reason || null,
        notes: Array.isArray(s?.notes) ? s.notes : [],
        confidence: s?.confidence || null,
        source: s?.source || null,
        disclaimer: s?.disclaimer || null,
      }));
    }
    if (finiteOrNull(tl?.terminal_margin_mins) != null) {
      base.details[m].margin_to_terminal_target_mins = Number(tl.terminal_margin_mins);
    }
    if (tl?.checked_at) {
      base.details[m].checked_at = tl.checked_at;
    }
  });

  if (!base.rail || typeof base.rail !== "object") base.rail = {};
  if (!base.taxi || typeof base.taxi !== "object") base.taxi = {};
  if (!base.drive || typeof base.drive !== "object") base.drive = {};
  if (sourceLevels?.rail) {
    base.rail.overall_disruption = {
      ...(base.rail.overall_disruption || {}),
      level: String(sourceLevels.rail || "").toLowerCase(),
    };
  }
  if (sourceLevels?.road) {
    if (!base.taxi.road || typeof base.taxi.road !== "object") base.taxi.road = {};
    if (!base.drive.road || typeof base.drive.road !== "object") base.drive.road = {};
    base.taxi.road.status = String(sourceLevels.road || "").toLowerCase();
    base.drive.road.status = String(sourceLevels.road || "").toLowerCase();
  }
  if (sourceLevels?.taxi_road) {
    if (!base.taxi.road || typeof base.taxi.road !== "object") base.taxi.road = {};
    base.taxi.road.status = String(sourceLevels.taxi_road || "").toLowerCase();
  }
  if (sourceLevels?.self_drive_road) {
    if (!base.drive.road || typeof base.drive.road !== "object") base.drive.road = {};
    base.drive.road.status = String(sourceLevels.self_drive_road || "").toLowerCase();
  }
  if (sourceLevels?.weather) {
    if (!base.rail.weather || typeof base.rail.weather !== "object") base.rail.weather = {};
    base.rail.weather.risk = {
      ...(base.rail.weather.risk || {}),
      level: String(sourceLevels.weather || "").toLowerCase(),
    };
  }

  if (projection?.source_levels && typeof projection.source_levels === "object") {
    base.source_levels = { ...(base.source_levels || {}), ...projection.source_levels };
  }
  if (projection?.monitor_targets && typeof projection.monitor_targets === "object") {
    base.monitor_targets = projection.monitor_targets;
  }
  if (projection?.policy_state && typeof projection.policy_state === "object") {
    base.policy_state = projection.policy_state;
  }
  if (projection?.source_monitor_execution_id != null) {
    base.source_monitor_execution_id = projection.source_monitor_execution_id;
  }
  if (projection?.source_monitor_plan_version != null) {
    base.source_monitor_plan_version = projection.source_monitor_plan_version;
  }
  if (Array.isArray(hero?.terminal_common_steps)) {
    if (!base.details || typeof base.details !== "object") base.details = {};
    base.details.terminal_common = {
      ...(base.details.terminal_common || {}),
      steps: hero.terminal_common_steps.map((s) => ({
        id: s?.id || null,
        at: s?.at || null,
        title: s?.title || null,
        from: s?.from || null,
        to: s?.to || null,
        mode: s?.mode || null,
        duration_mins: finiteOrNull(s?.duration_mins),
        status: s?.status || null,
        status_reason: s?.status_reason || null,
        notes: Array.isArray(s?.notes) ? s.notes : [],
        confidence: s?.confidence || null,
        source: s?.source || null,
        disclaimer: s?.disclaimer || null,
      })),
    };
  }
  base.ui_projection = projection;

  return base;
};

const SEO_COLLAPSIBLE_SECTIONS = [
  {
    heading: "How this return journey planner calculates your route home",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          This return journey planner is designed to help arriving passengers leave East Midlands Airport with real-time awareness of flight arrival timing, terminal exit windows, rail options and road conditions.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          When you enter your home postcode and inbound flight details, the system builds an arrival-to-home baseline for each available travel mode: train, taxi and self-drive. It estimates when you are likely to leave the terminal after disembarkation, immigration and baggage reclaim.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          If you are planning the outward journey to the airport instead, use the{" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            main live journey planner for departure and towards-EMA trips
          </a>
          .
        </p>
        <p style={{ margin: "0 0 10px" }}>
          For rail journeys, we use official National Rail data feeds to generate route options from EMA gateway stations such as Derby, Nottingham, East Midlands Parkway and Long Eaton. Each rail leg is mapped and baselined so we can assess onward transfer times and interchange risk on the way home.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          The return recommendation is also informed by monitored EMA journey history, with more than 900 evaluated journeys and thousands of stored route observations already collected across UK rail and road journeys, including proprietary gateway-to-terminal transfer behaviour.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          For road journeys, we use live Google traffic data, TomTom congestion checks and National Highways incident feeds to evaluate motorway and major A-road conditions leaving EMA. This allows us to establish a realistic travel duration from the airport back to your postcode under current conditions.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          Taxi comparisons use live fare calculations where available, while self-drive plans account for pickup parking and transfer timings. All modes are then assessed against your flight arrival and estimated terminal exit time.
        </p>
        <p style={{ margin: 0 }}>
          The result is not just a route suggestion; it is a time-aware recommendation for getting home from East Midlands Airport after landing.
        </p>
      </>
    ),
  },
  {
    heading: "What we monitor after your flight lands",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          After your return journey is created, the system continues monitoring your route in the period around arrival and terminal exit.
        </p>
        <p style={{ margin: "0 0 10px" }}>We track:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Flight arrival status and schedule changes.</li>
          <li>Estimated terminal-exit timing after disembarkation, immigration and baggage reclaim.</li>
          <li>Official National Rail disruption updates across each CRS station involved in your route home.</li>
          <li>Live departure and arrival board checks during hyper and in-transit monitoring windows.</li>
          <li>RailData service alerts affecting specific lines and interchanges.</li>
          <li>National Highways incident feeds for motorway and trunk road segments leaving EMA.</li>
          <li>Live Google traffic conditions to detect congestion or unexpected delays.</li>
          <li>TomTom corridor-level congestion checks on the main road route home.</li>
          <li>Weather forecasts at both EMA and your home postcode.</li>
        </ul>
        <p style={{ margin: 0 }}>
          Each data source is evaluated against your original return baseline. If a change affects your journey home, the system recalculates and updates the recommendation.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          Verified users can receive updates when the return journey shifts into light green, amber or red conditions after landing.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          Verify your email once the journey is built so the route from EMA to home can stay actively monitored and you can receive regular updates if issues develop after landing.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          If you are collecting or dropping off someone at the terminal rather than travelling onward yourself, compare pickup and parking options with the{" "}
          <a href="/east-midlands-airport/drop-off-calculator" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            EMA drop-off and pickup calculator
          </a>
          .
        </p>
      </>
    ),
  },
  {
    heading: "Hyper monitoring around arrival and terminal exit",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          In the final hours before your flight lands and through the terminal exit window, monitoring automatically enters a higher-frequency hyper mode.
        </p>
        <p style={{ margin: "0 0 10px" }}>During this window, the system increases checks across:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Flight arrival timing updates.</li>
          <li>Road traffic conditions from EMA to home.</li>
          <li>Rail services and gateway transfers.</li>
          <li>Live departure and arrival board checks on the selected rail path.</li>
          <li>TomTom congestion assurance on the main driving corridor.</li>
          <li>Airport and home weather conditions.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          This allows us to detect late-developing congestion, rail cancellations or flight timing shifts that affect the return journey.
        </p>
        <p style={{ margin: 0 }}>
          If significant changes occur, verified users may receive alerts advising them to switch mode or expect a slower trip home. Hyper monitoring is designed to reduce uncertainty after landing.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          Email verification is what enables the active monitored journey and keeps the post-landing update flow running through the hyper window.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          For the opposite problem, deciding how early to set off for check-in, security and terminal arrival, use{" "}
          <a href="/east-midlands-airport/when-to-leave-for-east-midlands-airport" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            when to leave for East Midlands Airport
          </a>
          .
        </p>
      </>
    ),
  },
  {
    heading: "Independent, data-driven return monitoring",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          This journey monitoring system operates independently using a combination of official transport data feeds, live traffic services and real-time weather forecasting.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          It is designed to provide passengers with a structured, data-driven view of their route home from East Midlands Airport, rather than relying solely on static timetables or generic drive times.
        </p>
        <p style={{ margin: "0 0 10px" }}>By combining:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Official rail data.</li>
          <li>National Highways incident information.</li>
          <li>Live Google traffic.</li>
          <li>TomTom congestion assurance.</li>
          <li>Live rail departure and arrival board checks.</li>
          <li>Flight status updates.</li>
          <li>Airport parking and transfer modelling.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          We create a continuously evaluated return journey profile tailored to your specific arrival.
        </p>
        <p style={{ margin: 0 }}>
          The aim is simple: help you leave EMA at the right time, using the most reliable transfer mode available for the trip home.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          To keep that journey actively monitored after it is created, verify your email so updates can be sent if the return profile changes.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          If you want a broader outbound route comparison before travelling to EMA, the{" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            live journey planner
          </a>
          {" "}and{" "}
          <a href="/train-vs-taxi-east-midlands-airport" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            train vs taxi guide
          </a>
          {" "}cover that side of the trip.
        </p>
      </>
    ),
  },
];

const SEO_FAQ_SECTIONS = [
  {
    question: "How do you estimate when I can leave EMA after landing?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          The planner estimates a terminal-exit window using your flight arrival time plus expected disembarkation, immigration and baggage-reclaim timings.
        </p>
        <p style={{ margin: "0 0 10px" }}>That means the return recommendation reflects:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Flight arrival timing.</li>
          <li>Immigration processing.</li>
          <li>Baggage reclaim timing.</li>
          <li>Terminal exit assumptions.</li>
        </ul>
        <p style={{ margin: 0 }}>
          It is designed to produce a realistic start time for the home journey rather than assuming you leave the terminal immediately after touchdown.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          For timing the outward airport run instead, use{" "}
          <a href="/east-midlands-airport/when-to-leave-for-east-midlands-airport" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            when to leave for East Midlands Airport
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: "Which stations can be used for the return journey from EMA?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>East Midlands Airport does not have its own rail station. Return journeys home often route via:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>East Midlands Parkway.</li>
          <li>Derby.</li>
          <li>Nottingham.</li>
          <li>Long Eaton.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>From the terminal, the planner models a transfer to the rail gateway before the onward train journey home.</p>
        <p style={{ margin: 0 }}>
          The most reliable gateway depends on your home postcode, service frequency, and onward interchange timing. The system evaluates each viable gateway before making a recommendation.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          If you want to compare rail against road before travelling to the airport, see{" "}
          <a href="/train-vs-taxi-east-midlands-airport" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            train vs taxi to East Midlands Airport
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: "How reliable is driving home from East Midlands Airport?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          Driving reliability depends largely on conditions along the major motorway and A-road corridors leaving EMA toward your destination.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          Traffic incidents, motorway lane closures and peak-hour congestion can significantly affect journey time.
        </p>
        <p style={{ margin: "0 0 10px" }}>This monitoring system uses:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>National Highways incident feeds.</li>
          <li>Live Google traffic data.</li>
          <li>TomTom congestion checks on the main corridor.</li>
          <li>Weather forecasts.</li>
        </ul>
        <p style={{ margin: 0 }}>
          This helps assess corridor-level risk and detect deviations from baseline travel times.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          For pickup, short-stay or rapid drop-off planning around the terminal itself, use the{" "}
          <a href="/east-midlands-airport/drop-off-calculator" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            East Midlands drop-off calculator
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: "What happens if there is a rail disruption after I land?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>If your return journey includes rail travel, we monitor:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Official National Rail disruption feeds.</li>
          <li>RailData alerts at each CRS station.</li>
          <li>Live departure and arrival board checks during hyper and in-transit monitoring.</li>
          <li>Interchange risk at gateway stations.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          If a cancellation, delay or service change affects your planned route home, the system reassesses the return plan and may recommend an alternative mode.
        </p>
        <p style={{ margin: 0 }}>
          In higher-risk windows, notifications are sent automatically to verified users when the return journey shifts into light green, amber or red conditions.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          Verify your email to make sure that monitored return journey updates can actually be delivered if rail conditions change after landing.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          If you are still deciding whether road or rail is the better way to reach the airport on the outward trip, compare both in the{" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            live journey planner
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: "What is Hyper Mode for the return journey?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          Hyper Mode activates automatically around your expected arrival and terminal exit window.
        </p>
        <p style={{ margin: "0 0 10px" }}>During this period, monitoring frequency increases and the system checks:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Flight arrival adjustments.</li>
          <li>Road congestion changes.</li>
          <li>Rail service updates.</li>
          <li>Weather shifts.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          This allows the return recommendation to adapt to late-developing disruptions.
        </p>
        <p style={{ margin: 0 }}>
          If significant changes are detected, alerts are issued so you can adjust in time.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          Email verification is important here because it turns the saved route into an actively monitored journey from EMA to home, with regular updates when conditions change.
        </p>
      </>
    ),
  },
  {
    question: "Is this an official East Midlands Airport system?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          No. This is an independent journey monitoring system that uses official transport data feeds and live traffic services to help passengers plan the journey home from East Midlands Airport.
        </p>
        <p style={{ margin: 0 }}>
          It is designed to provide a structured, data-driven view of route conditions, but it does not replace airline or airport guidance.
        </p>
      </>
    ),
  },
];

function CollapsibleSeoSections() {
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, color: "#64748b", marginBottom: 8 }}>
        HOW IT WORKS
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {SEO_COLLAPSIBLE_SECTIONS.map((section) => (
          <details key={section.heading} className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ {section.heading}</summary>
            <div className="train-collapsible-body" style={{ color: "#475467", fontSize: 14, lineHeight: 1.65 }}>
              {section.body}
            </div>
          </details>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
        Expand each section for methodology, monitoring scope and final-hours operating mode. For outward trips, use the{" "}
        <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
          departure and towards-EMA planner
        </a>
        .
      </div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, color: "#64748b", marginTop: 16, marginBottom: 8 }}>
        FAQS
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {SEO_FAQ_SECTIONS.map((faq) => (
          <details key={faq.question} className="train-collapsible">
            <summary className="train-summary-bar">❓ {faq.question}</summary>
            <div className="train-collapsible-body" style={{ color: "#475467", fontSize: 14, lineHeight: 1.65 }}>
              {faq.answer}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function EmaRelatedToolsSection() {
  return (
    <section
      style={{
        marginTop: 12,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#fff",
      }}
    >
      <strong style={{ color: "#0f172a" }}>Explore East Midlands Airport journey tools:</strong>
      <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
        Use the return planner for getting home after landing, and switch to the sister tools below for outward journeys, pickup/drop-off planning and mode comparisons.
      </div>
      <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0, paddingLeft: 18, color: "#1f2937" }}>
        <li>
          <a href="/east-midlands-airport/return-journey-monitoring">Return journey monitoring from EMA</a>
        </li>
        <li>
          <a href="/east-midlands-airport/live-journey-planner">Live journey planner (leave time + disruption checks)</a>
        </li>
        <li>
          <a href="/east-midlands-airport/birmingham-to-east-midlands-airport">Birmingham to East Midlands Airport</a>
        </li>
        <li>
          <a href="/east-midlands-airport/when-to-leave-for-east-midlands-airport">When to leave for East Midlands Airport</a>
        </li>
        <li>
          <a href="/how-to-get-to-east-midlands-airport">How to get to East Midlands Airport</a>
        </li>
        <li>
          <a href="/train-vs-taxi-east-midlands-airport">Train vs taxi to East Midlands Airport</a>
        </li>
        <li>
          <a href="/east-midlands-airport/drop-off-calculator">EMA drop-off calculator (Rapid vs Short Stay vs Long Stay)</a>
        </li>
        <li>
          <a href="/international-students/uk-arrival-guide">Student arrival assistant (UK airport pickup support)</a>
        </li>
      </ul>
    </section>
  );
}

export default function ReturnFromEMA() {
  const USE_MOCK = false;
  const MOCK_RESPONSE = null; // drop a stored JSON here if you want to mock

  const DEFAULT_PICKUP = "NG1 5FS";
  const DEFAULT_FLIGHT_NUMBER_TO_EMA = "FR9085";
  const DEFAULT_FLIGHT_NUMBER_FROM_EMA = "FR9084";

  const [form, setForm] = useState({
    pickup_postcode: DEFAULT_PICKUP,
    flight_number: DEFAULT_FLIGHT_NUMBER_FROM_EMA,
    flight_date: "",
    flight_direction: "Arrival",
    flight_anchor_utc: "",
    passengers: 2,
    bags: 2,
    preferred_modes: ["taxi", "rail", "self_drive"],
    parking_duration_days: 4,
    buffer_mode: "standard",
  });

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [pickupActiveIdx, setPickupActiveIdx] = useState(-1);
  const [pickupWarning, setPickupWarning] = useState("");
  const [postcodeError, setPostcodeError] = useState("");
  const [postcodeValidating, setPostcodeValidating] = useState(false);
  const [pickupFocused, setPickupFocused] = useState(false);
  const pickupTimer = useRef(null);

  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightActiveIdx, setFlightActiveIdx] = useState(-1);
  const [flightFocused, setFlightFocused] = useState(false);
  const flightTimer = useRef(null);
  const [flightError, setFlightError] = useState("");

  const [flightSchedules, setFlightSchedules] = useState({ Arrival: [], Departure: [] });
  const [flightDates, setFlightDates] = useState([]);
  const [flightDateError, setFlightDateError] = useState("");
  const [flightDirection, setFlightDirection] = useState("Arrival");
  const [travelDirection, setTravelDirection] = useState("from_ema");
  const flightScheduleTimer = useRef(null);
  const flightScheduleRequestSeq = useRef(0);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [subErrors, setSubErrors] = useState([]);
  const [journeyMeta, setJourneyMeta] = useState({
    journey_id: null,
    token: "",
    token_expires_at: null,
    email: "",
    email_verified: false,
    creating: false,
    emailing: false,
    verifying: false,
    email_sent: false,
    note: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const resultContainerRef = useRef(null);
  const loadingTimers = useRef([]);
  const [scrollToResultsPending, setScrollToResultsPending] = useState(false);
  const isCompactMobile = useCompactMobile(768);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryToken = String(searchParams.get("token") || "").trim();
  const stepParam = String(searchParams.get("step") || "").trim().toLowerCase();
  const showMobileResultsScreen = isCompactMobile && stepParam === "results" && !!result;
  const [isTokenBoundJourney, setIsTokenBoundJourney] = useState(false);
  const pickupListboxId = "ema-v2-pickup-suggestions";
  const pickupWarningId = "ema-v2-pickup-warning";
  const postcodeErrorId = "ema-v2-postcode-error";
  const flightListboxId = "ema-v2-flight-suggestions";
  const flightErrorId = "ema-v2-flight-error";
  const flightDateErrorId = "ema-v2-flight-date-error";
  const formErrorId = "ema-v2-form-error";

  const flightNumKey = (n) => String(n || "").replace(/\s+/g, "").toUpperCase();
  const formatScheduleLabel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/London",
    });
  };
  const scheduleAnchorTime = (s) =>
    s?.anchor_time_utc || s?.estimated_time_utc || s?.scheduled_anchor_utc || s?.scheduled_time_utc || null;
  const scheduleDateKey = (s) => {
    const anchor = scheduleAnchorTime(s);
    if (!anchor) {
      const dated = String(s?.date || s?.snapshot_date || "").trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(dated) ? dated : String(dated).slice(0, 10) || "";
    }
    const d = new Date(anchor);
    if (Number.isNaN(d.getTime())) {
      const dated = String(s?.date || s?.snapshot_date || "").trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(dated) ? dated : String(dated).slice(0, 10) || "";
    }
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    return year && month && day ? `${year}-${month}-${day}` : String(anchor).slice(0, 10);
  };
  const scheduleDelayMins = (s) => {
    const est = s?.estimated_time_utc ? new Date(s.estimated_time_utc).getTime() : NaN;
    const sch = (s?.scheduled_anchor_utc || s?.scheduled_time_utc)
      ? new Date(s.scheduled_anchor_utc || s.scheduled_time_utc).getTime()
      : NaN;
    if (!Number.isFinite(est) || !Number.isFinite(sch)) return null;
    return Math.round((est - sch) / 60000);
  };
  const isFutureSchedule = (iso) => {
    if (!iso) return false;
    const t = new Date(iso);
    return !Number.isNaN(t.getTime()) && t.getTime() > Date.now() + 60000;
  };
  const dedupeSchedules = (items = [], directionLabel = "Departure") => {
    const uniq = new Map();
    (Array.isArray(items) ? items : []).forEach((s) => {
      const anchor = scheduleAnchorTime(s);
      if (!anchor || !isFutureSchedule(anchor)) return;
      const dateKey = scheduleDateKey(s) || String(anchor).slice(0, 10);
      const key = `${directionLabel}|${dateKey}|${anchor}`;
      if (!uniq.has(key)) uniq.set(key, s);
    });
    return Array.from(uniq.values()).sort(
      (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
    );
  };

  const availableFlightDirections = useMemo(() => {
    if (travelDirection === "from_ema") return ["Arrival"];
    const next = [];
    if ((flightSchedules.Departure || []).length) next.push("Departure");
    if ((flightSchedules.Arrival || []).length) next.push("Arrival");
    return next;
  }, [travelDirection, flightSchedules]);

  const resolvedFlightDirection =
    travelDirection === "from_ema"
      ? "Arrival"
      : availableFlightDirections.includes(flightDirection)
      ? flightDirection
      : availableFlightDirections[0] || "Departure";

  const isLikelyUkPostcode = (value) => {
    if (!value) return false;
    const norm = String(value).toUpperCase().trim();
    const pcRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][0-9][A-Z]|[A-Z]{2}[0-9]{2})\s*[0-9][A-Z]{2}$/;
    return pcRegex.test(norm);
  };
  const isLikelyFlightNumber = (value) => {
    const v = String(value || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!v) return false;
    return /^[A-Z0-9]{2,3}\d{1,4}$/.test(v);
  };

  const resetLoadingIndicators = () => {
    loadingTimers.current.forEach((timer) => clearTimeout(timer));
    loadingTimers.current = [];
    setLoadingSteps([]);
    setLoadingSlow(false);
  };

  const markLoadingStep = (id) => {
    setLoadingSteps((prev) => prev.map((step) => (step.id === id ? { ...step, status: "done" } : step)));
  };

  const startLoadingIndicators = ({ phase = "departure" } = {}) => {
    resetLoadingIndicators();
    const steps =
      phase === "arrival"
        ? [
            { id: "flight", label: "Checking live flight timing into East Midlands", status: "done" },
            { id: "road", label: "Checking road journey, motorway traffic and EMA corridor timings", status: "pending" },
            { id: "rail", label: "Checking rail gateways, interchange timings and live station status", status: "pending" },
            { id: "weather", label: "Checking weather at home and East Midlands Airport", status: "pending" },
            { id: "compare", label: "Building your return plan and comparing the best options home", status: "pending" },
          ]
        : [
            { id: "flight", label: "Checking live flight timing and departure window", status: "done" },
            { id: "road", label: "Checking live road traffic, motorway incidents and terminal timing", status: "pending" },
            { id: "rail", label: "Checking National Rail, RailData, live boards and gateway stations", status: "pending" },
            { id: "weather", label: "Checking weather at your postcode and at East Midlands Airport", status: "pending" },
            { id: "compare", label: "Building your personalised journey plan and comparing modes", status: "pending" },
          ];

    setLoadingSteps(steps);
    loadingTimers.current.push(setTimeout(() => markLoadingStep("road"), 700));
    loadingTimers.current.push(setTimeout(() => markLoadingStep("rail"), 1700));
    loadingTimers.current.push(setTimeout(() => markLoadingStep("weather"), 3000));
    loadingTimers.current.push(setTimeout(() => markLoadingStep("compare"), 4700));
    loadingTimers.current.push(setTimeout(() => setLoadingSlow(true), 6500));
  };
  const selectPickupSuggestion = (postcode) => {
    setForm((prev) => ({ ...prev, pickup_postcode: postcode }));
    setPickupSuggestions([]);
    setPickupFocused(false);
    setPickupActiveIdx(-1);
    setPickupWarning("");
    setPostcodeError("");
  };
  const selectFlightSuggestion = (flightValue) => {
    const val = String(flightValue || "").toUpperCase();
    setForm((prev) => ({ ...prev, flight_number: val, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
    setFlightFocused(false);
    setFlightSuggestions([]);
    setFlightActiveIdx(-1);
    refreshFlightSchedules(val, { debounce: false, resetDate: true });
  };
  const handlePickupKeyDown = (e) => {
    const max = pickupSuggestions.length - 1;
    if (!pickupFocused || max < 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setPickupActiveIdx((prev) => (prev < max ? prev + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setPickupActiveIdx((prev) => (prev > 0 ? prev - 1 : max));
      return;
    }
    if (e.key === "Enter" && pickupActiveIdx >= 0 && pickupActiveIdx <= max) {
      e.preventDefault();
      const candidate = pickupSuggestions[pickupActiveIdx]?.postcode;
      if (candidate) selectPickupSuggestion(candidate);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setPickupFocused(false);
      setPickupActiveIdx(-1);
    }
  };
  const handleFlightKeyDown = (e) => {
    const max = flightSuggestions.length - 1;
    if (!flightFocused || max < 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFlightActiveIdx((prev) => (prev < max ? prev + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFlightActiveIdx((prev) => (prev > 0 ? prev - 1 : max));
      return;
    }
    if (e.key === "Enter" && flightActiveIdx >= 0 && flightActiveIdx <= max) {
      e.preventDefault();
      const candidate = flightSuggestions[flightActiveIdx]?.flightNumber;
      if (candidate) selectFlightSuggestion(candidate);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setFlightFocused(false);
      setFlightActiveIdx(-1);
    }
  };
  const pickupInputDescribedBy = [pickupWarning ? pickupWarningId : null, postcodeError ? postcodeErrorId : null]
    .filter(Boolean)
    .join(" ");
  const flightInputDescribedBy = [flightError ? flightErrorId : null].filter(Boolean).join(" ");

  const validatePostcodeExists = async (value, { silent = false } = {}) => {
    const pc = String(value || "").trim();
    if (!pc || !isLikelyUkPostcode(pc)) {
      if (!silent) setPostcodeError("Use a valid UK postcode (e.g. NG1 5FS).");
      return false;
    }
    try {
      setPostcodeValidating(true);
      const resp = await api.get("/api/postcodes/validate", { params: { postcode: pc } });
      const valid = !!resp.data?.valid;
      if (!valid) {
        if (!silent) setPostcodeError(resp.data?.error || "Postcode not found.");
        return false;
      }
      setPostcodeError("");
      return true;
    } catch (e) {
      if (!silent) setPostcodeError(e?.response?.data?.error || "Could not validate postcode.");
      return false;
    } finally {
      setPostcodeValidating(false);
    }
  };

  const fetchPickupSuggestions = (value) => {
    const v = String(value || "");
    if (pickupTimer.current) clearTimeout(pickupTimer.current);
    if (v.length < 2) {
      setPickupSuggestions([]);
      setPickupActiveIdx(-1);
      setPickupWarning("");
      setPostcodeError("");
      return;
    }
    if (v.length >= 4 && !isLikelyUkPostcode(v)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. NG1 5FS).");
    } else {
      setPickupWarning("");
    }
    pickupTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/postcodes/suggest", { params: { q: v, limit: 10 } });
        const suggestions = resp.data?.suggestions || [];
        const seen = new Map();
        suggestions.forEach((pc) => {
          const raw = String(pc || "").toUpperCase();
          const norm = raw.replace(/[^A-Z0-9]/g, "");
          if (!norm) return;
          if (!seen.has(norm)) {
            const formatted =
              norm.length > 3 ? `${norm.slice(0, norm.length - 3)} ${norm.slice(-3)}` : norm;
            seen.set(norm, formatted);
          }
        });
        setPickupSuggestions([...seen.values()].map((pc) => ({ postcode: pc, label: "Suggested" })));
        setPickupActiveIdx(-1);
      } catch (e) {
        setPickupSuggestions([]);
        setPickupActiveIdx(-1);
      }
    }, 180);
  };

  const fetchFlightSuggestions = (value) => {
    const v = String(value || "");
    if (flightTimer.current) clearTimeout(flightTimer.current);
    if (v.length < 2) {
      setFlightSuggestions([]);
      setFlightActiveIdx(-1);
      setFlightError(v.length === 0 ? "" : "Flight number format looks invalid.");
      return;
    }
    if (!isLikelyFlightNumber(v)) {
      setFlightError("Use flight format like FR9084 or TOM7260.");
    } else {
      setFlightError("");
    }
    flightTimer.current = setTimeout(async () => {
      try {
        const params = {
          q: v,
          limit: 8,
          ...(travelDirection === "from_ema" ? { direction: "Arrival" } : {}),
        };
        const resp = await api.get("/api/ema/v2/flights/search", { params });
        const list = resp.data?.suggestions || [];
        setFlightSuggestions(list.map((f) => ({ flightNumber: f })));
        setFlightActiveIdx(-1);
      } catch (e) {
        setFlightSuggestions([]);
        setFlightActiveIdx(-1);
      }
    }, 180);
  };

  useEffect(() => {
    if (!pickupFocused || pickupSuggestions.length === 0) {
      if (pickupActiveIdx !== -1) setPickupActiveIdx(-1);
      return;
    }
    if (pickupActiveIdx >= pickupSuggestions.length) {
      setPickupActiveIdx(pickupSuggestions.length - 1);
    }
  }, [pickupFocused, pickupSuggestions, pickupActiveIdx]);

  useEffect(() => {
    if (!flightFocused || flightSuggestions.length === 0) {
      if (flightActiveIdx !== -1) setFlightActiveIdx(-1);
      return;
    }
    if (flightActiveIdx >= flightSuggestions.length) {
      setFlightActiveIdx(flightSuggestions.length - 1);
    }
  }, [flightFocused, flightSuggestions, flightActiveIdx]);

  useEffect(() => {
    if (flightDirection === resolvedFlightDirection) return;
    setFlightDirection(resolvedFlightDirection);
    setForm((prev) => {
      if (prev.flight_direction === resolvedFlightDirection) return prev;
      return { ...prev, flight_direction: resolvedFlightDirection };
    });
  }, [flightDirection, resolvedFlightDirection]);

  useEffect(() => {
    refreshFlightSchedules(form.flight_number, { debounce: false, resetDate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!queryToken) return;
    let cancelled = false;
    const hydrateFromToken = async () => {
      setLoading(true);
      startLoadingIndicators({ phase: "token" });
      setError(null);
      try {
        let loaded = false;
        try {
          const resp = await api.post("/api/ema/v2/journey/evaluate-cached", { token: queryToken });
          if (cancelled) return;
          const payload = applyUiProjectionPreferred(resp.data?.data || null, resp.data?.ui_projection || null);
          const prefill = payload?.input_prefill || {};
          if (payload) setResult(payload);
          setForm((prev) => ({
            ...prev,
            pickup_postcode: prefill.pickup_postcode || prev.pickup_postcode,
            flight_number: prefill.flight_number || prev.flight_number,
            flight_date: prefill.flight_date || prev.flight_date,
            flight_direction: prefill.flight_direction || prev.flight_direction,
            flight_anchor_utc: prefill.flight_anchor_utc || prev.flight_anchor_utc,
            passengers: Number(prefill.passengers || prev.passengers || 2),
            bags: Number(prefill.bags || prev.bags || 2),
            preferred_modes: Array.isArray(prefill.preferred_modes) ? prefill.preferred_modes : prev.preferred_modes,
            parking_duration_days: Number(prefill.parking_duration_days || prev.parking_duration_days || 4),
            buffer_mode: prefill.buffer_mode || prev.buffer_mode || "standard",
          }));
          if (prefill.travel_direction === "from_ema") setTravelDirection("from_ema");
          setPostcodeError("");
          setFlightError("");
          if (prefill.flight_direction) setFlightDirection(prefill.flight_direction);
          if (prefill.flight_number) refreshFlightSchedules(prefill.flight_number, { debounce: false, resetDate: false });
          const j = resp.data?.journey || {};
          setJourneyMeta((prev) => ({
            ...prev,
            journey_id: j.journey_id || prev.journey_id,
            token: j.token || queryToken || prev.token,
            token_expires_at: j.token_expires_at || prev.token_expires_at,
            email: j.email || prev.email || "",
            email_verified: j.email_verified != null ? !!j.email_verified : prev.email_verified,
            note: resp.data?.cached ? "Loaded saved journey from cache." : "Loaded saved journey.",
          }));
          loaded = true;
        } catch {
          const rResp = await api.post("/api/ema/v2/return/flight-plan", { token: queryToken });
          if (cancelled) return;
          const rJourney = rResp.data?.journey || {};
          const rData = rResp.data?.data || null;
          setTravelDirection("from_ema");
          if (rData) {
            const nextForm = {
              ...form,
              pickup_postcode: rJourney?.postcode || form.pickup_postcode,
              flight_number: rJourney?.flight_number || form.flight_number,
              flight_date: rJourney?.flight_date || form.flight_date,
              flight_direction: "Arrival",
              flight_anchor_utc: rData?.flight?.arrival || rData?.flight?.scheduled_arrival || form.flight_anchor_utc || "",
            };
            setForm((prev) => ({ ...prev, ...nextForm }));
            const normalized = normalizeReturnPlanToView(rData, nextForm);
            setResult(applyUiProjectionPreferred(normalized, rResp.data?.ui_projection || null));
            if (rJourney?.flight_number) refreshFlightSchedules(rJourney.flight_number, { debounce: false, resetDate: false });
          }
          setJourneyMeta((prev) => ({
            ...prev,
            journey_id: rJourney.journey_id || prev.journey_id,
            token: rJourney.token || queryToken || prev.token,
            token_expires_at: rJourney.token_expires_at || prev.token_expires_at,
            email: rJourney.email || prev.email || "",
            email_verified: rJourney.email_verified != null ? !!rJourney.email_verified : prev.email_verified,
            note: rResp.data?.cached ? "Loaded saved return journey from cache." : "Loaded saved return journey.",
          }));
          loaded = true;
        }
        if (!loaded && !cancelled) setError("Could not load journey from token.");
        setIsTokenBoundJourney(true);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || "Could not load journey from token.");
      } finally {
        if (!cancelled) {
          resetLoadingIndicators();
          setLoading(false);
        }
      }
    };
    hydrateFromToken();
    return () => {
      cancelled = true;
      resetLoadingIndicators();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryToken]);

  useEffect(
    () => () => {
      if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
      resetLoadingIndicators();
    },
    []
  );

  const refreshFlightSchedules = (flightNum, { debounce = true, resetDate = true, forceDirection = null } = {}) => {
    const clean = flightNumKey(flightNum);
    const requestedDirection = travelDirection === "from_ema" ? "Arrival" : forceDirection || resolvedFlightDirection;
    if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    if (!clean || clean.length < 2) {
      setFlightSchedules({ Arrival: [], Departure: [] });
      setFlightDirection(requestedDirection);
      setFlightDates([]);
      setFlightDateError("");
      setFlightError(clean.length ? "Use flight format like FR9084 or TOM7260." : "");
      if (resetDate) {
        setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
      }
      return;
    }

    const runner = async () => {
      const requestId = ++flightScheduleRequestSeq.current;
      const shouldFetchArrival = true;
      const shouldFetchDeparture = travelDirection !== "from_ema";
      try {
        const [arrivalResp, departureResp] = await Promise.all([
          shouldFetchArrival
            ? api
                .get("/api/ema/v2/flights/schedules", { params: { flight_number: clean, direction: "Arrival" } })
                .then((r) => r.data?.schedules || [])
                .catch(() => [])
            : Promise.resolve([]),
          shouldFetchDeparture
            ? api
                .get("/api/ema/v2/flights/schedules", { params: { flight_number: clean, direction: "Departure" } })
                .then((r) => r.data?.schedules || [])
                .catch(() => [])
            : Promise.resolve([]),
        ]);
        if (requestId !== flightScheduleRequestSeq.current) return;
        const arrival = dedupeSchedules(arrivalResp, "Arrival");
        const departure = dedupeSchedules(departureResp, "Departure");
        const options =
          travelDirection === "from_ema"
            ? ["Arrival"]
            : [
                ...(departure.length ? ["Departure"] : []),
                ...(arrival.length ? ["Arrival"] : []),
              ];
        if (!options.length) options.push("Departure");
        const nextDirection = options.includes(requestedDirection) ? requestedDirection : options[0];

        setFlightDirection(nextDirection);
        setFlightSchedules({ Arrival: arrival, Departure: departure });
        if (!isLikelyFlightNumber(clean)) {
          setFlightError("Use flight format like FR9084 or TOM7260.");
        } else if (travelDirection === "from_ema" && !arrival.length) {
          setFlightError("Flight not found as an EMA arrival in our schedules.");
        } else if (travelDirection !== "from_ema" && !departure.length && !arrival.length) {
          setFlightError("Flight not found in our EMA schedules.");
        } else {
          setFlightError("");
        }
        const selectedList = nextDirection === "Arrival" ? arrival : departure;
        if (travelDirection === "from_ema" && !arrival.length) {
          setFlightDateError("No upcoming EMA arrivals found for this flight.");
        } else if (!selectedList.length) {
          setFlightDateError(`No upcoming EMA ${nextDirection.toLowerCase()} slots found for this flight.`);
        } else {
          setFlightDateError("");
        }
        if (resetDate) {
          setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
        }
      } catch (e) {
        if (requestId !== flightScheduleRequestSeq.current) return;
        setFlightSchedules({ Arrival: [], Departure: [] });
        setFlightDirection(requestedDirection);
        setFlightDates([]);
        setFlightDateError("Select a valid flight date.");
        setFlightError("Could not check this flight right now.");
        if (resetDate) {
          setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
        }
      }
    };

    if (debounce) {
      flightScheduleTimer.current = setTimeout(runner, 180);
    } else {
      runner();
    }
  };

  useEffect(() => {
    const effectiveDirection = resolvedFlightDirection;
    const merged = [
      ...(flightSchedules.Arrival || []).map((s) => ({ ...s, direction: "Arrival" })),
      ...(flightSchedules.Departure || []).map((s) => ({ ...s, direction: "Departure" })),
    ].filter((s) => s.direction === effectiveDirection);
    const unique = new Map();
    merged.forEach((s) => {
      const anchorIso = scheduleAnchorTime(s);
      if (!anchorIso || !isFutureSchedule(anchorIso)) return;
      const dayKey = scheduleDateKey(s) || String(anchorIso).slice(0, 10);
      const key = `${s.direction}|${dayKey}|${anchorIso}`;
      if (!unique.has(key)) unique.set(key, s);
    });
    const nextDates = Array.from(unique.values());
    nextDates.sort((a, b) => {
      const ta = new Date(scheduleAnchorTime(a) || a.date || a.snapshot_date).getTime();
      const tb = new Date(scheduleAnchorTime(b) || b.date || b.snapshot_date).getTime();
      return ta - tb;
    });
    setFlightDates(nextDates);

    if (form.flight_date) {
      const hasSelection = nextDates.some((s) => {
        const optionDate = scheduleDateKey(s);
        const optionAnchor = String(scheduleAnchorTime(s) || "");
        if (String(optionDate) !== String(form.flight_date || "")) return false;
        if (!form.flight_anchor_utc) return true;
        return optionAnchor === String(form.flight_anchor_utc || "");
      });
      if (!hasSelection) {
        setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
      }
    }
    if (!nextDates.length && form.flight_number) {
      setFlightDateError("Select a valid flight date.");
    } else {
      setFlightDateError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightSchedules, resolvedFlightDirection, form.flight_date, form.flight_anchor_utc, form.flight_number]);

  useEffect(() => {
    const targetDefault = travelDirection === "from_ema" ? DEFAULT_FLIGHT_NUMBER_FROM_EMA : DEFAULT_FLIGHT_NUMBER_TO_EMA;
    const nextFlightNumber =
      !form.flight_number ||
      form.flight_number === DEFAULT_FLIGHT_NUMBER_TO_EMA ||
      form.flight_number === DEFAULT_FLIGHT_NUMBER_FROM_EMA
        ? targetDefault
        : form.flight_number;
    setForm((prev) => ({
      ...prev,
      flight_number: nextFlightNumber,
      flight_date: "",
      flight_direction: travelDirection === "from_ema" ? "Arrival" : "",
      flight_anchor_utc: "",
    }));
    setFlightDirection(travelDirection === "from_ema" ? "Arrival" : "Departure");
    refreshFlightSchedules(nextFlightNumber, { debounce: false, resetDate: true });
    if (isTokenBoundJourney) setIsTokenBoundJourney(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelDirection]);

  useEffect(() => {
    if (!result || !scrollToResultsPending) return;
    setScrollToResultsPending(false);
    const target = resultContainerRef.current;
    if (!target) return;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [result, scrollToResultsPending]);

  const setStepView = (stepValue) => {
    const next = new URLSearchParams(searchParams);
    if (stepValue) next.set("step", stepValue);
    else next.delete("step");
    setSearchParams(next, { replace: true });
  };

  const submit = async () => {
    if (USE_MOCK && MOCK_RESPONSE) {
      setScrollToResultsPending(true);
      setResult(MOCK_RESPONSE);
      if (isCompactMobile) setStepView("results");
      setLoading(false);
      setError(null);
      return;
    }

    if (pickupWarning || !isLikelyUkPostcode(form.pickup_postcode)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. NG1 5FS).");
      setPostcodeError("Use a valid UK postcode (e.g. NG1 5FS).");
      setError("Please enter a valid pickup postcode.");
      return;
    }
    const postcodeOk = await validatePostcodeExists(form.pickup_postcode, { silent: false });
    if (!postcodeOk) {
      setError("Please enter a valid postcode that exists.");
      return;
    }
    if (!form.flight_number) {
      setError("Enter a flight number.");
      return;
    }
    if (!isLikelyFlightNumber(form.flight_number)) {
      setFlightError("Use flight format like FR9084 or TOM7260.");
      setError("Please enter a valid flight number.");
      return;
    }
    if (flightError) {
      setError(flightError);
      return;
    }
    if (travelDirection === "from_ema" && !(flightSchedules.Arrival || []).length) {
      setFlightError("Flight not found as an EMA arrival in our schedules.");
      setError("This flight is not available for EMA arrivals.");
      return;
    }
    if (travelDirection !== "from_ema" && !(flightSchedules.Arrival || []).length && !(flightSchedules.Departure || []).length) {
      setFlightError("Flight not found in our EMA schedules.");
      setError("This flight is not available in EMA schedules.");
      return;
    }
    if (!form.flight_date) {
      setFlightDateError("Pick a flight date.");
      setError("Pick a flight date.");
      return;
    }
    if (!form.flight_anchor_utc) {
      setError("Flight time missing; re-select the flight date/time.");
      return;
    }

    setLoading(true);
    startLoadingIndicators({ phase: travelDirection === "from_ema" ? "arrival" : "departure" });
    setScrollToResultsPending(true);
    setError(null);
    setSubErrors([]);
    setResult(null);

    try {
      const cleanPostcode = form.pickup_postcode.replace(/\s+/g, "");
      if (travelDirection === "from_ema") {
        setJourneyMeta((prev) => ({ ...prev, creating: true, note: "" }));
        const returnResp = await api.post("/api/ema/v2/return/flight-plan", {
          token: isTokenBoundJourney ? journeyMeta.token || undefined : undefined,
          postcode: cleanPostcode,
          flight_number: form.flight_number,
          flight_date: form.flight_date,
          flight_anchor_utc: form.flight_anchor_utc,
          direction: "Arrival",
          passengers: Number(form.passengers || 1),
          bags: Number(form.bags || 0),
          include_google: true,
          terminal_exit_buffer_mins: 40,
        });
        const normalized = normalizeReturnPlanToView(returnResp.data?.data || null, form);
        setResult(applyUiProjectionPreferred(normalized, returnResp.data?.ui_projection || null));
        if (isCompactMobile) setStepView("results");
        const jData = returnResp.data?.journey || {};
        setJourneyMeta((prev) => ({
          ...prev,
          creating: false,
          journey_id: jData.journey_id || prev.journey_id,
          token: jData.token || prev.token,
          token_expires_at: jData.token_expires_at || prev.token_expires_at,
          email: jData.email || prev.email || "",
          email_verified: jData.email_verified != null ? !!jData.email_verified : prev.email_verified,
          note: returnResp.data?.cached ? "Loaded cached return journey." : "Return journey evaluated (EMA -> Home).",
        }));
      } else {
        setJourneyMeta((prev) => ({ ...prev, creating: true, note: "" }));
        const evalResp = await api.post("/api/ema/v2/journey/evaluate-cached", {
          token: isTokenBoundJourney ? journeyMeta.token || undefined : undefined,
          postcode: cleanPostcode,
          flight_number: form.flight_number,
          flight_date: form.flight_date,
          direction: form.flight_direction || resolvedFlightDirection,
          passengers: Number(form.passengers || 1),
          bags: Number(form.bags || 0),
          preferred_modes: form.preferred_modes,
          days_stay: Number(form.parking_duration_days || 4),
          buffer_mode: form.buffer_mode || "standard",
        });

        const nextResult = applyUiProjectionPreferred(evalResp.data?.data || null, evalResp.data?.ui_projection || null);
        setResult(nextResult);
        if (isCompactMobile) setStepView("results");
        const jData = evalResp.data?.journey || {};
        setJourneyMeta((prev) => ({
          ...prev,
          creating: false,
          journey_id: jData.journey_id || prev.journey_id,
          token: jData.token || prev.token,
          token_expires_at: jData.token_expires_at || prev.token_expires_at,
          email: jData.email || prev.email || "",
          email_verified: jData.email_verified != null ? !!jData.email_verified : prev.email_verified,
          note: evalResp.data?.cached ? "Loaded cached evaluation." : "Journey evaluated and saved.",
        }));
      }
    } catch (err) {
      setScrollToResultsPending(false);
      setJourneyMeta((prev) => ({ ...prev, creating: false }));
      setError(err.response?.data?.error || err.message || "Something went wrong");
    } finally {
      resetLoadingIndicators();
      setLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    if (!journeyMeta.token || !journeyMeta.email) {
      setJourneyMeta((prev) => ({ ...prev, note: "Enter email and ensure token is created." }));
      return;
    }
      try {
        setJourneyMeta((prev) => ({ ...prev, emailing: true, note: "" }));
        await api.post(`/api/ema/v2/journey/${journeyMeta.token}/email`, { email: journeyMeta.email });
      setJourneyMeta((prev) => ({ ...prev, emailing: false, email_sent: true, email_verified: false, note: "Verification code sent to your email." }));
    } catch (e) {
      setJourneyMeta((prev) => ({
        ...prev,
        emailing: false,
        note: e?.response?.data?.error || e?.message || "Could not send verification email",
      }));
    }
  };

  const renderLoading = () => {
    if (!loading || !loadingSteps.length) return null;
    return (
      <div
        style={{
          marginTop: 14,
          border: "1px solid #dbe4f0",
          borderRadius: 16,
          overflow: "hidden",
          background: "#ffffff",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
            color: "#fff",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800 }}>Building your personalised East Midlands Airport journey</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
            We are checking live flight, road, rail, weather and airport timing data.
          </div>
        </div>
        <div style={{ padding: 14, display: "grid", gap: 9 }}>
          {loadingSteps.map((step) => (
            <div
              key={step.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 13,
                color: "#0f172a",
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: step.status === "done" ? "#dcfce7" : "#e0e7ff",
                  color: step.status === "done" ? "#166534" : "#1d4ed8",
                  fontSize: 12,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {step.status === "done" ? "✓" : "…"}
              </span>
              <span>{step.label}</span>
            </div>
          ))}
          {loadingSlow && (
            <div
              style={{
                marginTop: 2,
                borderRadius: 12,
                background: "#fff7ed",
                border: "1px solid #fdba74",
                color: "#9a3412",
                padding: "10px 12px",
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              Live data calls can take a few extra seconds when rail, road and airport sources are all being checked together. Your journey is still processing.
            </div>
          )}
        </div>
      </div>
    );
  };

  const verifyCodeNow = async () => {
    if (!verificationCode) {
      setJourneyMeta((prev) => ({ ...prev, note: "Enter verification code/token first." }));
      return;
    }
    if (!journeyMeta.token) {
      setJourneyMeta((prev) => ({ ...prev, note: "Journey token missing. Re-submit journey first." }));
      return;
    }
    try {
      setJourneyMeta((prev) => ({ ...prev, verifying: true, note: "" }));
      await api.post(`/api/ema/v2/journey/${journeyMeta.token}/verify-code`, {
        code: verificationCode.trim(),
      });
      setJourneyMeta((prev) => ({ ...prev, verifying: false, email_verified: true, note: "Email verified." }));
      setVerificationCode("");
    } catch (e) {
      setJourneyMeta((prev) => ({
        ...prev,
        verifying: false,
        note: e?.response?.data?.error || e?.message || "Verification failed",
      }));
    }
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Return From East Midlands Airport | Live Journey Home Planner (2026)</title>

        <meta
          name="description"
          content="Plan your journey home from East Midlands Airport using live arrival timing, road traffic, train services and weather data. Compare train, taxi and self-drive options from EMA to your postcode."
        />

        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/east-midlands-airport/return-journey-monitoring"
        />

        <meta property="og:title" content="Return From East Midlands Airport | Live Journey Home Planner (2026)" />
        <meta
          property="og:description"
          content="Plan your journey home from East Midlands Airport using live arrival timing, road traffic, train services and weather data."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/east-midlands-airport/return-journey-monitoring" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.cheapcabgrab.com/east-midlands-airport/return-journey-monitoring#webpage",
            name: "Return From East Midlands Airport | Live Journey Home Planner (2026)",
            url: "https://www.cheapcabgrab.com/east-midlands-airport/return-journey-monitoring",
            description:
              "Plan your journey home from East Midlands Airport using live arrival timing, road traffic, train services and weather data.",
            inLanguage: "en-GB",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How does this planner estimate when I can leave EMA?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The planner evaluates flight arrival timing, terminal exit assumptions, road traffic and rail services to calculate a realistic journey home from East Midlands Airport.",
                },
              },
              {
                "@type": "Question",
                name: "Does this monitor disruptions after my flight lands?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Verified journeys can be monitored around arrival and terminal exit using road, rail and flight status data to detect significant changes affecting the route home.",
                },
              },
              {
                "@type": "Question",
                name: "Is there a train station at East Midlands Airport?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. Most rail journeys use East Midlands Parkway or nearby stations, then a transfer (bus or taxi) to the terminal.",
                },
              },
              {
                "@type": "Question",
                name: "Which station is best for East Midlands Airport?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "East Midlands Parkway is often the closest rail gateway, but Nottingham, Derby and Long Eaton can be better depending on your train and onward transfer timing.",
                },
              },
              {
                "@type": "Question",
                name: "Is this an official East Midlands Airport system?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. This is an independent travel planning tool that uses official transport data feeds to assist passengers planning the journey home from East Midlands Airport.",
                },
              },
            ],
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["SoftwareApplication", "WebApplication"],
            name: "East Midlands Airport Return Journey Planner",
            url: "https://www.cheapcabgrab.com/east-midlands-airport/return-journey-monitoring",
            operatingSystem: "All",
            applicationCategory: "TravelApplication",
            applicationSubCategory: "Journey planning",
            description:
              "A live journey planning and monitoring tool for getting home from East Midlands Airport, combining arrival timing, road traffic, rail services, weather and flight status.",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            featureList: [
              "Flight-aware terminal exit estimation",
              "Rail, taxi and self-drive comparisons from EMA",
              "Real-time road and rail disruption updates",
              "Gateway station transfer integration",
              "Immigration and baggage timing support",
            ],
          })}
        </script>
      </Helmet>

      {!showMobileResultsScreen && <LiveStrip variant="east-midlands" />}

      {!showMobileResultsScreen && (
      <>
      <div className="parking-header">
        <h1>Return From East Midlands Airport (Live Journey Home Planner 2026)</h1>
        <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0f172a" }}>
          Updated: 21st Apr 2026 • Terminal-to-home live journey monitor
        </div>
        <div style={{ fontSize: 11, color: "#475467", marginTop: 6 }}>
          Live arrival, road, rail and weather monitoring for the journey home from EMA, including terminal-exit timing, onward travel options and disruption-aware routing.
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
          Going to East Midlands Airport instead?{" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            Use the main live journey planner for departure and towards-EMA trips
          </a>
          .
        </div>
      </div>

      <div
        className="parking-form ema-v2-form"
        style={{
          marginTop: 18,
          borderRadius: 22,
          border: "1px solid #cbd5e1",
          background:
            "linear-gradient(180deg, rgba(240,249,255,0.95) 0%, rgba(255,255,255,0.98) 24%, rgba(248,250,252,1) 100%)",
          boxShadow: "0 18px 44px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "space-between",
            padding: isCompactMobile ? "14px 16px 10px" : "16px 18px 12px",
            borderBottom: "1px solid rgba(148, 163, 184, 0.22)",
            background:
              "linear-gradient(90deg, rgba(14,165,233,0.12) 0%, rgba(15,118,110,0.08) 45%, rgba(245,158,11,0.08) 100%)",
          }}
        >
          <div style={{ display: "grid", gap: 4 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                background: "#082f49",
                color: "#e0f2fe",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                width: "fit-content",
              }}
            >
              Return Flow
            </div>
            <div style={{ fontSize: isCompactMobile ? 16 : 18, fontWeight: 800, color: "#0f172a" }}>
              Flight arrival to home postcode
            </div>
            <div style={{ fontSize: 12, color: "#475467" }}>
              Locking the journey from EMA and tailoring the checks for terminal exit, onward rail, road and pickup timing.
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              For check-in timing, station choice before departure or road planning towards the terminal, use the{" "}
              <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
                main East Midlands live journey planner
              </a>
              .
            </div>
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 14,
              border: "1px solid rgba(14,165,233,0.25)",
              background: "rgba(255,255,255,0.75)",
              color: "#0f172a",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <span aria-hidden="true">🛬</span>
            <span>Arrival-aware return planner</span>
          </div>
        </div>
        <div className="ema-v2-form-grid" style={{ display: "grid", gap: 12 }}>
          <div
            className="ema-v2-route-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: isCompactMobile ? "column" : "row",
              gap: isCompactMobile ? 12 : 10,
              alignItems: isCompactMobile ? "stretch" : "end",
              justifyContent: "space-between",
            }}
          >
            <>
              <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 320, color: "#0f172a", fontWeight: 800 }}>
                From
                <input
                  className="parking-input"
                  value="East Midlands Airport (locked)"
                  disabled
                  style={{
                    background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
                    color: "#9a3412",
                    border: "1px solid #fdba74",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85)",
                    fontWeight: 700,
                  }}
                />
              </label>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: "2px solid #0f766e",
                  background: "linear-gradient(135deg, #ecfeff 0%, #dcfce7 100%)",
                  color: "#115e59",
                  fontSize: 20,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "center",
                  boxShadow: "0 10px 24px rgba(15, 118, 110, 0.14)",
                }}
                aria-hidden="true"
              >
                →
              </div>
              <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 320, color: "#0f172a", fontWeight: 800 }}>
                Home postcode
                <input
                  className="parking-input"
                  value={form.pickup_postcode}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, pickup_postcode: e.target.value }));
                    if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                    setPostcodeError("");
                    fetchPickupSuggestions(e.target.value);
                  }}
                  onKeyDown={handlePickupKeyDown}
                  onFocus={() => setPickupFocused(true)}
                  onBlur={() =>
                    setTimeout(() => {
                      setPickupFocused(false);
                      setPickupActiveIdx(-1);
                      validatePostcodeExists(form.pickup_postcode, { silent: false });
                    }, 200)
                  }
                  placeholder="e.g. NG1 5FS"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={pickupFocused && pickupSuggestions.length > 0}
                  aria-controls={pickupListboxId}
                  aria-activedescendant={
                    pickupFocused && pickupActiveIdx >= 0
                      ? `ema-v2-pickup-option-${pickupActiveIdx}`
                      : undefined
                  }
                  aria-invalid={!!pickupWarning || !!postcodeError}
                  aria-describedby={pickupInputDescribedBy || undefined}
                  style={{
                    background: "#ffffff",
                    border: "1px solid #bae6fd",
                    boxShadow: "0 1px 0 rgba(255,255,255,0.8), 0 6px 18px rgba(14, 165, 233, 0.06)",
                  }}
                />
                {pickupWarning && (
                  <div id={pickupWarningId} className="input-warning" role="status" aria-live="polite">
                    {pickupWarning}
                  </div>
                )}
                {postcodeError && (
                  <div id={postcodeErrorId} className="input-warning" role="status" aria-live="polite">
                    {postcodeError}
                  </div>
                )}
                {pickupFocused && pickupSuggestions.length > 0 && (
                  <div className="suggestions" id={pickupListboxId} role="listbox" aria-label="Postcode suggestions">
                    {pickupSuggestions.map((s, idx) => (
                      <button
                        type="button"
                        key={s.postcode}
                        className="suggestion"
                        style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}
                        id={`ema-v2-pickup-option-${idx}`}
                        role="option"
                        aria-selected={idx === pickupActiveIdx}
                        onMouseEnter={() => setPickupActiveIdx(idx)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectPickupSuggestion(s.postcode);
                        }}
                      >
                        {s.postcode} <span className="suggestion-label">{s.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </label>
            </>
          </div>

          <div
            className="ema-v2-flight-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: isCompactMobile ? "column" : "row",
              gap: isCompactMobile ? 12 : 10,
              alignItems: isCompactMobile ? "stretch" : "end",
              justifyContent: "space-between",
            }}
          >

          <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 170px", maxWidth: isCompactMobile ? "100%" : 260, color: "#0f172a", fontWeight: 800 }}>
            Arrival flight number
            <input
              className="parking-input"
              value={form.flight_number}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                setForm((prev) => ({ ...prev, flight_number: val, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
                if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                if (val && !isLikelyFlightNumber(val)) setFlightError("Use flight format like FR9084 or TOM7260.");
                else setFlightError("");
                fetchFlightSuggestions(val);
                refreshFlightSchedules(val, { debounce: true, resetDate: true });
              }}
              onKeyDown={handleFlightKeyDown}
              onFocus={() => setFlightFocused(true)}
              onBlur={() =>
                setTimeout(() => {
                  setFlightFocused(false);
                  setFlightActiveIdx(-1);
                }, 200)
              }
              placeholder="e.g. FR123"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={flightFocused && flightSuggestions.length > 0}
              aria-controls={flightListboxId}
              aria-activedescendant={
                flightFocused && flightActiveIdx >= 0
                  ? `ema-v2-flight-option-${flightActiveIdx}`
                  : undefined
              }
              aria-invalid={!!flightError}
              aria-describedby={flightInputDescribedBy || undefined}
              style={{
                background: "#ffffff",
                border: "1px solid #bae6fd",
                boxShadow: "0 1px 0 rgba(255,255,255,0.8), 0 6px 18px rgba(14, 165, 233, 0.06)",
              }}
            />
            {flightError && (
              <div id={flightErrorId} className="input-warning" role="status" aria-live="polite">
                {flightError}
              </div>
            )}
            {flightFocused && flightSuggestions.length > 0 && (
              <div className="suggestions" id={flightListboxId} role="listbox" aria-label="Flight number suggestions">
                {flightSuggestions.map((s, idx) => (
                  <button
                    type="button"
                    key={s.id || s.flightNumber}
                    className="suggestion"
                    style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}
                    id={`ema-v2-flight-option-${idx}`}
                    role="option"
                    aria-selected={idx === flightActiveIdx}
                    onMouseEnter={() => setFlightActiveIdx(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectFlightSuggestion(s.flightNumber || s.id || s);
                    }}
                  >
                    {s.flightNumber || s.id || s}
                  </button>
                ))}
              </div>
            )}
          </label>

          <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 360, color: "#0f172a", fontWeight: 800 }}>
            Flight arrival date / time
            <select
              className="parking-input"
              value={
                form.flight_date
                  ? `${form.flight_direction || resolvedFlightDirection}|${form.flight_date}|${form.flight_anchor_utc || ""}`
                  : ""
              }
              aria-invalid={!!flightDateError}
              aria-describedby={flightDateError ? flightDateErrorId : undefined}
              onChange={(e) => {
                const [dir, val, anchor] = String(e.target.value || "").split("|");
                setForm((prev) => ({
                  ...prev,
                  flight_date: val || "",
                  flight_direction: dir || "",
                  flight_anchor_utc: anchor || "",
                }));
                if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                if (dir) {
                  setFlightDirection(dir);
                }
                setFlightDateError("");
              }}
              style={{
                background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                border: "1px solid #bae6fd",
                boxShadow: "0 1px 0 rgba(255,255,255,0.8), 0 6px 18px rgba(14, 165, 233, 0.06)",
              }}
            >
              <option value="">Select flight date</option>
              {flightDates.map((s) => {
                const d = scheduleDateKey(s) || s.date || s.snapshot_date || s;
                const anchorIso = scheduleAnchorTime(s);
                const timeLabel = anchorIso ? formatScheduleLabel(anchorIso) : d || "—";
                const labelPrefix = "Arrives at EMA — ";
                const delayMins = scheduleDelayMins(s);
                const delayLabel =
                  delayMins == null
                    ? ""
                    : delayMins > 0
                    ? ` • Delay +${delayMins}m`
                    : delayMins < 0
                    ? ` • Early ${Math.abs(delayMins)}m`
                    : "";
                const statusLabel = s.status ? ` • ${String(s.status)}` : "";
                const label = `${labelPrefix}${timeLabel}${delayLabel}${statusLabel}`;
                const val = String(d || "").slice(0, 10);
                return (
                  <option
                    key={`${flightNumKey(form.flight_number)}-${s.direction}-${val}-${anchorIso || ""}`}
                    value={`${s.direction}|${val}|${anchorIso || ""}`}
                  >
                    {label}
                  </option>
                );
              })}
              {!flightDates.length && form.flight_date && (
                <option value={`${resolvedFlightDirection}|${form.flight_date}|${form.flight_anchor_utc || ""}`}>
                  {form.flight_date}
                </option>
              )}
            </select>
            {flightDateError && (
              <div id={flightDateErrorId} className="input-warning" role="status" aria-live="polite">
                {flightDateError}
              </div>
            )}
          </label>

            <div className="parking-actions ema-v2-submit-wrap" style={{ alignSelf: isCompactMobile ? "stretch" : "end", marginTop: 0, marginLeft: isCompactMobile ? 0 : "auto", width: isCompactMobile ? "100%" : "auto" }}>
              <button
                className="parking-btn ema-v2-submit-btn"
                onClick={submit}
                disabled={loading || postcodeValidating || !!postcodeError || !!flightError || !!pickupWarning || !form.flight_date || !form.flight_anchor_utc}
                style={{
                  minWidth: isCompactMobile ? "100%" : 220,
                  width: isCompactMobile ? "100%" : "auto",
                  paddingLeft: 22,
                  paddingRight: 22,
                  border: "1px solid #0f766e",
                  background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 52%, #f59e0b 100%)",
                  color: "#ffffff",
                  boxShadow: "0 14px 30px rgba(15, 118, 110, 0.22)",
                }}
              >
                {loading ? "Building your return journey..." : "Get my return plan"}
              </button>
            {error && (
              <div id={formErrorId} className="input-warning" style={{ marginTop: 8 }} role="alert" aria-live="assertive">
                {error}
              </div>
            )}
          </div>
          </div>
        </div>

        <details
          open={advancedOpen}
          onToggle={(e) => setAdvancedOpen(e.target.open)}
          style={{
            marginTop: 12,
            border: "1px solid #dbeafe",
            borderRadius: 14,
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
          }}
        >
          <summary style={{ padding: "12px 14px", cursor: "pointer", fontWeight: 800, color: "#0f172a" }}>
            Adjust assumptions (optional)
          </summary>
          <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            <label className="parking-label">
              Passengers
              <input
                className="parking-input"
                type="number"
                min="1"
                max="8"
                value={form.passengers}
                onChange={(e) => setForm((prev) => ({ ...prev, passengers: Number(e.target.value) }))}
              />
            </label>
            <label className="parking-label">
              Bags
              <input
                className="parking-input"
                type="number"
                min="0"
                max="12"
                value={form.bags}
                onChange={(e) => setForm((prev) => ({ ...prev, bags: Number(e.target.value) }))}
              />
            </label>
            <label className="parking-label">
              Preferred modes
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                {["taxi", "rail", "self_drive"].map((m) => (
                  <label key={m} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={form.preferred_modes.includes(m)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => {
                          const set = new Set(prev.preferred_modes || []);
                          if (checked) set.add(m);
                          else set.delete(m);
                          return { ...prev, preferred_modes: Array.from(set) };
                        });
                      }}
                    />
                    {m === "self_drive" ? "Self-drive" : m === "rail" ? "Rail" : "Taxi"}
                  </label>
                ))}
              </div>
            </label>
            {form.preferred_modes.includes("self_drive") && (
              <label className="parking-label">
                Parking stay (days)
                <input
                  className="parking-input"
                  type="number"
                  min="1"
                  max="30"
                  value={form.parking_duration_days}
                  onChange={(e) => setForm((prev) => ({ ...prev, parking_duration_days: Number(e.target.value) }))}
                />
              </label>
            )}
            <label className="parking-label">
              Buffer mode
              <select
                className="parking-input"
                value={form.buffer_mode}
                onChange={(e) => setForm((prev) => ({ ...prev, buffer_mode: e.target.value }))}
              >
                <option value="standard">Standard buffers</option>
                <option value="fasttrack">FastTrack / light buffer</option>
              </select>
              <div className="input-hint">Tuning only; still using standard buffers in this beta.</div>
            </label>
          </div>
        </details>
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            lineHeight: 1.35,
            color: "#6b7280",
          }}
        >
          Live return monitoring uses official transport data feeds, including flight arrival data, National Rail service information, National Highways incident data, Google traffic conditions and Open-Meteo weather forecasts.
          {" "}Need the outward trip instead?{" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "underline" }}>
            Open the departure and towards-EMA planner
          </a>
          .
        </div>
      </div>
      </>
      )}

      {USE_MOCK && result && <div style={{ marginTop: 20 }}>Mock response loaded.</div>}

      {renderLoading()}

      {!showMobileResultsScreen && <EmaLiveOpsSection />}

      {subErrors.length > 0 && (
        <div className="input-warning" style={{ marginTop: 12 }}>
          {subErrors.join(" • ")}
        </div>
      )}

      {result && (!isCompactMobile || showMobileResultsScreen) && (
        <div
          ref={resultContainerRef}
          style={{
            marginTop: showMobileResultsScreen ? 8 : 20,
            display: "grid",
            gap: 12,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {showMobileResultsScreen && (
            <button
              type="button"
              onClick={() => setStepView("")}
              style={{
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#0f172a",
                borderRadius: 16,
                padding: "12px 14px",
                width: "100%",
                boxSizing: "border-box",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              ← Edit journey
            </button>
          )}
          <RecommendationSummary result={result} flightForm={form} journeyMeta={journeyMeta} />
          <SecureJourneyAccessStrip
            journeyMeta={journeyMeta}
            setJourneyMeta={setJourneyMeta}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            onSendVerificationEmail={sendVerificationEmail}
            onVerifyCode={verifyCodeNow}
          />
        </div>
      )}

      {!showMobileResultsScreen && <CollapsibleSeoSections />}
      {!showMobileResultsScreen && <EmaRelatedToolsSection />}

      <div style={{ marginTop: 32 }}>
        <ReviewsBar pageKey="/east-midlands-airport/return-journey-monitoring" />
      </div>
    </div>
  );
}

function RecommendationSummary({ result, flightForm, journeyMeta }) {
  const [openDetailMode, setOpenDetailMode] = useState(null);
  const isCompactMobile = useCompactMobile(768);
  const uiProjection = result?.ui_projection && typeof result.ui_projection === "object" ? result.ui_projection : null;
  const projectionHero = uiProjection?.hero || {};
  const projectionCards = uiProjection?.cards || {};
  const projectionTimelines = uiProjection?.timelines || {};
  const rail = result?.rail;
  const taxi = result?.taxi;
  const drive = result?.drive;
  const backendRecommendation = result?.recommendation || null;
  const backendDetails = result?.details || null;
  const submitted = result?.input_prefill || {};
  const viewForm = {
    pickup_postcode: submitted.pickup_postcode || flightForm.pickup_postcode,
    flight_number: submitted.flight_number || flightForm.flight_number,
    flight_date: submitted.flight_date || flightForm.flight_date,
    flight_direction: submitted.flight_direction || flightForm.flight_direction,
    flight_anchor_utc: submitted.flight_anchor_utc || flightForm.flight_anchor_utc,
    bags: submitted.bags != null ? Number(submitted.bags) : Number(flightForm.bags || 0),
    parking_duration_days:
      submitted.parking_duration_days != null ? Number(submitted.parking_duration_days) : Number(flightForm.parking_duration_days),
    travel_direction: submitted.travel_direction || (result?.return_guidance ? "from_ema" : "to_ema"),
  };
  const sourceLevels = result?.source_levels || {};
  const monitorTargets = result?.monitor_targets || {};
  const policyState = result?.policy_state || {};
  const modeKeyForTitle = (title) => {
    if (title === "Train") return "train";
    if (title === "Self Drive") return "self_drive";
    if (title === "Taxi") return "taxi";
    return null;
  };
  const projectionCardForTitle = (title) => {
    const key = modeKeyForTitle(title);
    return key ? projectionCards?.[key] || null : null;
  };
  const projectionDetailForTitle = (title) => {
    const key = modeKeyForTitle(title);
    const tl = key ? projectionTimelines?.[key] || null : null;
    const roadSections = Array.isArray(tl?.road_sections) ? tl.road_sections : [];
    const hasTimelineSteps = Array.isArray(tl?.steps) && tl.steps.length > 0;
    if (!tl || (!hasTimelineSteps && roadSections.length === 0)) return null;
    const merged = [...(Array.isArray(tl.steps) ? tl.steps : []), ...(Array.isArray(tl.terminal_common_steps) ? tl.terminal_common_steps : [])];
    return {
      available: tl.available !== false && (merged.length > 0 || roadSections.length > 0),
      terminalEta: null,
      terminalMargin: Number.isFinite(Number(tl.terminal_margin_mins)) ? Number(tl.terminal_margin_mins) : null,
      roadSections,
      timeline: merged.map((s, idx) => ({
        key: s?.id || `step_${idx}`,
        at: s?.at || null,
        title: s?.title || "Step",
        from: s?.from || null,
        to: s?.to || null,
        durationMins: Number.isFinite(Number(s?.duration_mins)) ? Number(s.duration_mins) : null,
        status: s?.status || "unknown",
        statusReason: s?.status_reason || null,
        source: s?.source || "estimated",
        roadSummary: null,
        notes: [s?.disclaimer || null, ...(Array.isArray(s?.notes) ? s.notes : [])].filter(Boolean),
      })),
      checkedAt: tl?.checked_at || null,
    };
  };
  const direction = viewForm.flight_direction || "Departure";
  const isReturnJourney = viewForm.travel_direction === "from_ema" || !!result?.return_guidance;
  const flight = result?.flight || null;
  const flightAnchor = viewForm.flight_anchor_utc || rail?.request?.flight_anchor_utc || null;
  const badgeColorForLevel = (level) => {
    if (level === "green") return "#16a34a";
    if (level === "light_green") return "#22c55e";
    if (level === "amber") return "#f59e0b";
    if (level === "red") return "#dc2626";
    return "#94a3b8";
  };
  const prettyAirline = (name) => {
    const raw = String(name || "").trim();
    if (!raw) return direction === "Arrival" ? "Inbound flight" : "Outbound flight";
    return raw
      .replace(/\.COM$/i, "")
      .replace(/\b([A-Z]+)/g, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase());
  };
  const computeFlightBadge = () => {
    const statusText = String(flight?.status || flight?.raw?.status || "").trim();
    const statusLower = statusText.toLowerCase();
    const scheduledIso =
      direction === "Arrival"
        ? flight?.scheduled_arrival_utc || null
        : flight?.scheduled_departure_utc || null;
    const estimatedIso =
      direction === "Arrival"
        ? flight?.estimated_arrival_utc || null
        : flight?.estimated_departure_utc || null;
    const actualIso =
      direction === "Arrival"
        ? flight?.actual_arrival_utc || null
        : flight?.actual_departure_utc || null;
    const schedMs = scheduledIso ? new Date(scheduledIso).getTime() : NaN;
    const estMs = estimatedIso ? new Date(estimatedIso).getTime() : NaN;
    const delayMins =
      Number.isFinite(schedMs) && Number.isFinite(estMs) ? Math.round((estMs - schedMs) / 60000) : null;

    if (statusLower.includes("cancel")) return { label: "Cancelled", level: "red", raw: statusText || null };
    if (actualIso || statusLower.includes("depart") || statusLower.includes("arriv") || statusLower.includes("landed")) {
      return {
        label: direction === "Arrival" ? "Arrived" : "Departed",
        level: "green",
        raw: statusText || null,
      };
    }
    if (delayMins != null) {
      if (delayMins <= 10) return { label: "Scheduled", level: "green", raw: statusText || null };
      if (delayMins <= 30) return { label: `Delayed ${delayMins}m`, level: "amber", raw: statusText || null };
      return { label: `Major delay ${delayMins}m`, level: "red", raw: statusText || null };
    }
    if (statusLower.includes("sched")) return { label: "Scheduled", level: "green", raw: statusText || null };
    if (statusText) return { label: statusText, level: "light_green", raw: statusText || null };
    return null;
  };
  const flightStatusBadge = projectionHero?.flight_status_badge || computeFlightBadge();
  const flightAirlineName = String(flight?.airline?.name || flight?.airline_name || flight?.raw?.airline?.name || "").trim();
  const flightToCity = String(flight?.destination?.city_name || flight?.raw?.arrivalAirport?.cityName || "").trim();
  const flightToCode = String(flight?.destination?.airport_code || flight?.arrival_airport_code || flight?.raw?.arrivalAirport?.code || "").trim().toUpperCase();
  const flightFromCity = String(flight?.origin?.city_name || flight?.raw?.departureAirport?.cityName || "").trim();
  const flightFromCode = String(flight?.origin?.airport_code || flight?.departure_airport_code || flight?.raw?.departureAirport?.code || "").trim().toUpperCase();
  const stationNames = result?.diagnostics?.station_names || {};
  const stationNameFallback = {
    CRE: "Crewe",
    DBY: "Derby",
    TAM: "Tamworth",
    STP: "London St Pancras International",
    VXH: "Vauxhall",
    IPS: "Ipswich",
    NRW: "Norwich",
    EMD: "East Midlands Parkway",
    LGE: "Long Eaton",
    NOT: "Nottingham",
    LIV: "Liverpool Lime Street",
    BHM: "Birmingham New Street",
    PAD: "London Paddington",
    WHC: "Walthamstow Central",
    LBG: "London Bridge",
    COV: "Coventry",
    LEI: "Leicester",
    NUN: "Nuneaton",
    TOM: "Tottenham Hale",
    ELY: "Ely",
    WWA: "Woolwich Arsenal",
    WWC: "Woolwich (Elizabeth line)",
  };
  const stationLabelFriendly = (crsOrLabel) => {
    const raw = String(crsOrLabel || "").trim();
    if (!raw) return "—";
    const key = raw.toUpperCase();
    if (/^[A-Z]{3}$/.test(key)) {
      const name = stationNames[key] || stationNameFallback[key] || null;
      if (!name) return key;
      const clean = String(name).replace(/\bRail Station\b/i, "").trim();
      return clean;
    }
    const mCode = key.match(/\b([A-Z]{3})\b/);
    if (mCode?.[1]) {
      const code = mCode[1];
      const name = stationNames[code] || stationNameFallback[code] || null;
      if (name) return String(name).replace(/\bRail Station\b/i, "").trim();
    }
    return raw;
  };
  const stationLabelCompact = (crsOrLabel) => {
    const raw = String(crsOrLabel || "").trim();
    if (!raw) return "—";
    const key = raw.toUpperCase();
    if (/^[A-Z]{3}$/.test(key)) {
      const name = stationNames[key] || stationNameFallback[key] || null;
      return name ? String(name).replace(/\bRail Station\b/i, "").trim() : key;
    }
    const m = raw.match(/\(([A-Z]{3})\)/);
    if (m?.[1] && stationNames[m[1]]) return String(stationNames[m[1]]).replace(/\bRail Station\b/i, "").trim();
    return raw;
  };
  const routeHeadline =
    direction === "Arrival"
      ? flightFromCity || flightFromCode
        ? `${prettyAirline(flightAirlineName)} arrival from ${flightFromCity || "origin"}${flightFromCode ? ` (${flightFromCode})` : ""}`
        : null
      : flightToCity || flightToCode
      ? `${prettyAirline(flightAirlineName)} departure to ${flightToCity || "destination"}${flightToCode ? ` (${flightToCode})` : ""}`
      : null;
  const prettifyStationText = (text) => {
    const src = String(text || "");
    if (!src) return src;
    return src.replace(/\b([A-Z]{3})\b/g, (m, code) => {
      const label = stationLabelCompact(code);
      return label || m;
    });
  };

  const flightLabel = (() => {
    if (projectionHero?.flight_line) return String(projectionHero.flight_line);
    const baseDate = flightAnchor || viewForm.flight_date;
    const dateText = formatDate(baseDate);
    const timeText = formatTime(flightAnchor);
    return `${viewForm.flight_number || "Flight"} — ${dateText}${timeText !== "—" ? ` ${timeText}` : ""}`;
  })();
  const googleLegNotes = (leg, fallbackDurationMins = null) => {
    if (!leg) return Number.isFinite(Number(fallbackDurationMins)) ? [`${Number(fallbackDurationMins)} mins`] : [];
    const mode = String(leg?.mode || "").trim();
    const route = String(leg?.route || "").trim();
    const agency = String(leg?.agency || "").trim();
    const from = String(leg?.from || "").trim();
    const to = String(leg?.to || "").trim();
    const departAt = leg?.depart_at_utc || null;
    const arriveAt = leg?.arrive_at_utc || null;
    const duration = Number.isFinite(Number(leg?.duration_mins))
      ? Number(leg.duration_mins)
      : Number.isFinite(Number(fallbackDurationMins))
      ? Number(fallbackDurationMins)
      : null;
    const modeLabel = mode ? mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase() : "Transit";
    return [
      route ? `${modeLabel} ${route}${agency ? ` (${agency})` : ""}` : modeLabel,
      from ? `From: ${stationLabelFriendly(from)}` : null,
      to ? `To: ${stationLabelFriendly(to)}` : null,
      departAt || arriveAt ? `Times: ${formatTime(departAt)} -> ${formatTime(arriveAt)}` : null,
      duration != null ? `${duration} mins` : null,
    ].filter(Boolean);
  };
  const computedTerminalTarget = (() => {
    if (!flightAnchor) return null;
    const t = new Date(flightAnchor);
    if (Number.isNaN(t.getTime())) return null;
    if (direction === "Arrival") {
      t.setMinutes(t.getMinutes() + 45);
    } else {
      t.setMinutes(t.getMinutes() - 180);
    }
    return t.toISOString();
  })();
  const projectionHeroStepAt = (id) =>
    Array.isArray(projectionHero?.time_steps)
      ? projectionHero.time_steps.find((step) => String(step?.id || "") === id)?.at || null
      : null;
  const terminalTime =
    projectionHeroStepAt("terminal") ||
    backendDetails?.terminal_common?.target_time ||
    result?.return_guidance?.terminal_exit_at ||
    result?.airport_guidance?.arrive_by ||
    (direction === "Arrival"
      ? rail?.request?.terminal_exit_at || rail?.request?.terminal_arrive_by || computedTerminalTarget
      : rail?.request?.terminal_arrive_by || computedTerminalTarget);
  const firstMile = rail?.segments?.find((s) => s.id === "first_mile");
  const airportHop = rail?.segments?.find((s) => s.id === "airport_hop");
  const railBest = rail?.best_candidate?.journeys?.[0];
  const normalizeStatusLevel = (level) => {
    const v = String(level || "").toLowerCase().trim();
    if (!v) return "unknown";
    if (v.includes("sched")) return "green";
    if (v.includes("on time") || v === "ontime" || v === "normal" || v === "planned" || v === "ok") return "green";
    if (v.includes("cancel")) return "red";
    if (v.includes("delay")) return "amber";
    if (v.includes("light_green")) return "light_green";
    if (v.includes("green")) return "green";
    if (v.includes("amber")) return "amber";
    if (v.includes("red")) return "red";
    return v;
  };
  const severityRank = (level) => {
    const v = normalizeStatusLevel(level);
    if (v === "critical_red" || v === "red") return 4;
    if (v === "amber") return 3;
    if (v === "light_green") return 2;
    if (v === "green") return 1;
    return 0;
  };
  const pickStrongestKnownLevel = (...levels) =>
    levels
      .map((l) => normalizeStatusLevel(l))
      .filter((l) => l && l !== "unknown")
      .sort((a, b) => severityRank(b) - severityRank(a))[0] || null;
  const railJourneyLegs = Array.isArray(railBest?.legs) ? railBest.legs : [];
  const railJourneyLevel = pickStrongestKnownLevel(
    rail?.best_candidate?.disruption?.level,
    railBest?.realtime,
    railBest?.status,
    ...railJourneyLegs.flatMap((leg) => [leg?.status, leg?.realtime])
  );
  const hasExplicitRailIssue = (() => {
    const issueText = [
      rail?.best_candidate?.disruption?.level,
      railBest?.realtime,
      railBest?.status,
      ...(Array.isArray(railBest?.connection_issues) ? railBest.connection_issues : []),
      ...railJourneyLegs.flatMap((leg) => [leg?.status, leg?.realtime, leg?.notes]),
    ]
      .flat()
      .map((v) => String(v || "").toLowerCase())
      .join(" | ");
    return /(cancel|delayed|delay|missed|disrupt|suspended|replacement bus|bus replacement)/i.test(issueText);
  })();
  const canonicalRoadLevel = normalizeStatusLevel(
    sourceLevels?.road || drive?.road?.disruption?.level || drive?.road?.status || taxi?.road?.disruption?.level || taxi?.road?.status
  );
  const rawCanonicalRailLevel = normalizeStatusLevel(
    sourceLevels?.rail || rail?.overall_disruption?.level || rail?.best_candidate?.disruption?.level
  );
  const canonicalRailLevel =
    (rawCanonicalRailLevel === "amber" || rawCanonicalRailLevel === "red") && !hasExplicitRailIssue
      ? railJourneyLevel || "light_green"
      : rawCanonicalRailLevel === "unknown"
      ? railJourneyLevel || "unknown"
      : rawCanonicalRailLevel;
  const canonicalFlightLevel = normalizeStatusLevel(sourceLevels?.flight || flightStatusBadge?.level || flight?.status || flight?.raw?.status);
  const canonicalWeatherLevel = normalizeStatusLevel(
    sourceLevels?.weather || rail?.weather?.risk?.level || drive?.weather?.risk?.level || taxi?.weather?.risk?.level
  );
  const canonicalTflLevel = normalizeStatusLevel(sourceLevels?.tfl || "unknown");
  const worstLevel = (...levels) =>
    levels
      .map((l) => normalizeStatusLevel(l))
      .sort((a, b) => severityRank(b) - severityRank(a))[0] || "unknown";
  const terminalOpsLevel = (() => {
    const w = worstLevel(canonicalFlightLevel, canonicalWeatherLevel);
    if (w === "red") return "amber";
    return w === "unknown" ? "green" : w;
  })();
  const recommendedModeLevel = (() => {
    const mode = String(backendRecommendation?.recommended_mode || "").toLowerCase();
    if (mode === "rail") return canonicalRailLevel;
    if (mode === "self_drive") return canonicalRoadLevel;
    if (mode === "taxi") return canonicalRoadLevel;
    return null;
  })();
  const computedOverallFromSources = [canonicalRoadLevel, canonicalRailLevel, canonicalTflLevel, canonicalWeatherLevel, canonicalFlightLevel]
    .sort((a, b) => severityRank(b) - severityRank(a))[0];
  const overallLevel = normalizeStatusLevel(
    result?.recommendation?.overall_disruption?.level || sourceLevels?.overall || recommendedModeLevel || computedOverallFromSources || "unknown"
  );
  const levelColor =
    overallLevel === "green"
      ? "#16a34a"
      : overallLevel === "light_green"
      ? "#22c55e"
      : overallLevel === "amber"
      ? "#f59e0b"
      : overallLevel === "red"
      ? "#dc2626"
      : "#64748b";
  const journeyStatusLabel = String(overallLevel || "unknown").replace(/_/g, " ");
  const weatherLine =
    projectionHero?.weather_line ||
    rail?.weather?.ema?.summary ||
    rail?.weather?.home?.summary ||
    taxi?.weather?.ema?.summary ||
    taxi?.weather?.home?.summary ||
    drive?.weather?.ema?.summary ||
    drive?.weather?.home?.summary ||
    "Weather data temporarily unavailable";
  const userFacingRecommendationSummary = (() => {
    const projected = String(projectionHero?.recommendation_summary || "").trim();
    if (projected && !/journey plan from home to ema terminal with check-in buffer coverage/i.test(projected)) {
      return projected;
    }
    const raw = String(backendRecommendation?.summary || "").trim();
    if (!raw) return null;
    const cleanTail = (s) => String(s || "").replace(/\.+\s*$/, "").trim();
    let m = raw.match(
      /^(.+?) recommended based on £([\d.]+) cheaper, (\d+) mins slower, and (.+)\.?$/i
    );
    if (m) {
      return `${m[1]} recommended: saves £${m[2]} with ${cleanTail(m[4])} (adds ${m[3]} mins).`;
    }
    m = raw.match(
      /^(.+?) recommended based on £([\d.]+) cheaper, (\d+) mins faster, and (.+)\.?$/i
    );
    if (m) {
      return `${m[1]} recommended: saves £${m[2]} and is ${m[3]} mins faster, with ${cleanTail(m[4])}.`;
    }
    m = raw.match(
      /^(.+?) recommended based on £([\d.]+) more expensive, (\d+) mins faster, and (.+)\.?$/i
    );
    if (m) {
      return `${m[1]} recommended: faster by ${m[3]} mins with ${cleanTail(m[4])} (costs £${m[2]} more).`;
    }
    return /journey plan from home to ema terminal with check-in buffer coverage/i.test(raw) ? null : raw;
  })();

  let leaveHome = null;
  if (railBest?.depart && firstMile?.eta_safe_mins != null) {
    const d = new Date(railBest.depart);
    d.setMinutes(d.getMinutes() - Number(firstMile.eta_safe_mins || 0));
    leaveHome = d.toISOString();
  }
  const leaveHomeTime = leaveHome || railBest?.depart || null;

  const badge = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        background: `${levelColor}22`,
        color: levelColor,
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: levelColor }} />
      Journey status: {journeyStatusLabel}
    </div>
  );

  const computeLeaveForTaxi = () => {
    if (direction === "Arrival") {
      const projectedLeave = projectionCards?.taxi?.leave_home_at || projectionHeroStepAt("leave") || null;
      if (projectedLeave) return projectedLeave;
    }
    const backendLeave = backendDetails?.taxi?.steps?.find((s) => String(s?.id || "") === "leave_home")?.at || null;
    if (backendLeave) return backendLeave;
    if (!taxi?.road) return null;
    const eta = taxi.road.eta_safe_mins ?? taxi.road.eta_best_mins;
    if (eta == null) return null;
    const target =
      direction === "Arrival"
        ? rail?.request?.terminal_exit_at || rail?.request?.terminal_arrive_by || flightAnchor
        : rail?.request?.terminal_arrive_by || flightAnchor;
    if (!target) return null;
    const t = new Date(target);
    t.setMinutes(t.getMinutes() - Number(eta || 0));
    return t.toISOString();
  };

  const computeLeaveForDrive = () => {
    if (direction === "Arrival") {
      const projectedLeave = projectionCards?.self_drive?.leave_home_at || projectionHeroStepAt("leave") || null;
      if (projectedLeave) {
        const projectedTransfer = (() => {
          const note = String(
            projectionCards?.self_drive?.insight ||
              projectionCards?.self_drive?.summary ||
              ""
          );
          const m = note.match(/Parking transfer:\s*(\d+)\s*mins?/i);
          return m?.[1] ? Number(m[1]) : null;
        })();
        const etaRoad = drive?.road?.eta_safe_mins ?? drive?.road?.eta_best_mins ?? null;
        return { leave: projectedLeave, transfer: projectedTransfer, etaRoad };
      }
    }
    const backendLeave = backendDetails?.self_drive?.steps?.find((s) => String(s?.id || "") === "leave_home")?.at || null;
    if (backendLeave) {
      const backendTransfer =
        backendDetails?.self_drive?.steps?.find((s) => String(s?.id || "") === "arrive_terminal")?.notes?.find?.((n) => /Transfer estimate:\s*(\d+)-(\d+)\s*mins/i.test(String(n || ""))) ||
        null;
      const transferMatch = String(backendTransfer || "").match(/Transfer estimate:\s*(\d+)-(\d+)\s*mins/i);
      const transfer = transferMatch?.[2] ? Number(transferMatch[2]) : null;
      const etaRoad = drive?.road?.eta_safe_mins ?? drive?.road?.eta_best_mins ?? null;
      return { leave: backendLeave, transfer, etaRoad };
    }
    if (!drive?.road) return null;
    const etaRoad = drive.road.eta_safe_mins ?? drive.road.eta_best_mins;
    if (etaRoad == null) return null;
    const parking = drive.parking?.all?.[0] || drive.parking?.silver?.[0] || drive.parking?.gold?.[0] || drive.parking?.bronze?.[0];
    const transferMinutes =
      parking?.transfer_minutes ??
      parking?.shuttle_minutes ??
      parking?.walk_minutes ??
      (parking?.transfer_time ? Number(parking.transfer_time) : null);
    const totalEta = etaRoad + (transferMinutes != null ? Number(transferMinutes) : 0);
    const target =
      direction === "Arrival"
        ? rail?.request?.terminal_exit_at || rail?.request?.terminal_arrive_by || flightAnchor
        : rail?.request?.terminal_arrive_by || flightAnchor;
    if (!target) return null;
    const t = new Date(target);
    t.setMinutes(t.getMinutes() - Number(totalEta || 0));
    return { leave: t.toISOString(), transfer: transferMinutes, etaRoad };
  };

  const taxiLeave = computeLeaveForTaxi();
  const driveLeave = computeLeaveForDrive();
  const driveParkingProduct = (() => {
    const backendProduct = backendDetails?.self_drive?.steps?.find((s) => String(s?.id || "") === "arrive_parking")?.to;
    if (backendProduct) return String(backendProduct);
    const quoteProduct =
      drive?.parking?.all?.[0]?.product_name ||
      drive?.parking?.silver?.[0]?.product_name ||
      drive?.parking?.gold?.[0]?.product_name ||
      drive?.parking?.bronze?.[0]?.product_name;
    return quoteProduct ? String(quoteProduct) : null;
  })();
  const stationTarget =
    direction === "Arrival"
      ? rail?.request?.station_depart_after || rail?.request?.station_arrive_by
      : rail?.request?.station_arrive_by || rail?.request?.station_depart_after;
  const statusChip = (label, level) => {
    const levelColor =
      level === "green"
        ? "#16a34a"
        : level === "light_green"
        ? "#22c55e"
        : level === "amber"
        ? "#f59e0b"
        : level === "red"
        ? "#dc2626"
        : "#94a3b8";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 10px",
          borderRadius: 999,
          background: `${levelColor}22`,
          color: levelColor,
          fontWeight: 700,
          fontSize: 12,
          textTransform: "capitalize",
          marginLeft: -10,
        }}
      >
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: levelColor }} />
        {label}: {level || "—"}
      </span>
    );
  };

  const railStatusLevel = canonicalRailLevel;
  const railTargetDeltaMins = Number(rail?.best_candidate?.journeys?.[0]?.target_delta_mins);
  const isVeryEarlyRailArrival =
    direction === "Departure" && Number.isFinite(railTargetDeltaMins) && railTargetDeltaMins <= -240;
  const displayedRailStatusLevel =
    isVeryEarlyRailArrival && canonicalRailLevel === "green" ? "light_green" : canonicalRailLevel;
  const resolveCustomerRailStepStatus = (primaryLevel, ...fallbackLevels) => {
    const primary = normalizeStatusLevel(primaryLevel);
    const fallback = pickStrongestKnownLevel(...fallbackLevels);
    if ((primary === "amber" || primary === "red") && !hasExplicitRailIssue && fallback && severityRank(fallback) < severityRank(primary)) {
      return fallback;
    }
    if (primary && primary !== "unknown") return primary;
    if (fallback) return fallback;
    return displayedRailStatusLevel === "amber" || displayedRailStatusLevel === "red" ? "light_green" : displayedRailStatusLevel || "green";
  };
  const resolveGatewayTransferStatus = (...levels) => pickStrongestKnownLevel(...levels) || "green";
  const checksStateLabel = (source, level) => {
    const lvl = String(level || "").toLowerCase();
    const hasRun = !!policyState?.last_monitor_at || result?.source_monitor_execution_id != null;
    if (source === "tfl" && (policyState?.tfl_relevant === false || !Array.isArray(monitorTargets?.tfl_crs) || monitorTargets.tfl_crs.length === 0)) {
      return "N/A";
    }
    if (!hasRun) return lvl && lvl !== "unknown" ? "Evaluated" : "Seeded";
    if (lvl === "amber" || lvl === "red") return "Change";
    return "Verified";
  };
  const checksRows = Array.isArray(projectionHero?.checks_rows) && projectionHero.checks_rows.length
    ? projectionHero.checks_rows.map((row) => ({
        key: row?.key || String(row?.label || "check").toLowerCase(),
        label: row?.label || "Check",
        level: row?.level || "unknown",
      }))
    : [
    { key: "road", label: "Road", level: canonicalRoadLevel },
    { key: "rail", label: "Rail", level: displayedRailStatusLevel },
    { key: "flight", label: "Flight", level: canonicalFlightLevel },
    { key: "weather", label: "Weather", level: canonicalWeatherLevel },
    { key: "tfl", label: "TfL", level: canonicalTflLevel },
  ];
  const normalizeLevel = normalizeStatusLevel;
  const tflRelevant =
    policyState?.tfl_relevant === true ||
    (Array.isArray(monitorTargets?.tfl_crs) && monitorTargets.tfl_crs.length > 0);
  const displayedChecksRows = checksRows.filter((row) => (row.key === "tfl" ? tflRelevant : true));
  const checksSummaryLine = displayedChecksRows
    .map((row) => `${row.label} ${checksStateLabel(row.key, row.level)}`)
    .join(" • ");
  const roadsLine = Array.isArray(monitorTargets?.roads) && monitorTargets.roads.length ? monitorTargets.roads.join(", ") : null;
  const crsLine = Array.isArray(monitorTargets?.crs) && monitorTargets.crs.length ? monitorTargets.crs.join(", ") : null;
  const weatherLineTargets =
    Array.isArray(monitorTargets?.weather_locations) && monitorTargets.weather_locations.length
      ? monitorTargets.weather_locations.join(" + ")
      : null;
  const nextCheckDue = policyState?.next_check_due_at || policyState?.next_notify_due_at || null;
  const lastMonitorAt = policyState?.last_monitor_at || null;
  const dueSlotHours = policyState?.due_slot_hours;
  const phaseLabel = (() => {
    const slot = Number(dueSlotHours);
    if (!Number.isFinite(slot)) return null;
    if (slot <= 0) return "departure window";
    return `${slot}h`;
  })();
  const hasContinuousMonitoring = !!journeyMeta?.email_verified;

  const cheapestParkingPrice = (() => {
    const groups = ["all", "silver", "gold", "bronze"];
    const matches = [];
    const allPrices = [];
    groups.forEach((g) => {
      (drive?.parking?.[g] || []).forEach((p) => {
        if (p?.price_gbp != null) {
          allPrices.push(Number(p.price_gbp));
          if (!p.days_stay || Number(p.days_stay) === Number(viewForm.parking_duration_days || 0)) {
            matches.push(Number(p.price_gbp));
          }
        }
      });
    });
    if (matches.length) return Math.min(...matches);
    if (allPrices.length) return Math.min(...allPrices);
    return null;
  })();
  const driveFuelCost = drive?.fuel?.fuel?.fuelCost != null ? Number(drive.fuel.fuel.fuelCost) : null;
  const driveFallbackFuelCost = (() => {
    const km = drive?.fuel?.distance_km;
    if (driveFuelCost != null || km == null) return null;
    const litresPer100 = 7; // simple default
    const pricePerLitre = 1.45;
    const litresUsed = (Number(km) * litresPer100) / 100;
    return Number((litresUsed * pricePerLitre).toFixed(2));
  })();
  const driveCostLabel = (() => {
    const fuelCost = driveFuelCost != null ? driveFuelCost : driveFallbackFuelCost;
    if (fuelCost != null && cheapestParkingPrice != null) return `Cost: £${(fuelCost + cheapestParkingPrice).toFixed(2)}`;
    if (cheapestParkingPrice != null) return `Cost: £${cheapestParkingPrice.toFixed(2)}`;
    if (fuelCost != null) return `Cost: £${fuelCost.toFixed(2)}`;
    return "Cost: awaiting price";
  })();
  const driveTotalCost = (() => {
    const fuelCost = driveFuelCost != null ? driveFuelCost : driveFallbackFuelCost;
    if (fuelCost != null && cheapestParkingPrice != null) return Number((fuelCost + cheapestParkingPrice).toFixed(2));
    if (cheapestParkingPrice != null) return Number(cheapestParkingPrice.toFixed(2));
    if (fuelCost != null) return Number(fuelCost.toFixed(2));
    return null;
  })();
  const railDurationMins = (() => {
    const fm = rail?.segments?.find((s) => s.id === "first_mile");
    const hop = rail?.segments?.find((s) => s.id === "airport_hop");
    const j = rail?.best_candidate?.journeys?.[0];
    const railCore = Number(j?.duration_mins || 0);
    const first = Number(fm?.eta_safe_mins || 0);
    const last = Number(hop?.eta_safe_mins || 0);
    const total = railCore + first + last;
    return total > 0 ? total : null;
  })();
  const trainAlternatives = (() => {
    const journeys = rail?.best_candidate?.journeys || [];
    if (!journeys.length) return { balanced: null, maxBuffer: null };
    const withDelta = journeys
      .map((j) => {
        const d = Number(j?.target_delta_mins);
        return Number.isFinite(d) ? { ...j, _delta: d } : null;
      })
      .filter(Boolean);
    const balancedPool = withDelta.filter((j) => j._delta >= 31 && j._delta <= 90);
    const balanced = balancedPool.length
      ? balancedPool.slice().sort((a, b) => {
          const ad = a?.depart ? new Date(a.depart).getTime() : NaN;
          const bd = b?.depart ? new Date(b.depart).getTime() : NaN;
          if (!Number.isNaN(ad) && !Number.isNaN(bd) && ad !== bd) return bd - ad;
          return Math.abs(a._delta) - Math.abs(b._delta);
        })[0]
      : null;
    const maxBufferPool = withDelta.filter((j) => j._delta < 0);
    const maxBuffer = maxBufferPool.length
      ? maxBufferPool.slice().sort((a, b) => a._delta - b._delta)[0]
      : null;
    return { balanced, maxBuffer };
  })();
  const selectedJourney = rail?.best_candidate?.journeys?.[0] || null;
  const isSameJourney = (a, b) => {
    const ad = a?.depart ? new Date(a.depart).getTime() : NaN;
    const aa = a?.arrive ? new Date(a.arrive).getTime() : NaN;
    const bd = b?.depart ? new Date(b.depart).getTime() : NaN;
    const ba = b?.arrive ? new Date(b.arrive).getTime() : NaN;
    return Number.isFinite(ad) && Number.isFinite(aa) && Number.isFinite(bd) && Number.isFinite(ba) && ad === bd && aa === ba;
  };
  const balancedAlt = !isSameJourney(trainAlternatives.balanced, selectedJourney) ? trainAlternatives.balanced : null;
  const driveDurationMins = (() => {
    const roadEta = drive?.road?.eta_safe_mins ?? drive?.road?.eta_best_mins;
    if (roadEta == null) return null;
    return Number(roadEta) + Number(driveLeave?.transfer || 0);
  })();
  const taxiDurationMins = (() => {
    const eta = taxi?.road?.eta_safe_mins ?? taxi?.road?.eta_best_mins;
    return eta != null ? Number(eta) : null;
  })();

  const statusColor = (level) => badgeColorForLevel(level);
  const extractRoadTokens = (text = "") => {
    const matches = String(text || "").toUpperCase().match(/\b(?:M\d{1,3}[A-Z]?|A\d{1,4}[A-Z]?)\b/g) || [];
    return [...new Set(matches)];
  };
  const buildRoadSummary = (road) => {
    const durationByRoad = new Map();
    const firstSeenOrder = [];
    const firstSeenSet = new Set();
    (road?.route_detail?.steps || []).forEach((step) => {
      const firstRoadFromStep = (() => {
        const byName = extractRoadTokens(step?.name || "");
        if (byName.length) return byName[0];
        const ins = String(step?.instruction || "").toUpperCase();
        const direct = ins.match(/\b(?:ONTO|ONTO THE|ON|MERGE ONTO|TAKE THE RAMP ONTO|STAY ON)\s+(M\d{1,3}[A-Z]?|A\d{1,4}[A-Z]?)/i);
        return direct?.[1] ? String(direct[1]).toUpperCase() : null;
      })();
      const roadsInStep = firstRoadFromStep ? [firstRoadFromStep] : [];
      if (!roadsInStep.length) return;
      const uniqueRoads = [...new Set(roadsInStep)];
      const dur = Number(step?.duration_sec);
      const weight = Number.isFinite(dur) && dur > 0 ? dur / uniqueRoads.length : 0;
      uniqueRoads.forEach((r) => {
        durationByRoad.set(r, (durationByRoad.get(r) || 0) + weight);
        if (!firstSeenSet.has(r)) {
          firstSeenSet.add(r);
          firstSeenOrder.push(r);
        }
      });
    });

    let roads = firstSeenOrder
      .filter((r) => {
        const mins = (durationByRoad.get(r) || 0) / 60;
        const isMotorway = /^M\d/i.test(String(r || ""));
        const isARoad = /^A\d/i.test(String(r || ""));
        if (isMotorway) return mins >= 3;
        if (isARoad) return mins >= 8;
        return false;
      });

    if (!roads.length) {
      const motorways = Array.isArray(road?.corridor?.motorways_used) ? road.corridor.motorways_used : [];
      const aRoads = Array.isArray(road?.corridor?.a_roads_used) ? road.corridor.a_roads_used : [];
      roads = [...new Set([...motorways, ...aRoads])];
    }

    if (!roads.length) {
      const fallbackRoads = new Set();
      extractRoadTokens(road?.route_detail?.summary || "").forEach((r) => fallbackRoads.add(r));
      (road?.route_detail?.steps || []).forEach((s) => {
        extractRoadTokens(`${s?.instruction || ""} ${s?.name || ""}`).forEach((r) => fallbackRoads.add(r));
      });
      roads = [...fallbackRoads];
    }

    return {
      roads,
      mainRoad: roads[0] || null,
      label: roads.length ? roads.slice(0, 3).join(" -> ") : null,
    };
  };
  const driveRoadSummary = buildRoadSummary(drive?.road);
  const taxiRoadSummary = buildRoadSummary(taxi?.road);

  const buildSegments = () => {
    const trainSegments = (() => {
      if (!rail) return null;
      const fm = rail.segments?.find((s) => s.id === "first_mile");
      const railSeg = rail.segments?.find((s) => s.id === "rail");
      const hop = rail.segments?.find((s) => s.id === "airport_hop");
      const bestJourney = rail?.best_candidate?.journeys?.[0];
      const originCrs = bestJourney?.legs?.[0]?.from || bestJourney?.hubs?.[0] || rail?.request?.access_decision?.preferred_crs || "Station";
      const destCrs = rail?.request?.resolved_dest_crs || rail?.request?.dest_crs || "Gateway";
      const homeLabel = viewForm.pickup_postcode || "Home";
      return [
        { label: homeLabel, level: fm?.status || "unknown" },
        { label: stationLabelCompact(originCrs), level: fm?.status || railSeg?.status || "unknown" },
        { label: stationLabelCompact(destCrs), level: railSeg?.status || railStatusLevel },
        { label: "EMA", level: hop?.status || railStatusLevel },
      ];
    })();

    const driveSegments = (() => {
      if (!drive) return null;
      const roadLevel = drive?.road?.disruption?.level || drive?.road?.status || "unknown";
      const transferLevel = roadLevel;
      const parkingProduct =
        drive?.parking?.all?.[0]?.product_name ||
        drive?.parking?.silver?.[0]?.product_name ||
        drive?.parking?.gold?.[0]?.product_name ||
        drive?.parking?.bronze?.[0]?.product_name ||
        "Parking";
      const homeLabel = viewForm.pickup_postcode || "Home";
      const mainRoadLabel = driveRoadSummary.mainRoad;
      return [
        { label: homeLabel, level: roadLevel },
        ...(mainRoadLabel ? [{ label: mainRoadLabel, level: roadLevel }] : []),
        { label: parkingProduct, level: transferLevel },
        { label: "EMA", level: roadLevel },
      ];
    })();

    const taxiSegments = (() => {
      if (!taxi) return null;
      const roadLevel = taxi?.road?.disruption?.level || taxi?.road?.status || "unknown";
      const homeLabel = viewForm.pickup_postcode || "Home";
      const mainRoadLabel = taxiRoadSummary.mainRoad;
      return [
        { label: homeLabel, level: roadLevel },
        ...(mainRoadLabel ? [{ label: mainRoadLabel, level: roadLevel }] : []),
        { label: "EMA", level: roadLevel },
      ];
    })();

    return { trainSegments, driveSegments, taxiSegments };
  };

  const { trainSegments, driveSegments, taxiSegments } = buildSegments();
  const disruptionPenalty = (level) => {
    if (level === "green") return 0;
    if (level === "light_green") return 8;
    if (level === "amber") return 18;
    if (level === "red") return 35;
    return 12;
  };
  const modeScores = (() => {
    const entries = [];
    const railJourney = rail?.best_candidate?.journeys?.[0];
    const railFirst = rail?.segments?.find((s) => s.id === "first_mile");
    const railHop = rail?.segments?.find((s) => s.id === "airport_hop");
    const railTime =
      railJourney && (railFirst?.eta_safe_mins != null || railHop?.eta_safe_mins != null)
        ? Number(railJourney.duration_mins || 0) + Number(railFirst?.eta_safe_mins || 0) + Number(railHop?.eta_safe_mins || 0)
        : railJourney?.duration_mins ?? null;
    const railCost = rail?.best_candidate?.fare?.total_gbp != null ? Number(rail.best_candidate.fare.total_gbp) : null;
    const railChanges = Number(railJourney?.changes || 0);
    const railHassle = 24 + railChanges * 8 + (railJourney?.connection_issues?.length ? 8 : 0);
    const railLevel = rail?.overall_disruption?.level || rail?.best_candidate?.disruption?.level || "unknown";
    if (railJourney && railCost != null && railTime != null) {
      entries.push({
        title: "Train",
        cost: railCost,
        time: railTime,
        hassle: railHassle,
        disruption: disruptionPenalty(railLevel),
        meetsTarget: !!rail?.best_candidate?.meets_target,
      });
    }

    const driveTime =
      drive?.road?.eta_safe_mins != null
        ? Number(drive.road.eta_safe_mins) + Number(driveLeave?.transfer || 0)
        : drive?.road?.eta_best_mins != null
        ? Number(drive.road.eta_best_mins) + Number(driveLeave?.transfer || 0)
        : null;
    const driveLevel = drive?.road?.disruption?.level || drive?.road?.status || "unknown";
    if (driveTotalCost != null && driveTime != null) {
      entries.push({
        title: "Self Drive",
        cost: driveTotalCost,
        time: driveTime,
        hassle: 18,
        disruption: disruptionPenalty(driveLevel),
        meetsTarget: true,
      });
    }

    const taxiTime = taxi?.road?.eta_safe_mins ?? taxi?.road?.eta_best_mins ?? null;
    const taxiCost = taxi?.fare?.price != null ? Number(taxi.fare.price) : null;
    const taxiLevel = taxi?.road?.disruption?.level || taxi?.road?.status || "unknown";
    if (taxiCost != null && taxiTime != null) {
      entries.push({
        title: "Taxi",
        cost: taxiCost,
        time: Number(taxiTime),
        hassle: 5,
        disruption: disruptionPenalty(taxiLevel),
        meetsTarget: true,
      });
    }
    if (!entries.length) return entries;

    const costs = entries.map((e) => e.cost);
    const times = entries.map((e) => e.time);
    const minCost = Math.min(...costs);
    const maxCost = Math.max(...costs);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);

    const normalize = (value, min, max) => (max === min ? 0 : (value - min) / (max - min));
    const scored = entries.map((e) => {
      const costNorm = normalize(e.cost, minCost, maxCost);
      const timeNorm = normalize(e.time, minTime, maxTime);
      let score = 100;
      score -= costNorm * 45;
      score -= timeNorm * 25;
      score -= Math.min(e.hassle, 40) * 0.55;
      score -= e.disruption;
      if (!e.meetsTarget && e.title === "Train") score -= 18;
      return { ...e, score };
    });

    const taxiEntry = scored.find((e) => e.title === "Taxi");
    const driveEntry = scored.find((e) => e.title === "Self Drive");
    if (taxiEntry && driveEntry) {
      const taxiMuchCostlier = taxiEntry.cost > driveEntry.cost * 1.4;
      const taxiNotMuchFaster = taxiEntry.time >= driveEntry.time - 12;
      if (taxiMuchCostlier && taxiNotMuchFaster) {
        taxiEntry.score -= 16;
      }
    }
    return scored;
  })();
  const backendRecommendedModeTitle = (() => {
    const mode = String(backendRecommendation?.recommended_mode || "").toLowerCase();
    if (mode === "rail") return "Train";
    if (mode === "self_drive") return "Self Drive";
    if (mode === "taxi") return "Taxi";
    return null;
  })();
  const recommendedMode =
    backendRecommendedModeTitle || modeScores.slice().sort((a, b) => b.score - a.score)[0]?.title || null;
  const heroCards =
    direction === "Arrival"
      ? [
          { title: "Train", body: "Home to EMA pickup window with live rail and transfer checks.", data: rail },
          { title: "Self Drive", body: "Drive to EMA with live road corridor and pickup-side parking context.", data: drive },
          { title: "Taxi", body: "Home pickup to EMA terminal timed for post-landing collection.", data: taxi },
        ]
      : [
          { title: "Train", body: "Home to EMA via origin CRS and key interchange hubs.", data: rail },
          { title: "Self Drive", body: "Drive to EMA with live road corridor and parking timing checks.", data: drive },
          { title: "Taxi", body: "Direct home-to-terminal transfer with disruption-aware routing.", data: taxi },
        ];
  const recommendationSummaryLine =
    userFacingRecommendationSummary ||
    (() => {
      const preferred = heroCards.find((card) => card.title === recommendedMode) || null;
      if (!preferred) return null;
      const costText = String(preferred.metric2 || "").replace(/^Cost:\s*/i, "").trim();
      const durationText = String(preferred.metric3 || "").replace(/^Duration:\s*/i, "").trim();
      return [
        `${preferred.title} recommended.`,
        costText && costText !== "—" ? `${costText}.` : null,
        durationText && durationText !== "—" ? `Journey time ${durationText}.` : null,
      ]
        .filter(Boolean)
        .join(" ");
    })();
  const [selectedMode, setSelectedMode] = useState(recommendedMode || heroCards[0]?.title || "Train");
  useEffect(() => {
    const fallback = recommendedMode || "Train";
    setSelectedMode(fallback);
    setOpenDetailMode(null);
  }, [recommendedMode]);
  const visibleHeroCards = isCompactMobile ? heroCards.filter((c) => c.title === selectedMode) : heroCards;
  const addMinutesIso = (iso, mins) => {
    if (!iso || !Number.isFinite(Number(mins))) return null;
    const base = new Date(iso).getTime();
    if (!Number.isFinite(base)) return null;
    return new Date(base + Number(mins) * 60000).toISOString();
  };
  const diffMins = (fromIso, toIso) => {
    const a = new Date(fromIso).getTime();
    const b = new Date(toIso).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.round((b - a) / 60000);
  };
  const trainDetail = (() => {
    const normalizeStationLabel = (raw) => {
      const v = stationLabelFriendly(raw || null) || String(raw || "").trim();
      if (!v) return "Station";
      if (/(station|airport|terminal)/i.test(v)) return v;
      return `${v} Station`;
    };
    const firstMileTaxiEstimate = ({ distanceKm, durationMins }) => {
      const km = Number(distanceKm);
      if (Number.isFinite(km) && km > 0) return Math.max(8, Math.round(3.5 + km * 1.4));
      const mins = Number(durationMins);
      if (Number.isFinite(mins) && mins > 0) return Math.max(8, Math.round(4 + mins * 0.35));
      return null;
    };
    const minsBetween = (fromIso, toIso) => {
      const a = fromIso ? new Date(fromIso).getTime() : NaN;
      const b = toIso ? new Date(toIso).getTime() : NaN;
      if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
      return Math.round((b - a) / 60000);
    };
    const flightStep = flightAnchor
      ? {
          key: "flight_anchor",
          at: flightAnchor,
          title: direction === "Arrival" ? "Flight arrival at EMA" : "Flight departure from EMA",
          from: direction === "Arrival" ? (flightFromCode || "Origin") : "EMA",
          to: direction === "Arrival" ? "EMA" : (flightToCode || "Destination"),
          durationMins: null,
          status: flightStatusBadge?.level || "unknown",
          source: flight?.status_source || "flight_feed",
          notes: [flightStatusBadge?.label ? `Status: ${flightStatusBadge.label}` : null, routeHeadline || null].filter(Boolean),
        }
      : null;
    if (backendDetails?.train?.steps?.length) {
      const commonSteps = Array.isArray(backendDetails?.train?.terminal_common_steps)
        ? backendDetails.train.terminal_common_steps
        : Array.isArray(backendDetails?.terminal_common?.steps)
        ? backendDetails.terminal_common.steps
        : [];
      const mergedRaw = [...backendDetails.train.steps, ...commonSteps];
      const merged = flightStep
        ? mergedRaw.filter((s) => !["flight_departure", "flight_arrival"].includes(String(s?.id || "")))
        : mergedRaw;
      const mergedSequenced = (() => {
        const out = merged
          .map((s) => ({ ...s }))
          .filter((s) => String(s?.id || "") !== "terminal_target");
        if (String(direction || "").trim().toLowerCase() !== "departure") return out;
        const gatewayStep = out.find((s) => String(s?.id || "") === "gateway_to_terminal");
        const gatewayMs = gatewayStep?.at ? new Date(gatewayStep.at).getTime() : NaN;
        if (!Number.isFinite(gatewayMs)) return out;
        const sequence = ["checkin", "security", "gate"];
        let cursorMs = gatewayMs;
        sequence.forEach((id) => {
          const s = out.find((x) => String(x?.id || "") === id);
          if (!s) return;
          const stepMs = s?.at ? new Date(s.at).getTime() : NaN;
          if (!Number.isFinite(stepMs) || stepMs < cursorMs) {
            s.at = new Date(cursorMs).toISOString();
          }
          const appliedMs = s?.at ? new Date(s.at).getTime() : NaN;
          if (Number.isFinite(appliedMs)) cursorMs = appliedMs + 60 * 1000;
        });
        return out;
      })();
      const stationLabelForAction = (raw) => {
        const v = stationLabelFriendly(raw || null) || String(raw || "").trim();
        if (!v) return "Station";
        if (/(station|airport|terminal)/i.test(v)) return v;
        return `${v} Station`;
      };
      const railJourneyFallback = rail?.best_candidate?.journeys?.[0] || null;
      const railLegsFallback = Array.isArray(railJourneyFallback?.legs) ? railJourneyFallback.legs : [];
      const fallbackOriginStation =
        railLegsFallback[0]?.from ||
        rail?.request?.access_decision?.preferred_crs ||
        rail?.request?.origin_crs ||
        null;
      const fallbackGatewayStation =
        rail?.request?.resolved_dest_crs ||
        rail?.request?.dest_crs ||
        railLegsFallback[railLegsFallback.length - 1]?.to ||
        null;
      const fallbackInterchanges = Array.isArray(railJourneyFallback?.interchanges_crs)
        ? railJourneyFallback.interchanges_crs.filter(Boolean)
        : [];
      const isGenericStationToken = (raw) => {
        const t = String(raw || "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();
        if (!t) return true;
        return [
          "origin",
          "destination",
          "gateway",
          "interchange",
          "rail",
          "station",
          "origin station",
          "destination station",
          "gateway station",
          "interchange station",
          "home station",
        ].includes(t);
      };
      const cleanStationStopLabel = (raw) =>
        stationLabelForAction(raw)
          .replace(/\s*\(stop[^)]*\)\s*/gi, " ")
          .replace(/\bRailway Station\b/gi, "Station")
          .replace(/\s+/g, " ")
          .trim();
      const resolveStationStopLabel = (raw, fallback = null) => {
        const candidateRaw = String(raw || "").trim();
        const candidate = !candidateRaw || isGenericStationToken(candidateRaw) ? fallback || candidateRaw : candidateRaw;
        return cleanStationStopLabel(candidate || fallback || "Station");
      };
      const nullableStationLabel = (raw) => {
        const s = String(raw ?? "").trim();
        if (!s || s === "—") return null;
        return stationLabelFriendly(s);
      };
      const inferGatewayTaxiFare = ({ fromLabel, notes = [] }) => {
        const joined = String((notes || []).join(" • "));
        const explicit = joined.match(/Estimated fare:\s*~£\s*(\d+(?:\.\d+)?)/i);
        if (explicit?.[1]) return Math.round(Number(explicit[1]));
        const from = String(fromLabel || "").toLowerCase();
        if (from.includes("east midlands parkway")) return 31;
        if (from.includes("nottingham")) return 46;
        if (from.includes("long eaton")) return 36;
        if (from.includes("derby")) return 45;
        return null;
      };
      const parseBoardStopCode = (txt) => {
        const m = String(txt || "").match(/\(Stop\s*([^)]+)\)/i);
        return m?.[1] ? `Stop ${String(m[1]).trim()}` : null;
      };
      const parseCoachBay = (notes = []) => {
        const joined = String((notes || []).join(" • "));
        const m = joined.match(/Coach Park\s*\((Bay\s*[A-Z0-9]+)\)/i);
        return m?.[1] ? `Coach Park (${m[1]})` : null;
      };
      const safeDiffMins = (fromIso, toIso) => {
        const a = fromIso ? new Date(fromIso).getTime() : NaN;
        const b = toIso ? new Date(toIso).getTime() : NaN;
        if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
        return Math.round((b - a) / 60000);
      };
      const firstMileTaxiEstimate = (step) => {
        const explicit = Number(step?.taxi_estimate_gbp);
        if (Number.isFinite(explicit) && explicit > 0) return Math.round(explicit);
        const km = Number(step?.distance_km);
        if (Number.isFinite(km) && km > 0) return Math.max(8, Math.round(3.5 + km * 1.4));
        const mins = Number(step?.duration_mins);
        if (Number.isFinite(mins) && mins > 0) return Math.max(8, Math.round(4 + mins * 0.35));
        return null;
      };
      const polishedStepTitle = (step) => {
        const id = String(step?.id || "");
        if (id === "home_to_station") {
          const mode = String(step?.mode || "").toLowerCase();
          const action = mode === "walk" ? "Walk" : mode === "taxi" ? "Taxi" : mode === "drive" ? "Drive" : "Walk";
          const station = resolveStationStopLabel(step?.to, fallbackOriginStation);
          const mins = Number(step?.duration_mins);
          const dur = Number.isFinite(mins) && mins > 0 ? ` \u2022 ${formatDuration(mins)}` : "";
          return `${action} to ${station}${dur}`;
        }
        if (id === "station_to_gateway") return "Board rail service";
        if (id === "rail_final_leg") return "Final rail leg";
        if (id === "gateway_to_terminal") {
          return direction === "Arrival" ? "Gateway to EMA terminal/coach bay" : "Gateway to EMA terminal zone";
        }
        if (id === "arrival_pickup_target" || id === "pickup_target") return "Meet at terminal exit";
        return step?.title || "Step";
      };
      return {
        available: merged.length > 0,
        terminalEta: null,
        terminalMargin: backendDetails.train.margin_to_terminal_target_mins ?? null,
        timeline: [
          ...(flightStep ? [flightStep] : []),
          ...mergedSequenced.map((s, idx) => {
            const id = String(s?.id || "");
            const defaultNotes = [s.disclaimer || null, ...(Array.isArray(s.notes) ? s.notes.map((n) => prettifyStationText(n)) : [])]
              .filter(Boolean)
              .filter((n) => !/google first-mile path did not match selected nr station\/timing/i.test(String(n)));
            if (id === "home_to_station") {
              const taxiEstimate = firstMileTaxiEstimate(s);
              const fromPc = formatUkPostcode(s?.from || viewForm?.pickup_postcode || "");
              const derivedHomeMins = (() => {
                const explicit = Number(s?.duration_mins);
                if (Number.isFinite(explicit) && explicit > 0) return explicit;
                const fmSafe = Number(firstMile?.eta_safe_mins);
                if (Number.isFinite(fmSafe) && fmSafe > 0) return fmSafe;
                const fmBest = Number(firstMile?.eta_best_mins);
                if (Number.isFinite(fmBest) && fmBest > 0) return fmBest;
                return null;
              })();
              const homeStepAt = (() => {
                if (s?.at) return s.at;
                const mins = derivedHomeMins;
                const railBoardAt = mergedSequenced?.find((x) => String(x?.id || "") === "station_to_gateway")?.at;
                const boardMs = railBoardAt ? new Date(railBoardAt).getTime() : NaN;
                if (Number.isFinite(boardMs) && Number.isFinite(mins) && mins > 0) {
                  return new Date(boardMs - mins * 60000).toISOString();
                }
                return leaveHomeTime || railBest?.depart || null;
              })();
              const notes = [
                taxiEstimate != null ? `Taxi option ~£${taxiEstimate}` : null,
                ...defaultNotes
                  .filter((n) => !/walk to the station|take a taxi to the station/i.test(String(n)))
                  .filter((n) => !/^from\s+/i.test(String(n).trim()))
                  .filter((n) => !/^taxi option\s*~?£/i.test(String(n).trim())),
              ]
                .filter(Boolean)
                .filter((n, i, arr) => arr.findIndex((x) => String(x).trim().toLowerCase() === String(n).trim().toLowerCase()) === i);
              return {
                key: s.id || `step_${idx}`,
                at: homeStepAt,
                title: polishedStepTitle(s),
                from: fromPc ? `From ${fromPc}` : null,
                to: null,
                durationMins: derivedHomeMins,
                status: resolveCustomerRailStepStatus(s.status, firstMile?.status, firstMile?.realtime),
                statusReason: s.status_reason || null,
                source: null,
                notes,
              };
            }
            if (id === "station_to_gateway") {
              const fromLabel = resolveStationStopLabel(s?.from, fallbackOriginStation || "Origin");
              const destination = resolveStationStopLabel(
                s?.to,
                fallbackInterchanges[0] || fallbackGatewayStation || "Destination"
              );
              const explicitMins = Number(s?.duration_mins);
              const inferredMins = Number.isFinite(explicitMins) && explicitMins > 0
                ? explicitMins
                : safeDiffMins(s?.at, mergedSequenced?.[idx + 1]?.at);
              const duration = Number.isFinite(Number(inferredMins)) && Number(inferredMins) > 0 ? formatDuration(Number(inferredMins)) : null;
              if (String(direction || "").toLowerCase() === "arrival") {
                const firstInterchange = mergedSequenced.find((x) => String(x?.id || "").startsWith("rail_interchange"));
                const firstHub = cleanStationStopLabel(firstInterchange?.to || destination);
                const firstLegMins = safeDiffMins(s?.at, firstInterchange?.at);
                return {
                  key: s.id || `step_${idx}`,
                  at: s.at || null,
                  title: `Depart ${fromLabel}`,
                  from:
                    `Train to ${firstHub}` +
                    (Number.isFinite(Number(firstLegMins)) && Number(firstLegMins) > 0 ? ` • ${formatDuration(Number(firstLegMins))}` : duration ? ` • ${duration}` : ""),
                  to: null,
                  durationMins: null,
                  status: resolveCustomerRailStepStatus(s.status, railJourneyLevel),
                  statusReason: s.status_reason || null,
                  source: null,
                  notes: [],
                };
              }
              return {
                key: s.id || `step_${idx}`,
                at: s.at || null,
                title: `${fromLabel} -> ${destination}${duration ? ` • ${duration}` : ""}`,
                from: null,
                to: null,
                durationMins: null,
                status: resolveCustomerRailStepStatus(s.status, railJourneyLevel),
                statusReason: s.status_reason || null,
                source: null,
                notes: [],
              };
            }
            if (id === "rail_final_leg") {
              const fromLabel = resolveStationStopLabel(
                s?.from,
                fallbackInterchanges[fallbackInterchanges.length - 1] || fallbackOriginStation || "Interchange"
              );
              const destination = resolveStationStopLabel(s?.to, fallbackGatewayStation || "Gateway station");
              const explicitMins = Number(s?.duration_mins);
              const inferredMins = Number.isFinite(explicitMins) && explicitMins > 0
                ? explicitMins
                : safeDiffMins(s?.at, mergedSequenced?.find((x) => String(x?.id || "") === "gateway_to_terminal")?.at);
              const duration = Number.isFinite(Number(inferredMins)) && Number(inferredMins) > 0 ? formatDuration(Number(inferredMins)) : null;
              if (String(direction || "").toLowerCase() === "arrival") {
                return {
                  key: s.id || `step_${idx}`,
                  at: s.at || null,
                  title: `Depart ${fromLabel}`,
                  from: `Train to ${destination}${duration ? ` • ${duration}` : ""}`,
                  to: null,
                  durationMins: null,
                  status: resolveCustomerRailStepStatus(s.status, railJourneyLevel),
                  statusReason: s.status_reason || null,
                  source: null,
                  notes: [],
                };
              }
              return {
                key: s.id || `step_${idx}`,
                at: s.at || null,
                title: `${fromLabel} -> ${destination}${duration ? ` • ${duration}` : ""}`,
                from: null,
                to: null,
                durationMins: null,
                status: resolveCustomerRailStepStatus(s.status, railJourneyLevel),
                statusReason: s.status_reason || null,
                source: null,
                notes: [],
              };
            }
            if (id === "gateway_to_terminal") {
              const fromStation = resolveStationStopLabel(s?.from, fallbackGatewayStation || "Gateway");
              const duration = Number.isFinite(Number(s?.duration_mins)) ? formatDuration(Number(s.duration_mins)) : null;
              const isGoogleBus = String(s?.source || "").toLowerCase().includes("google_transit_matched");
              if (isGoogleBus) {
                const boardStop = parseBoardStopCode(s?.from);
                const coachBay = parseCoachBay(defaultNotes);
                return {
                  key: s.id || `step_${idx}`,
                  at: s.at || null,
                  title: String(direction || "").toLowerCase() === "arrival" ? `Depart ${fromStation}` : `Skylink bus to EMA${duration ? ` • ${duration}` : ""}`,
                  from:
                    String(direction || "").toLowerCase() === "arrival"
                      ? `Skylink bus to EMA${duration ? ` • ${duration}` : ""}`
                      : fromStation,
                  to: String(direction || "").toLowerCase() === "arrival" ? null : "EMA Terminal",
                  durationMins: null,
                  status: resolveGatewayTransferStatus(s.status, airportHop?.status),
                  statusReason: s.status_reason || null,
                  source: null,
                  notes: [
                    boardStop ? `Board at: ${boardStop} (outside main entrance)` : null,
                    coachBay ? `Arrives at: ${coachBay}` : null,
                    "Frequency: Every 30 mins",
                  ].filter(Boolean),
                };
              }
              const fare = inferGatewayTaxiFare({ fromLabel: fromStation, notes: defaultNotes });
              return {
                key: s.id || `step_${idx}`,
                at: s.at || null,
                title: String(direction || "").toLowerCase() === "arrival" ? `Depart ${fromStation}` : `Taxi to EMA${duration ? ` • ${duration}` : ""}`,
                from:
                  String(direction || "").toLowerCase() === "arrival"
                    ? `Taxi to EMA${duration ? ` • ${duration}` : ""}`
                    : fromStation,
                to: String(direction || "").toLowerCase() === "arrival" ? null : "EMA Terminal",
                durationMins: null,
                status: resolveGatewayTransferStatus(s.status, airportHop?.status),
                statusReason: s.status_reason || null,
                source: null,
                notes: [fare != null ? `Est. fare ~£${fare}` : null].filter(Boolean),
              };
            }
            if (id.startsWith("rail_interchange")) {
              const interchangeIdx = Math.max(0, Number(id.split("_").pop()) - 1);
              const fromFallback =
                interchangeIdx > 0
                  ? fallbackInterchanges[interchangeIdx - 1] || fallbackOriginStation || "Rail"
                  : fallbackOriginStation || "Rail";
              const hubFallback = fallbackInterchanges[interchangeIdx] || fallbackGatewayStation || "Interchange";
              const fromLabel = resolveStationStopLabel(s?.from, fromFallback);
              const hub = resolveStationStopLabel(s?.to, hubFallback);
              const explicitMins = Number(s?.duration_mins);
              const inferredMins = Number.isFinite(explicitMins) && explicitMins > 0
                ? explicitMins
                : safeDiffMins(s?.at, mergedSequenced?.[idx + 1]?.at);
              const duration = Number.isFinite(Number(inferredMins)) && Number(inferredMins) > 0 ? formatDuration(Number(inferredMins)) : null;
              if (String(direction || "").toLowerCase() === "arrival") {
                const nextRailDepartureStep = mergedSequenced
                  .slice(idx + 1)
                  .find((x) => {
                    const sid = String(x?.id || "");
                    return sid === "rail_final_leg" || sid.startsWith("rail_interchange");
                  });
                const changeWaitMins = safeDiffMins(s?.at, nextRailDepartureStep?.at);
                return {
                  key: s.id || `step_${idx}`,
                  at: s.at || null,
                  title:
                    `Arrive ${hub}` +
                    (Number.isFinite(Number(changeWaitMins)) && Number(changeWaitMins) > 0 && Number(changeWaitMins) <= 45
                      ? ` (change ${Math.round(Number(changeWaitMins))} mins)`
                      : ""),
                  from: null,
                  to: null,
                  durationMins: null,
                  status: resolveCustomerRailStepStatus(s.status, railJourneyLevel),
                  statusReason: s.status_reason || null,
                  source: null,
                  notes: [],
                };
              }
              return {
                key: s.id || `step_${idx}`,
                at: s.at || null,
                title: `${fromLabel} -> ${hub}${duration ? ` • ${duration}` : ""}`,
                from: null,
                to: null,
                durationMins: null,
                status: resolveCustomerRailStepStatus(s.status, railJourneyLevel),
                statusReason: s.status_reason || null,
                source: null,
                notes: [],
              };
            }
            return {
              key: s.id || `step_${idx}`,
              at: s.at || null,
              title: polishedStepTitle(s),
              from: nullableStationLabel(s.from),
              to: nullableStationLabel(s.to),
              durationMins: s.duration_mins ?? null,
              status: s.status || "unknown",
              statusReason: s.status_reason || null,
              source: null,
              notes: defaultNotes,
            };
          }),
        ],
        checkedAt: null,
      };
    }

    const fm = rail?.segments?.find((s) => s.id === "first_mile");
    const railSeg = rail?.segments?.find((s) => s.id === "rail");
    const hop = rail?.segments?.find((s) => s.id === "airport_hop");
    const journey = rail?.best_candidate?.journeys?.[0] || null;
    const home = viewForm.pickup_postcode || "Home";
    const homeStation = fm?.to || rail?.request?.access_decision?.preferred_crs || journey?.legs?.[0]?.from || "Home station";
    const gateway = rail?.request?.resolved_dest_crs || rail?.request?.dest_crs || journey?.legs?.[journey?.legs?.length - 1]?.to || "EMA gateway";
    const interchanges = Array.isArray(journey?.interchanges_crs) ? journey.interchanges_crs : [];
    const hubsBase = interchanges.length ? interchanges : Array.isArray(journey?.hubs) ? journey.hubs : [];
    const hubs = hubsBase.filter((h) => h && h !== homeStation && h !== gateway);
    const hopSafeMins = Number(hop?.eta_safe_mins ?? hop?.eta_best_mins ?? 0);
    const terminalEta = journey?.arrive ? addMinutesIso(journey.arrive, hopSafeMins) : null;
    const terminalMargin = terminalEta && terminalTime ? diffMins(terminalEta, terminalTime) : null;
    const gateTime = direction === "Departure" && flightAnchor ? addMinutesIso(flightAnchor, -45) : null;
    const firstMileSource = fm?.mode === "taxi" || fm?.mode === "drive" ? "Estimated (road)" : "Estimated";
    const railSource = railSeg?.checked_sources?.length ? "Schedule + realtime" : "Schedule";
    const hopSource = hop?.data_sources?.length ? "Estimated (local transfer)" : "Estimated";
    const hasGoogleTransit = !!(rail?.google_transit?.snapshot || rail?.google_transit?.alternate_transit_available);
    const googleCompare = rail?.google_transit_compare || null;
    const gFirstMatched = !!googleCompare?.first_mile_matched;
    const gLastMatched = !!googleCompare?.last_mile_matched;
    const gFirstLeg = googleCompare?.selected_first_mile || null;
    const gLastLeg = googleCompare?.selected_last_mile || null;
    const timeline = [];

    timeline.push({
      key: "leave_home",
      at: leaveHomeTime,
      title: `${String(fm?.mode || "").toLowerCase() === "walk" ? "Walk" : "Taxi"} to ${normalizeStationLabel(
        gFirstMatched ? gFirstLeg?.to || homeStation : homeStation
      )}`,
      from: `From ${formatUkPostcode(home)}`,
      to: null,
      durationMins: null,
      status: fm?.status || "unknown",
      source: null,
      notes: [
        (() => {
          const d =
            gFirstMatched && Number.isFinite(Number(gFirstLeg?.duration_mins))
              ? Number(gFirstLeg.duration_mins)
              : fm?.eta_safe_mins ?? fm?.eta_best_mins ?? null;
          return Number.isFinite(Number(d)) && Number(d) > 0 ? `${formatDuration(Number(d))}` : null;
        })(),
        (() => {
          const est = firstMileTaxiEstimate({
            distanceKm: fm?.distance_km,
            durationMins:
              gFirstMatched && Number.isFinite(Number(gFirstLeg?.duration_mins))
                ? Number(gFirstLeg.duration_mins)
                : fm?.eta_safe_mins ?? fm?.eta_best_mins ?? null,
          });
          return est != null ? `Taxi option ~£${est}` : null;
        })(),
      ].filter(Boolean),
    });

    const allLegs = Array.isArray(journey?.legs) ? journey.legs : [];
    const firstLeg = allLegs.length ? allLegs[0] : null;
    const finalLeg = allLegs.length ? allLegs[allLegs.length - 1] : null;

    timeline.push({
      key: "rail_leg",
      at: firstLeg?.sched_dep || firstLeg?.depart || firstLeg?.dep || journey?.depart || null,
      title: `${stationLabelFriendly(firstLeg?.from_crs || firstLeg?.from || homeStation)} -> ${stationLabelFriendly(
        firstLeg?.to_crs || firstLeg?.to || gateway
      )}`,
      from: null,
      to: null,
      durationMins: minsBetween(firstLeg?.sched_dep || firstLeg?.depart || firstLeg?.dep, firstLeg?.sched_arr || firstLeg?.arrive || firstLeg?.arr),
      status: resolveCustomerRailStepStatus(firstLeg?.status, firstLeg?.realtime, railSeg?.status),
      source: null,
      notes: [Number.isFinite(Number(journey?.changes)) ? `${Number(journey.changes)} ${Number(journey.changes) === 1 ? "change" : "changes"}` : null].filter(
        Boolean
      ),
    });
    hubs.forEach((hub, idx) => {
      const hubUpper = String(hub || "").toUpperCase();
      const extractCrs = (v) => {
        const s = String(v || "").trim().toUpperCase();
        if (/^[A-Z0-9]{3}$/.test(s)) return s;
        const m = s.match(/\(([A-Z0-9]{3})\)\s*$/);
        return m?.[1] || null;
      };
      const matchingLeg = Array.isArray(journey?.legs)
        ? journey.legs.find((leg) => {
            const toCode = extractCrs(leg?.to_crs) || extractCrs(leg?.to) || String(leg?.to || "").toUpperCase();
            return toCode === hubUpper;
          })
        : null;
      timeline.push({
        key: `rail_interchange_${idx + 1}`,
        at: matchingLeg?.sched_arr || matchingLeg?.arrive || matchingLeg?.arr || null,
        title: `${stationLabelFriendly(matchingLeg?.from_crs || matchingLeg?.from || "Rail")} -> ${stationLabelFriendly(hubUpper)}`,
        from: null,
        to: null,
        durationMins: minsBetween(matchingLeg?.sched_dep || matchingLeg?.depart || matchingLeg?.dep, matchingLeg?.sched_arr || matchingLeg?.arrive || matchingLeg?.arr),
        status: resolveCustomerRailStepStatus(matchingLeg?.status, matchingLeg?.realtime, railSeg?.status),
        source: null,
        notes: [],
      });
    });

    if (finalLeg && finalLeg !== firstLeg) {
      timeline.push({
        key: "rail_final_leg_compact",
        at: finalLeg?.sched_dep || finalLeg?.depart || finalLeg?.dep || null,
        title: `${stationLabelFriendly(finalLeg?.from_crs || finalLeg?.from || gateway)} -> ${stationLabelFriendly(
          finalLeg?.to_crs || finalLeg?.to || gateway
        )}`,
        from: null,
        to: null,
        durationMins: minsBetween(finalLeg?.sched_dep || finalLeg?.depart || finalLeg?.dep, finalLeg?.sched_arr || finalLeg?.arrive || finalLeg?.arr),
        status: resolveCustomerRailStepStatus(finalLeg?.status, finalLeg?.realtime, railSeg?.status),
        source: null,
        notes: [],
      });
    }

    timeline.push({
      key: "gateway_hop",
      at: journey?.arrive || null,
      title:
        gLastMatched
          ? `Skylink bus to EMA${
              Number.isFinite(Number(gLastLeg?.duration_mins))
                ? ` • ${formatDuration(Number(gLastLeg.duration_mins))}`
                : Number.isFinite(Number(hop?.eta_safe_mins ?? hop?.eta_best_mins))
                ? ` • ${formatDuration(Number(hop?.eta_safe_mins ?? hop?.eta_best_mins))}`
                : ""
            }`
          : `Taxi to EMA${
              Number.isFinite(Number(hop?.eta_safe_mins ?? hop?.eta_best_mins))
                ? ` • ${formatDuration(Number(hop?.eta_safe_mins ?? hop?.eta_best_mins))}`
                : ""
            }`,
      from: stationLabelFriendly(gateway).replace(/\s*\(stop[^)]*\)\s*/gi, " ").replace(/\s+/g, " ").trim(),
      to: "EMA Terminal",
      durationMins:
        gLastMatched && Number.isFinite(Number(gLastLeg?.duration_mins))
          ? Number(gLastLeg.duration_mins)
          : hop?.eta_safe_mins ?? hop?.eta_best_mins ?? null,
      status: resolveGatewayTransferStatus(hop?.status),
      source: null,
      notes: [
        ...(gLastMatched
          ? [
              (() => {
                const m = String(gLastLeg?.from || "").match(/\(Stop\s*([^)]+)\)/i);
                return m?.[1] ? `Board at: Stop ${String(m[1]).trim()} (outside main entrance)` : null;
              })(),
              (() => {
                const joined = String((googleLegNotes(gLastLeg, hop?.eta_safe_mins ?? hop?.eta_best_mins ?? null) || []).join(" • "));
                const m = joined.match(/Coach Park\s*\((Bay\s*[A-Z0-9]+)\)/i);
                return m?.[1] ? `Arrives at: Coach Park (${m[1]})` : null;
              })(),
              "Frequency: Every 30 mins",
            ]
          : [
              (() => {
                const n = Number(hop?.estimated_fare_gbp);
                if (Number.isFinite(n) && n > 0) return `Est. fare ~£${Math.round(n)}`;
                const from = String(stationLabelFriendly(gateway) || "").toLowerCase();
                if (from.includes("east midlands parkway")) return "Est. fare ~£31";
                if (from.includes("nottingham")) return "Est. fare ~£46";
                if (from.includes("long eaton")) return "Est. fare ~£36";
                if (from.includes("derby")) return "Est. fare ~£45";
                return null;
              })(),
            ]),
      ].filter(Boolean),
    });

    timeline.push({
      key: "flight_anchor",
      at: flightAnchor,
      title:
        direction === "Arrival"
          ? `${String(viewForm?.flight_number || "Flight").toUpperCase()} from ${String(flightFromCity || flightFromCode || "Origin")}`
          : `${String(viewForm?.flight_number || "Flight").toUpperCase()} to ${String(flightToCity || flightToCode || "Destination")}`,
      from: null,
      to: null,
      durationMins: null,
      status: flightStatusBadge?.level || "unknown",
      source: null,
      notes: [String(flightStatusBadge?.label || "").toLowerCase().includes("scheduled") ? "On time" : flightStatusBadge?.label || "Status updated"].filter(Boolean),
    });

    timeline.push({
      key: "terminal_target",
      at: terminalTime || null,
      title: direction === "Arrival" ? "Target terminal exit" : "Target terminal arrival",
      from: "EMA terminal zone",
      to: direction === "Arrival" ? "Landside" : "Check-in/security",
      durationMins: null,
      status: terminalMargin == null ? "unknown" : terminalMargin >= 25 ? "green" : terminalMargin >= 0 ? "amber" : "red",
      source: "Policy target",
      notes: [
        terminalMargin == null
          ? "Margin unavailable"
          : terminalMargin >= 0
          ? `Margin to target: +${terminalMargin} mins`
          : `Margin to target: ${terminalMargin} mins`,
      ],
    });

    if (direction === "Departure") {
      timeline.push({
        key: "security",
        at: terminalTime || null,
        title: "Proceed to security",
        from: "Check-in",
        to: "Security",
        durationMins: null,
        status: terminalMargin == null ? "unknown" : terminalMargin >= 0 ? "green" : "amber",
        source: "Estimated",
        notes: ["Security flow is estimated and can vary by queue conditions."],
      });
      timeline.push({
        key: "gate",
        at: gateTime,
        title: "Gate readiness window",
        from: "Security",
        to: "Departure gate",
        durationMins: null,
        status: "unknown",
        source: "Estimated",
        notes: ["Gate: not yet assigned (check screens)."],
      });
    }

    return {
      available: !!journey,
      terminalEta,
      terminalMargin,
      timeline,
      checkedAt: railSeg?.last_checked || rail?.generated_time || null,
    };
  })();
  const selfDriveDetail = (() => {
    if (!backendDetails?.self_drive?.steps?.length) return { available: false, terminalMargin: null, timeline: [], checkedAt: null };
    const commonSteps = Array.isArray(backendDetails?.self_drive?.terminal_common_steps)
      ? backendDetails.self_drive.terminal_common_steps
      : Array.isArray(backendDetails?.terminal_common?.steps)
      ? backendDetails.terminal_common.steps
      : [];
    const mergedRaw = [...backendDetails.self_drive.steps, ...commonSteps];
    const flightStep = flightAnchor
      ? {
          key: "flight_anchor_drive",
          at: flightAnchor,
          title: direction === "Arrival" ? "Flight arrival at EMA" : "Flight departure from EMA",
          from: direction === "Arrival" ? (flightFromCode || "Origin") : "EMA",
          to: direction === "Arrival" ? "EMA" : (flightToCode || "Destination"),
          durationMins: null,
          status: flightStatusBadge?.level || "unknown",
          source: flight?.status_source || "flight_feed",
          notes: [flightStatusBadge?.label ? `Status: ${flightStatusBadge.label}` : null, routeHeadline || null].filter(Boolean),
        }
      : null;
    const merged = flightStep
      ? mergedRaw.filter((s) => !["flight_departure", "flight_arrival"].includes(String(s?.id || "")))
      : mergedRaw;
    const mergedSequenced = (() => {
      const out = merged
        .map((s) => ({ ...s }))
        .filter((s) => String(s?.id || "") !== "terminal_target");
      if (String(direction || "").trim().toLowerCase() !== "departure") return out;
      const terminalStep = out.find((s) => String(s?.id || "") === "arrive_terminal");
      const terminalMs = terminalStep?.at ? new Date(terminalStep.at).getTime() : NaN;
      if (!Number.isFinite(terminalMs)) return out;
      const sequence = ["checkin", "security", "gate"];
      let cursorMs = terminalMs;
      sequence.forEach((id) => {
        const s = out.find((x) => String(x?.id || "") === id);
        if (!s) return;
        const stepMs = s?.at ? new Date(s.at).getTime() : NaN;
        if (!Number.isFinite(stepMs) || stepMs < cursorMs) {
          s.at = new Date(cursorMs).toISOString();
        }
        const appliedMs = s?.at ? new Date(s.at).getTime() : NaN;
        if (Number.isFinite(appliedMs)) cursorMs = appliedMs + 60 * 1000;
      });
      return out;
    })();
    return {
      available: mergedSequenced.length > 0,
      terminalEta: null,
      terminalMargin: backendDetails.self_drive.margin_to_terminal_target_mins ?? null,
        timeline: [
          ...(flightStep ? [flightStep] : []),
          ...mergedSequenced.map((s, idx) => ({
            key: s.id || `step_${idx}`,
            at: s.at || null,
            title: s.title || "Step",
            from: s.from || null,
            to: s.to || null,
            durationMins: s.duration_mins ?? null,
            status: s.status || "unknown",
            statusReason: s.status_reason || null,
            source: s.source || "estimated",
            roadSummary: s.id === "leave_home" ? driveRoadSummary.label : null,
            notes: [s.disclaimer || null, ...(Array.isArray(s.notes) ? s.notes : [])].filter(Boolean),
          })),
        ],
      checkedAt: null,
    };
  })();
  const taxiDetail = (() => {
    if (!backendDetails?.taxi?.steps?.length) return { available: false, terminalMargin: null, timeline: [], checkedAt: null };
    const commonSteps = Array.isArray(backendDetails?.taxi?.terminal_common_steps)
      ? backendDetails.taxi.terminal_common_steps
      : Array.isArray(backendDetails?.terminal_common?.steps)
      ? backendDetails.terminal_common.steps
      : [];
    const mergedRaw = [...backendDetails.taxi.steps, ...commonSteps];
    const flightStep = flightAnchor
      ? {
          key: "flight_anchor_taxi",
          at: flightAnchor,
          title: direction === "Arrival" ? "Flight arrival at EMA" : "Flight departure from EMA",
          from: direction === "Arrival" ? (flightFromCode || "Origin") : "EMA",
          to: direction === "Arrival" ? "EMA" : (flightToCode || "Destination"),
          durationMins: null,
          status: flightStatusBadge?.level || "unknown",
          source: flight?.status_source || "flight_feed",
          notes: [flightStatusBadge?.label ? `Status: ${flightStatusBadge.label}` : null, routeHeadline || null].filter(Boolean),
        }
      : null;
    const merged = flightStep
      ? mergedRaw.filter((s) => !["flight_departure", "flight_arrival"].includes(String(s?.id || "")))
      : mergedRaw;
    const mergedSequenced = (() => {
      const out = merged
        .map((s) => ({ ...s }))
        .filter((s) => String(s?.id || "") !== "terminal_target");
      if (String(direction || "").trim().toLowerCase() !== "departure") return out;
      const terminalStep = out.find((s) => String(s?.id || "") === "arrive_terminal");
      const terminalMs = terminalStep?.at ? new Date(terminalStep.at).getTime() : NaN;
      if (!Number.isFinite(terminalMs)) return out;
      const sequence = ["checkin", "security", "gate"];
      let cursorMs = terminalMs;
      sequence.forEach((id) => {
        const s = out.find((x) => String(x?.id || "") === id);
        if (!s) return;
        const stepMs = s?.at ? new Date(s.at).getTime() : NaN;
        if (!Number.isFinite(stepMs) || stepMs < cursorMs) {
          s.at = new Date(cursorMs).toISOString();
        }
        const appliedMs = s?.at ? new Date(s.at).getTime() : NaN;
        if (Number.isFinite(appliedMs)) cursorMs = appliedMs + 60 * 1000;
      });
      return out;
    })();
    return {
      available: mergedSequenced.length > 0,
      terminalEta: null,
      terminalMargin: backendDetails.taxi.margin_to_terminal_target_mins ?? null,
        timeline: [
          ...(flightStep ? [flightStep] : []),
          ...mergedSequenced.map((s, idx) => ({
            key: s.id || `step_${idx}`,
            at: s.at || null,
            title: s.title || "Step",
            from: s.from || null,
            to: s.to || null,
            durationMins: s.duration_mins ?? null,
            status: s.status || "unknown",
            statusReason: s.status_reason || null,
            source: s.source || "estimated",
            roadSummary: s.id === "leave_home" ? taxiRoadSummary.label : null,
            notes: [s.disclaimer || null, ...(Array.isArray(s.notes) ? s.notes : [])].filter(Boolean),
          })),
        ],
      checkedAt: null,
    };
  })();
  const cardDetails = {
    Train: projectionDetailForTitle("Train") || trainDetail,
    "Self Drive": projectionDetailForTitle("Self Drive") || selfDriveDetail,
    Taxi: projectionDetailForTitle("Taxi") || taxiDetail,
  };
  const modeDomId = (mode) => String(mode || "").toLowerCase().replace(/\s+/g, "-");
  const toggleDetail = (mode) => {
    setOpenDetailMode((prev) => {
      const next = prev === mode ? null : mode;
      if (next && typeof document !== "undefined") {
        setTimeout(() => {
          const detailEl = document.querySelector(`[data-ema-detail="outbound-${modeDomId(mode)}"]`);
          detailEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
      }
      return next;
    });
  };
  const openHeroTimeChainDetail = (mode) => {
    const targetMode = mode || recommendedMode || selectedMode || "Train";
    setSelectedMode(targetMode);
    setOpenDetailMode(targetMode);
    if (typeof document !== "undefined") {
      setTimeout(() => {
        const detailEl = document.querySelector(`[data-ema-detail="outbound-${modeDomId(targetMode)}"]`);
        detailEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };
  const heroPrimaryMode = recommendedMode || selectedMode || "Train";
  const projectionHeroTimeMap = Array.isArray(projectionHero?.time_steps)
    ? projectionHero.time_steps.reduce((acc, step) => {
        acc[String(step?.id || "")] = step?.at || null;
        return acc;
      }, {})
    : {};
  const heroLeaveAt =
    projectionHeroTimeMap.leave ||
    (heroPrimaryMode === "Train"
      ? leaveHomeTime
      : heroPrimaryMode === "Self Drive"
      ? driveLeave?.leave || null
      : taxiLeave || null);
  const heroTerminalAt = projectionHeroTimeMap.terminal || terminalTime || null;
  const heroFlightAt = projectionHeroTimeMap.flight || flightAnchor || null;
  const heroTimeSteps = Array.isArray(projectionHero?.time_steps) && projectionHero.time_steps.length
    ? projectionHero.time_steps.map((step) => ({
        id: step?.id || "step",
        icon: step?.id === "terminal" ? "🎯" : step?.id === "flight" ? "✈️" : "🕒",
        label: step?.label || "Step",
        time: step?.at || null,
      }))
    : [
    {
      id: "leave",
      icon: "🕒",
      label: direction === "Arrival" ? "Leave home" : "Leave home",
      time: heroLeaveAt,
    },
    {
      id: "terminal",
      icon: "🎯",
      label: direction === "Arrival" ? "Terminal exit" : "Terminal arrival",
      time: heroTerminalAt,
    },
    {
      id: "flight",
      icon: "✈️",
      label: direction === "Arrival" ? "Flight arrives" : "Flight departs",
      time: heroFlightAt,
    },
  ];
  if (isReturnJourney) {
    return <ReturnRecommendationSummary result={result} viewForm={viewForm} journeyMeta={journeyMeta} />;
  }
  const monitoringItemsFor = (mode) => {
    if (mode === "Train") {
      const gateway = rail?.request?.resolved_dest_crs || rail?.request?.dest_crs || "gateway CRS";
      const firstMileLabel =
        rail?.best_candidate?.journeys?.[0]?.legs?.[0]?.from_crs ||
        rail?.best_candidate?.journeys?.[0]?.legs?.[0]?.from ||
        rail?.segments?.find((s) => s.id === "first_mile")?.to ||
        rail?.request?.access_decision?.preferred_crs ||
        "origin CRS";
      return [
        direction === "Arrival" ? "Flight arrival status" : "Flight departure status",
        `Home -> ${stationLabelCompact(firstMileLabel)} first-mile timing`,
        `${stationLabelCompact(firstMileLabel)} -> ${stationLabelCompact(gateway)} rail disruption`,
        `Gateway -> terminal${direction === "Arrival" ? "/coach bay" : " zone"} transfer`,
        "Station timing buffers",
        "Weather risk updates",
      ];
    }
    const roads =
      mode === "Self Drive"
        ? (driveRoadSummary?.roads || []).slice(0, 2)
        : (taxiRoadSummary?.roads || []).slice(0, 2);
    const roadLine = roads.length ? `${roads.join(" / ")} incident feed (NH)` : "Motorway/A-road incident feed (NH)";
    return [
      direction === "Arrival" ? "Flight arrival status" : "Flight departure status",
      roadLine,
      "Live traffic flow (Google/OSRM)",
      direction === "Arrival" ? "Terminal exit margin checks" : "Terminal arrival margin checks",
      "Weather risk updates",
    ];
  };

  const InlineTimeline = ({ detail }) => {
    const [openRoadSectionId, setOpenRoadSectionId] = useState(null);
    if (!detail) return null;
    const presentRoadInstruction = (value) => {
      const text = String(value || "").trim();
      if (!text) return "Step";
      if (text === "Continue through airport approach and parking access") return "Airport access and parking buffer";
      return text;
    };
    const normText = (v) =>
      String(v || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    const cleanedTimeline = Array.isArray(detail.timeline)
      ? detail.timeline
          .map((s) => ({ ...s }))
          .filter((s) => {
            const key = normText(s?.key);
            const title = normText(s?.title);
            if (key.includes("terminal_target")) return false;
            if (title.includes("target terminal arrival")) return false;
            return true;
          })
      : [];
    const compactRoadTimeline = (() => {
      const out = [...cleanedTimeline];
      const majorSteps = out.filter((s) => /^major_road_/i.test(String(s?.key || "")));
      if (!majorSteps.length) return out;
      const parseRoad = (step) => {
        const title = String(step?.title || "");
        const m = title.match(/\b([MA]\d{1,4}[A-Z]?)\b/i);
        return m?.[1] ? String(m[1]).toUpperCase() : null;
      };
      const parseMins = (step) => {
        const direct = Number(step?.durationMins);
        if (Number.isFinite(direct) && direct > 0) return direct;
        const n1 = String(step?.title || "").match(/(\d+)\s*h\s*(\d+)\s*m/i);
        if (n1?.[1] || n1?.[2]) return Number(n1?.[1] || 0) * 60 + Number(n1?.[2] || 0);
        const n2 = String(step?.title || "").match(/(\d+)\s*m/i);
        if (n2?.[1]) return Number(n2[1]);
        const n3 = String((step?.notes || []).join(" • ")).match(/(\d+)\s*mins?/i);
        if (n3?.[1]) return Number(n3[1]);
        return null;
      };
      const ranked = majorSteps.map((s) => {
        const road = parseRoad(s);
        const mins = parseMins(s);
        const isMotorway = /^M\d/.test(String(road || ""));
        const isARoad = /^A\d/.test(String(road || ""));
        return { step: s, key: String(s?.key || ""), road, mins, isMotorway, isARoad };
      });
      const keep = new Set(
        ranked
          .filter((r) => r.isMotorway || (r.isARoad && Number.isFinite(r.mins) && Number(r.mins) >= 30))
          .map((r) => r.key)
      );
      if (keep.size < 2) {
        ranked
          .slice()
          .sort((a, b) => Number(b.mins || 0) - Number(a.mins || 0))
          .forEach((r) => {
            if (keep.size >= 2) return;
            keep.add(r.key);
          });
      }
      return out.filter((s) => {
        const k = String(s?.key || "");
        if (!/^major_road_/i.test(k)) return true;
        return keep.has(k);
      });
    })();
    const sequencedTimeline = (() => {
      const out = [...compactRoadTimeline];
      const departureFlow = out.some((s) =>
        String(s?.title || "").toLowerCase().includes("flight departure")
      );
      if (!departureFlow) return out;
      const matchByTitle = (s, t) => normText(s?.title).includes(t);
      const anchorStep =
        out.find((s) => String(s?.key || "") === "gateway_to_terminal") ||
        out.find((s) => matchByTitle(s, "gateway to ema terminal")) ||
        out.find((s) => String(s?.key || "") === "arrive_terminal") ||
        out.find((s) => matchByTitle(s, "arrive ema terminal")) ||
        null;
      const anchorMs = anchorStep?.at ? new Date(anchorStep.at).getTime() : NaN;
      if (!Number.isFinite(anchorMs)) return out;
      const resolveStep = (id) => {
        if (id === "checkin") {
          return (
            out.find((x) => String(x?.key || "") === "checkin") ||
            out.find((x) => normText(x?.title).includes("check-in guidance")) ||
            null
          );
        }
        if (id === "security") {
          return (
            out.find((x) => String(x?.key || "") === "security") ||
            out.find((x) => normText(x?.title).includes("proceed to security")) ||
            null
          );
        }
        return (
          out.find((x) => String(x?.key || "") === "gate") ||
          out.find((x) => normText(x?.title).includes("gate assignment")) ||
          null
        );
      };
      const sequence = ["checkin", "security", "gate"];
      let cursorMs = anchorMs;
      sequence.forEach((id) => {
        const s = resolveStep(id);
        if (!s) return;
        const t = s?.at ? new Date(s.at).getTime() : NaN;
        if (!Number.isFinite(t) || t < cursorMs) {
          s.at = new Date(cursorMs).toISOString();
        }
        const applied = s?.at ? new Date(s.at).getTime() : NaN;
        if (Number.isFinite(applied)) cursorMs = applied + 60 * 1000;
      });
      return out;
    })();
    const orderedTimeline = [...sequencedTimeline].sort((a, b) => {
      const ta = a?.at ? new Date(a.at).getTime() : Number.POSITIVE_INFINITY;
      const tb = b?.at ? new Date(b.at).getTime() : Number.POSITIVE_INFINITY;
      if (!Number.isFinite(ta) && !Number.isFinite(tb)) return 0;
      if (!Number.isFinite(ta)) return 1;
      if (!Number.isFinite(tb)) return -1;
      return ta - tb;
    });
    const classifyMargin = (margin) => {
      if (margin == null || !Number.isFinite(Number(margin))) return { band: "Unknown", color: "#94a3b8", note: "Terminal target margin: unavailable" };
      const m = Number(margin);
      if (m >= 45) return { band: "Comfortable", color: "#22c55e", note: `Terminal target margin: +${m} mins (Comfortable buffer)` };
      if (m >= 20) return { band: "Safe", color: "#16a34a", note: `Terminal target margin: +${m} mins (Safe buffer)` };
      if (m >= 6) return { band: "Tight", color: "#22c55e", note: `Terminal target margin: +${m} mins (Tight buffer)` };
      if (m >= 0) return { band: "Tight", color: "#22c55e", note: `Terminal target margin: +${m} mins (On target, but with limited buffer)` };
      if (m >= -7) return { band: "Slightly late", color: "#22c55e", note: `Terminal target margin: ${m} mins (Slightly late - recommend leaving 10-15 mins earlier)` };
      return { band: "Late", color: "#ef4444", note: `Terminal target margin: ${m} mins (Late)` };
    };
    const marginMeta = classifyMargin(detail.terminalMargin);
    const marginColor = marginMeta.color;
    const marginLabel = marginMeta.note;
    const roadSections = Array.isArray(detail?.roadSections) ? detail.roadSections : [];
    const parkingArrivalStep = orderedTimeline.find((step) => String(step?.key || "").toLowerCase() === "arrive_parking") || null;
    const roadSectionCards = roadSections.map((section, sectionIdx) => {
      if (sectionIdx !== roadSections.length - 1) return section;
      const baseSteps = Array.isArray(section?.steps) ? section.steps : [];
      const lastStepAt = baseSteps.length ? baseSteps[baseSteps.length - 1]?.at || null : null;
      const parkingAt = parkingArrivalStep?.at || null;
      const gapMins = (() => {
        if (!lastStepAt || !parkingAt) return null;
        const fromMs = new Date(lastStepAt).getTime();
        const toMs = new Date(parkingAt).getTime();
        if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return null;
        return Math.round((toMs - fromMs) / 60000);
      })();
      if (!Number.isFinite(gapMins) || gapMins <= 3) return section;
      return {
        ...section,
        duration_mins: Number.isFinite(Number(section?.duration_mins)) ? Number(section.duration_mins) + gapMins : section?.duration_mins,
        steps: [
          ...baseSteps,
          {
            id: `${section?.id || `road_section_${sectionIdx}`}_buffered_access`,
            at: lastStepAt,
            instruction: "Continue through airport approach and parking access",
            primary_road: null,
            duration_mins: gapMins,
            notes: ["Safe arrival timing includes local airport traffic and parking access buffer."],
            status: section?.status || section?.display_level || "unknown",
          },
        ],
      };
    });
    const displayedTimeline = roadSections.length
      ? orderedTimeline.filter((step) => {
          const stepKeyNorm = String(step?.key || "").toLowerCase();
          return !(
            /^major_road_/i.test(stepKeyNorm) ||
            stepKeyNorm === "leave_home" ||
            stepKeyNorm === "final_approach" ||
            stepKeyNorm === "parking_access_buffer"
          );
        })
      : orderedTimeline;
    return (
      <div style={{ marginTop: 10, borderTop: "1px solid #1f2937", paddingTop: 10 }}>
        <div
          style={{
            border: `1px solid ${marginColor}66`,
            borderRadius: 10,
            background: `${marginColor}1f`,
            color: marginColor,
            fontSize: 12,
            fontWeight: 800,
            padding: "8px 10px",
            marginBottom: 10,
          }}
        >
          {marginLabel}
        </div>
        {!detail.available && (
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Detail timeline is not available for this request.
          </div>
        )}
        {detail.available && (
          <div style={{ display: "grid", gap: 10 }}>
            {roadSectionCards.length > 0 && roadSectionCards.map((section, sectionIdx) => {
              const sectionId = section?.id || `road_section_${sectionIdx}`;
              const isOpen = openRoadSectionId === sectionId;
              return (
                <div key={sectionId} style={{ border: "1px solid #1f2937", borderRadius: 10, padding: "8px 10px", background: "#0b1220" }}>
                  <button
                    type="button"
                    onClick={() => setOpenRoadSectionId((current) => (current === sectionId ? null : sectionId))}
                    style={{
                      width: "100%",
                      background: "transparent",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "start" }}>
                      <div style={{ fontWeight: 800, color: "#e2e8f0" }}>{section?.at ? formatTime(section.at) : "—"}</div>
                      <div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 800, fontSize: 13, color: "#e2e8f0" }}>{section?.title || `Road section ${sectionIdx + 1}`}</div>
                          <span
                            style={{
                              fontSize: 10,
                              borderRadius: 999,
                              border: `1px solid ${statusColor(section?.status || section?.display_level)}77`,
                              color: statusColor(section?.status || section?.display_level),
                              padding: "1px 7px",
                              textTransform: "capitalize",
                              fontWeight: 700,
                            }}
                          >
                            {String(section?.display_level || section?.status || "unknown").replace(/_/g, " ")}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                          {[section?.summary || null, Number.isFinite(Number(section?.duration_mins)) && Number(section.duration_mins) > 0 ? formatDuration(Number(section.duration_mins)) : null]
                            .filter(Boolean)
                            .join(" • ")}
                        </div>
                        <div style={{ marginTop: 2, fontSize: 11, color: "#93c5fd", fontWeight: 700 }}>{isOpen ? "Hide steps" : "View steps"}</div>
                      </div>
                    </div>
                  </button>
                  {isOpen && Array.isArray(section?.steps) && section.steps.length > 0 && (
                    <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                      {section.steps.map((step, stepIdx) => (
                        <div key={step?.id || `road_step_${sectionIdx}_${stepIdx}`} style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "start" }}>
                          <div style={{ fontWeight: 800, color: "#e2e8f0" }}>{step?.at ? formatTime(step.at) : "—"}</div>
                          <div style={{ borderLeft: "2px solid #334155", paddingLeft: 10 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 800, fontSize: 13, color: "#e2e8f0" }}>{presentRoadInstruction(step?.instruction)}</span>
                              {step?.primary_road && (
                                <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>{step.primary_road}</span>
                              )}
                            </div>
                            {Number.isFinite(Number(step?.duration_mins)) && (
                              <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>{Math.round(Number(step.duration_mins))} mins</div>
                            )}
                            {Array.isArray(step?.notes) && step.notes.length > 0 && (
                              <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>{step.notes.join(" • ")}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {displayedTimeline.map((step) => {
              const showStatus = step.showStatus !== false;
              const showSource = step.showSource !== false;
              const stepKeyNorm = String(step?.key || "").toLowerCase();
              const stepTitleNorm = String(step?.title || "").toLowerCase();
              const isCheckinStep = stepKeyNorm === "checkin" || stepTitleNorm.includes("check-in guidance");
              const isSecurityStep = stepKeyNorm === "security" || stepTitleNorm.includes("proceed to security");
              const isGateStep = stepKeyNorm === "gate" || stepTitleNorm.includes("gate assignment");
              const isFlightStep =
                stepKeyNorm.includes("flight") ||
                stepTitleNorm.includes("flight departure") ||
                stepTitleNorm.includes("flight arrival");
              const isMajorRoadStep = /^major_road_/i.test(stepKeyNorm);
              const leaveHomeText = `${stepTitleNorm} ${String(step?.to || "").toLowerCase()} ${String(step?.from || "").toLowerCase()}`;
              const isRoadLeaveStep =
                stepKeyNorm === "leave_home" &&
                (/self-drive/i.test(stepTitleNorm) || /parking|jetparks|jet parks/i.test(leaveHomeText));
              const isTaxiLeaveStep = stepKeyNorm === "leave_home" && !isRoadLeaveStep;
              const isArriveParkingStep = stepKeyNorm === "arrive_parking";
              const isArriveTerminalStep = stepKeyNorm === "arrive_terminal";
              const effectiveStepStatus = (() => {
                if (!isArriveTerminalStep) return step.status || "unknown";
                const m = Number(detail?.terminalMargin);
                if (!Number.isFinite(m)) return step.status || "unknown";
                if (m >= 20) return "green";
                if (m >= -7) return "light_green";
                if (m >= -15) return "amber";
                return "red";
              })();
              const c = statusColor(effectiveStepStatus);
              const roadDurLabel =
                step.durationMins != null && Number(step.durationMins) > 0 ? formatDuration(step.durationMins) : null;
              const extractZoneFromStep = (s) => {
                const hay = [s?.title, ...(Array.isArray(s?.notes) ? s.notes : [])].join(" • ");
                const m = String(hay).match(/\bZone\s*([A-Z])\b/i);
                return m?.[1] ? `Zone ${String(m[1]).toUpperCase()}` : null;
              };
              const zoneLabel = isCheckinStep ? extractZoneFromStep(step) : null;
              const checkinNoteLine = isCheckinStep
                ? (Array.isArray(step?.notes) ? step.notes.find((n) => /^Opens at\s+/i.test(String(n || "").trim())) : null)
                : null;
              const openMinsBeforeDeparture = (() => {
                if (!isCheckinStep || !flightAnchor) return null;
                if (checkinNoteLine) {
                  const m = String(checkinNoteLine).match(/\((\d+)h before departure\)/i);
                  if (m?.[1]) return Number(m[1]) * 60;
                }
                if (!step?.at) return null;
                const tCheckin = new Date(step.at).getTime();
                const tFlight = new Date(flightAnchor).getTime();
                if (!Number.isFinite(tCheckin) || !Number.isFinite(tFlight)) return null;
                const mins = Math.round((tFlight - tCheckin) / 60000);
                return mins > 0 ? mins : null;
              })();
              const openHoursLabel =
                Number.isFinite(openMinsBeforeDeparture) && openMinsBeforeDeparture > 0
                  ? `${Math.round(openMinsBeforeDeparture / 60)}h before departure`
                  : null;
              const routeCity = (() => {
                const m = String(routeHeadline || "").match(/\bto\s+([A-Za-z\s'-]+)\s*\(/i);
                if (m?.[1]) return m[1].trim();
                const fallbackTo = String(step?.to || "").trim();
                if (fallbackTo && fallbackTo !== "EMA") return fallbackTo;
                return "Destination";
              })();
              const routeOrigin = (() => {
                const m = String(routeHeadline || "").match(/\bfrom\s+([A-Za-z\s'-]+)\s*\(/i);
                if (m?.[1]) return m[1].trim();
                const fallbackFrom = String(step?.from || "").trim();
                if (fallbackFrom && fallbackFrom !== "EMA") return fallbackFrom;
                return "Origin";
              })();
              const flightNoDisplay = String(viewForm?.flight_number || flight?.flight_number || "Flight").toUpperCase();
              const shortFlightStatus = (() => {
                const statusText = String(step?.notes?.[0] || "").toLowerCase();
                if (statusText.includes("scheduled")) return "On time";
                if (statusText.includes("on time")) return "On time";
                if (statusText.includes("delayed")) return "Delayed";
                if (statusText.includes("cancel")) return "Cancelled";
                if (String(step?.status || "").toLowerCase() === "green") return "On time";
                return "Status updated";
              })();
              const displayTitle = isCheckinStep
                ? `Check-in${zoneLabel ? ` (${zoneLabel})` : ""}`
                : isSecurityStep
                ? "Security"
                : isGateStep
                ? "Gate TBA"
                : isFlightStep
                ? direction === "Arrival"
                  ? `${flightNoDisplay} from ${routeOrigin}`
                  : `${flightNoDisplay} to ${routeCity}`
                : isMajorRoadStep
                ? `${String(step.title || "").replace(/\s*\(main corridor\)\s*/i, "").trim()}${roadDurLabel ? ` • ${roadDurLabel}` : ""}`
                : isRoadLeaveStep && String(direction || "").toLowerCase() === "arrival"
                ? "Depart home (self-drive)"
                : isTaxiLeaveStep && String(direction || "").toLowerCase() === "arrival"
                ? "Depart home (taxi)"
                : prettifyStationText(step.title);
              const displaySubline =
                isCheckinStep
                  ? checkinNoteLine || (step?.at ? `Opens at ${formatTime(step.at)}${openHoursLabel ? ` (${openHoursLabel})` : ""}` : "")
                  : isSecurityStep
                  ? "Allow up to 30 mins"
                  : isGateStep
                  ? "Expected ~60 mins before departure"
                  : isFlightStep
                  ? shortFlightStatus
                  : isMajorRoadStep
                  ? ""
                  : prettifyStationText(step.metaLine || (step.from && step.to ? `${step.from} -> ${step.to}` : step.from || step.to || ""));
              const displayNotes =
                isCheckinStep || isSecurityStep || isGateStep || isFlightStep
                  ? []
                  : isRoadLeaveStep
                  ? []
                  : isTaxiLeaveStep
                  ? []
                  : isMajorRoadStep
                  ? []
                  : isArriveParkingStep
                  ? (Array.isArray(step.notes) ? step.notes.filter((n) => /parking estimate/i.test(String(n))) : [])
                  : isArriveTerminalStep
                  ? []
                  : step.notes;
              return (
                <div key={step.key} style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "start" }}>
                  <div style={{ fontWeight: 800, color: "#e2e8f0" }}>{step.at ? formatTime(step.at) : "—"}</div>
                  <div style={{ borderLeft: "2px solid #334155", paddingLeft: 10 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: "#e2e8f0" }}>{displayTitle}</span>
                      {showStatus && !isFlightStep && (
                        <span
                          style={{
                            fontSize: 10,
                            borderRadius: 999,
                            border: `1px solid ${c}77`,
                            color: c,
                            padding: "1px 7px",
                            textTransform: "capitalize",
                            fontWeight: 700,
                          }}
                        >
                          {String(effectiveStepStatus || "unknown").replace(/_/g, " ")}
                        </span>
                      )}
                      {/* source tag intentionally hidden in UI; status pill remains visible */}
                    </div>
                    {step.statusReason && (
                      <div style={{ fontSize: 11, color: c, fontWeight: 700 }}>
                        {effectiveStepStatus === "amber" ? "Amber" : effectiveStepStatus === "red" ? "Red" : "Status"} — {step.statusReason}
                      </div>
                    )}
                    {(() => {
                      const sub = String(displaySubline || "").trim();
                      const hideSubline = !sub || sub === "— -> —";
                      if (hideSubline) return null;
                      return (
                        <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                          {sub}
                          {!isMajorRoadStep &&
                          step.durationMins != null &&
                          Number(step.durationMins) > 0 &&
                          !/^from\s+/i.test(sub)
                            ? ` • ${formatDuration(step.durationMins)}`
                            : ""}
                        </div>
                      );
                    })()}
                    {isGateStep && (
                      <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>
                        Allow 10-15 mins walk
                      </div>
                    )}
                    {step.roadSummary && (
                      <div style={{ marginTop: 2, fontSize: 11, color: "#93c5fd", fontWeight: 700 }}>
                        Main roads: {step.roadSummary}
                      </div>
                    )}
                    {displayNotes?.length > 0 && (
                      <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>
                        {displayNotes.join(" • ")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <details style={{ marginTop: 4 }}>
              <summary style={{ cursor: "pointer", color: "#93c5fd", fontSize: 12, fontWeight: 700 }}>Why this plan</summary>
              <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8", display: "grid", gap: 4 }}>
                <div>Buffers: airport target policy from current EMA journey logic.</div>
                <div>Sources: NR journey + incidents, first/last-mile estimators, road/weather context.</div>
                <div>Last checked: {detail.checkedAt ? `${formatDate(detail.checkedAt)} ${formatTime(detail.checkedAt)}` : "—"}</div>
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: isCompactMobile ? 12 : 16, background: "#0f172a", color: "#e2e8f0", width: "100%", maxWidth: "100%", boxSizing: "border-box", margin: 0 }}>
      <div style={{ fontWeight: 800, fontSize: 20, color: "#e2e8f0" }}>Live Travel Recommendation</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span>{flightLabel}</span>
        {flightStatusBadge?.label && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 8px",
              borderRadius: 999,
              border: `1px solid ${statusColor(flightStatusBadge.level)}66`,
              color: statusColor(flightStatusBadge.level),
              fontWeight: 700,
              fontSize: 11,
              lineHeight: 1.1,
              background: `${statusColor(flightStatusBadge.level)}1A`,
            }}
            title={flightStatusBadge?.raw || ""}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor(flightStatusBadge.level) }} />
            {flightStatusBadge.label}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
        {badge}
        <div style={{ fontSize: 13, color: "#cbd5e1" }}>Weather: {weatherLine}</div>
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}>
        {recommendationSummaryLine ||
          (direction === "Arrival"
            ? "Pickup journey plan from home to EMA timed to terminal exit after landing."
            : "Departure journey plan from home to EMA terminal with check-in buffer coverage.")}
      </div>
      {routeHeadline && <div style={{ marginTop: 4, fontSize: 13, color: "#cbd5e1", fontWeight: 600 }}>{direction === "Arrival" ? "🛬 " : "🛫 "}{routeHeadline}</div>}
      {isCompactMobile ? (
        <button
          type="button"
          onClick={() => openHeroTimeChainDetail(heroPrimaryMode)}
          style={{
            marginTop: 8,
            width: "100%",
            border: "1px solid #334155",
            background: "#0b1220",
            color: "#cbd5e1",
            borderRadius: 10,
            padding: "8px 8px",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr auto 1fr",
            alignItems: "center",
            justifyItems: "center",
            columnGap: 6,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            textAlign: "center",
            overflow: "hidden",
          }}
          title="Open full timeline"
        >
          <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.1 }}>{heroTimeSteps?.[0]?.label || "Leave home"}</span>
            <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 800 }}>{heroTimeSteps?.[0]?.icon || "🕒"} {heroTimeSteps?.[0]?.time ? formatTime(heroTimeSteps[0].time) : "—"}</span>
          </span>
          <span style={{ color: "#475569", fontSize: 12 }}>→</span>
          <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.1 }}>{heroTimeSteps?.[1]?.label || "Terminal"}</span>
            <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 800 }}>{heroTimeSteps?.[1]?.icon || "🎯"} {heroTimeSteps?.[1]?.time ? formatTime(heroTimeSteps[1].time) : "—"}</span>
          </span>
          <span style={{ color: "#475569", fontSize: 12 }}>→</span>
          <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.1 }}>{heroTimeSteps?.[2]?.label || "Flight"}</span>
            <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 800 }}>{heroTimeSteps?.[2]?.icon || "✈️"} {heroTimeSteps?.[2]?.time ? formatTime(heroTimeSteps[2].time) : "—"}</span>
          </span>
        </button>
      ) : (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", overflowX: "auto", paddingBottom: 2 }}>
          {heroTimeSteps.map((step, idx) => (
            <React.Fragment key={`hero-time-${step.id}`}>
              <button
                type="button"
                onClick={() => openHeroTimeChainDetail(heroPrimaryMode)}
                style={{
                  border: "1px solid #334155",
                  background: "#0b1220",
                  color: "#cbd5e1",
                  borderRadius: 999,
                  padding: "6px 10px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  width: "auto",
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                }}
                title="Open full timeline"
              >
                <span>{step.icon}</span>
                <span>{step.label}</span>
                <span style={{ color: "#e2e8f0" }}>{step.time ? formatTime(step.time) : "—"}</span>
              </button>
              {idx < heroTimeSteps.length - 1 && <span style={{ color: "#475569", fontSize: 12, flex: "0 0 auto" }}>→</span>}
            </React.Fragment>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10, border: "1px solid #1f2937", borderRadius: 10, background: "#0b1220", padding: "8px 10px", display: "grid", gap: 6 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color: "#a7f3d0" }}>
            {hasContinuousMonitoring ? "Checks Live" : "Current Snapshot"}
          </span>
          <div style={{ fontSize: 12, color: "#cbd5e1", fontWeight: 700 }}>
            {checksSummaryLine}
          </div>
        </div>
        <details>
          <summary style={{ cursor: "pointer", color: "#93c5fd", fontSize: 11, fontWeight: 700 }}>
            {hasContinuousMonitoring ? "View monitored routes" : "View route coverage"}
          </summary>
          <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {displayedChecksRows.map((row) => {
              const lvl = normalizeLevel(row.level);
              const chip = statusColor(lvl);
              return (
                <span
                  key={`route-status-${row.key}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "2px 8px",
                    borderRadius: 999,
                    border: `1px solid ${chip}66`,
                    background: `${chip}22`,
                    color: chip,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "capitalize",
                  }}
                >
                  {row.label} {lvl.replace("_", " ")}
                </span>
              );
            })}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>
            {[
              roadsLine ? `Roads: ${roadsLine}` : null,
              crsLine ? `Rail CRS: ${crsLine}` : null,
              weatherLineTargets ? `Weather: ${weatherLineTargets}` : null,
            ]
              .filter(Boolean)
              .join(" • ") || "Journey targets seeded"}
          </div>
        </details>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          {[nextCheckDue ? `Next check: ${formatDate(nextCheckDue)} ${formatTime(nextCheckDue)}` : "Next check: pending", phaseLabel ? `Phase: ${phaseLabel}` : null, lastMonitorAt ? `Last run: ${formatDate(lastMonitorAt)} ${formatTime(lastMonitorAt)}` : null]
            .filter(Boolean)
            .join(" • ")}
        </div>
        {!hasContinuousMonitoring && (
          <div style={{ fontSize: 11, color: "#fcd34d", fontWeight: 700 }}>
            Verify your email to activate journey alerts. We’ll monitor your route and let you know if anything important changes.
          </div>
        )}
      </div>
      {isCompactMobile && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 5, marginTop: 10, width: "100%" }}>
          {heroCards.map((c) => {
            const active = selectedMode === c.title;
            const tabLabel = c.title === "Self Drive" ? "Self Drive" : c.title;
            return (
              <button
                key={`tab-${c.title}`}
                type="button"
                onClick={() => {
                  setSelectedMode(c.title);
                  setOpenDetailMode(null);
                }}
                style={{
                  borderRadius: 999,
                  border: active ? "1px solid #22d3ee" : "1px solid #334155",
                  background: active ? "#0f2d45" : "#0b1626",
                  color: active ? "#67e8f9" : "#cbd5e1",
                  fontWeight: 800,
                  fontSize: 10.5,
                  padding: "7px 4px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                {tabLabel}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gap: isCompactMobile ? 8 : 10, gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(auto-fit,minmax(220px,1fr))", marginTop: 12, minWidth: 0 }}>
        {visibleHeroCards.map((c) => {
          const projectionCard = projectionCardForTitle(c.title);
          const projectionDetail = projectionDetailForTitle(c.title);
          const roadLevelForMode =
            c.title === "Self Drive"
              ? normalizeLevel(projectionCards?.self_drive?.road_status_level || projectionCards?.self_drive?.display_level || sourceLevels?.self_drive_road || canonicalRoadLevel)
              : c.title === "Taxi"
              ? normalizeLevel(projectionCards?.taxi?.road_status_level || projectionCards?.taxi?.display_level || sourceLevels?.taxi_road || canonicalRoadLevel)
              : canonicalRoadLevel;
          const statusLabel = c.title === "Train" ? "Rail status" : "Road status";
          const statusLevel =
            normalizeLevel(
              c.title === "Train"
                ? projectionCard?.status || displayedRailStatusLevel
                : projectionCard?.road_status_level || projectionCard?.display_level || roadLevelForMode
            ) || "unknown";
          const terminalChipLevel =
            c.title === "Train"
              ? null
              : normalizeLevel(
                  projectionCard?.terminal_display_level ||
                    projectionCard?.terminal_margin_status ||
                    (Number.isFinite(Number(projectionDetail?.terminalMargin))
                      ? Number(projectionDetail.terminalMargin) >= 20
                        ? "green"
                        : Number(projectionDetail.terminalMargin) >= 6
                        ? "amber"
                        : Number(projectionDetail.terminalMargin) >= 0
                        ? "amber"
                        : Number(projectionDetail.terminalMargin) >= -10
                        ? "amber"
                        : "red"
                      : null)
                );
          const terminalChipText =
            c.title === "Train"
              ? null
              : projectionCard?.terminal_margin_note ||
                (Number.isFinite(Number(projectionDetail?.terminalMargin))
                  ? Number(projectionDetail.terminalMargin) >= 20
                    ? `Terminal target margin: +${Number(projectionDetail.terminalMargin)} mins (Safe buffer)`
                    : Number(projectionDetail.terminalMargin) >= 6
                    ? `Terminal target margin: +${Number(projectionDetail.terminalMargin)} mins (Tight buffer)`
                    : Number(projectionDetail.terminalMargin) >= 0
                    ? `Terminal target margin: +${Number(projectionDetail.terminalMargin)} mins (On target, but with limited buffer)`
                    : Number(projectionDetail.terminalMargin) >= -10
                    ? `Terminal target margin: ${Number(projectionDetail.terminalMargin)} mins (Slightly late - recommend leaving 10-15 mins earlier)`
                    : `Terminal target margin: ${Number(projectionDetail.terminalMargin)} mins (Late)`
                  : terminalChipLevel && terminalChipLevel !== "green"
                  ? "Terminal timing monitored."
                  : null);
          const leaveLabel =
            projectionCard?.leave_home_at
              ? formatTime(projectionCard.leave_home_at)
              : c.title === "Train"
              ? leaveHomeTime
                ? formatTime(leaveHomeTime)
                : "—"
              : c.title === "Self Drive"
              ? driveLeave?.leave
                ? formatTime(driveLeave.leave)
                : "—"
              : taxiLeave
              ? formatTime(taxiLeave)
              : "—";
          const costLabel =
            projectionCard?.cost_text ||
            (c.title === "Train"
              ? `Cost: ${rail?.best_candidate?.fare?.total_gbp != null ? `£${rail.best_candidate.fare.total_gbp.toFixed(2)}` : "—"}`
              : c.title === "Self Drive"
              ? driveCostLabel
              : taxi?.fare?.price != null
              ? `Cost: £${Number(taxi.fare.price).toFixed(2)}`
              : "Cost: awaiting quote");
          const durationLabel =
            projectionCard?.duration_text ||
            (c.title === "Train"
              ? formatDuration(railDurationMins)
              : c.title === "Self Drive"
              ? formatDuration(driveDurationMins)
              : formatDuration(taxiDurationMins));
          const trainSelectedWindow =
            rail?.best_candidate?.journeys?.[0]?.depart && rail?.best_candidate?.journeys?.[0]?.arrive
              ? `${formatTime(rail.best_candidate.journeys[0].depart)}→${formatTime(rail.best_candidate.journeys[0].arrive)}`
              : null;
          const trainAltWindow = balancedAlt
            ? `Alt ${formatTime(balancedAlt.depart)}→${formatTime(balancedAlt.arrive)} (+${Math.round(balancedAlt._delta)}m)`
            : null;
          const trainInsight =
            trainSelectedWindow
              ? `${isVeryEarlyRailArrival ? "Early • " : ""}Selected ${trainSelectedWindow}${
                  trainAltWindow ? ` • ${trainAltWindow}` : stationTarget ? ` • Target ${formatTime(stationTarget)}` : ""
                }`
              : rail?.no_rail_reason ||
                (direction === "Arrival"
                  ? "No rail options found for this pickup window."
                  : "No rail options found for this departure window.");
          const driveInsight =
            driveLeave?.transfer != null
              ? `Parking transfer: ${driveLeave.transfer} mins (${driveParkingProduct || (direction === "Arrival" ? "Short Stay 1" : "JetParks")})`
              : "Parking transfer monitored live";
          const taxiPrimaryRoad = taxiRoadSummary?.mainRoad || null;
          const taxiEtaSignal =
            roadLevelForMode === "green" || roadLevelForMode === "light_green"
              ? "ETA stable"
              : roadLevelForMode === "amber"
              ? "possible delay"
              : roadLevelForMode === "red"
              ? "disruption risk"
              : "ETA monitored";
          const taxiRoadsCompact = (taxiRoadSummary?.roads || []).slice(0, 2).join(" • ");
          const taxiInsight = `${taxiRoadsCompact || (taxiPrimaryRoad ? `Route via ${taxiPrimaryRoad}` : "Route monitored")} • ${taxiEtaSignal}`;
          const compactInsight =
            projectionCard?.unavailability_message ||
            projectionCard?.insight ||
            projectionCard?.summary ||
            (c.title === "Train" ? trainInsight : c.title === "Self Drive" ? driveInsight : taxiInsight);
          const terminalTimingValue =
            c.title === "Train"
              ? projectionCard?.terminal_risk_state
                ? projectionCard.terminal_risk_state.replace(/_/g, " ")
                : projectionCard?.terminal_margin_note
                ? projectionCard.terminal_margin_note
                : "monitored"
              : projectionCard?.terminal_risk_state
              ? projectionCard.terminal_risk_state.replace(/_/g, " ")
              : terminalChipLevel
              ? terminalChipLevel.replace(/_/g, " ")
              : "on target";
          const compactPrimaryTrainRow =
            trainSelectedWindow && projectionDetail?.terminalMargin != null
              ? `${trainSelectedWindow} • ${Math.abs(Number(projectionDetail.terminalMargin))} mins ${Number(projectionDetail.terminalMargin) >= 0 ? "before" : "after"} target`
              : trainSelectedWindow
              ? trainSelectedWindow
              : compactInsight;
          const compactDriveInsight =
            driveLeave?.transfer != null
              ? `${driveLeave.transfer} mins transfer (${driveParkingProduct || (direction === "Arrival" ? "Short Stay 1" : "JetParks")})`
              : "Parking monitored";
          const compactTerminalText = Number.isFinite(Number(projectionDetail?.terminalMargin))
            ? `${Number(projectionDetail.terminalMargin)} mins${Number(projectionDetail.terminalMargin) < 0 ? ", leave earlier" : ""}`
            : terminalChipText || "Stable";
          const statusValue = String(statusLevel || "unknown").replace(/_/g, " ");
          const primaryTrainRow =
            trainSelectedWindow
              ? `Selected ${trainSelectedWindow}${
                  projectionDetail?.terminalMargin != null
                    ? ` • ${Number(projectionDetail.terminalMargin)} mins ${Number(projectionDetail.terminalMargin) >= 0 ? "before" : "after"} terminal target`
                    : stationTarget
                    ? ` • target ${formatTime(stationTarget)}`
                    : ""
                }`
              : compactInsight;
          const alternateTrainRow =
            trainAltWindow ||
            (projectionCard?.confidence_band === "low"
              ? "Alternate train guidance will refine closer to departure"
              : projectionCard?.reason_text || "Alternate train not highlighted");
          const fixedRows = [
            { label: statusLabel, value: statusValue, tone: statusColor(statusLevel), strong: true },
            {
              label: "Terminal timing",
              value: terminalTimingValue,
              tone: terminalChipLevel ? statusColor(terminalChipLevel) : "#cbd5e1",
              strong: !!terminalChipLevel,
            },
            { label: "Leave", value: leaveLabel, strong: true },
            { label: "Cost", value: String(costLabel || "").replace(/^(Cost|Fuel):\s*/i, "") },
            { label: "Duration", value: durationLabel },
            {
              label: c.title === "Train" ? "Selected train" : c.title === "Self Drive" ? "Parking" : "Route",
              value: isCompactMobile
                ? c.title === "Train"
                  ? compactPrimaryTrainRow
                  : c.title === "Self Drive"
                  ? compactDriveInsight
                  : compactInsight
                : c.title === "Train"
                ? primaryTrainRow
                : compactInsight,
              subtle: true,
            },
            {
              label: c.title === "Train" ? "Alternate train" : c.title === "Self Drive" ? "Terminal margin" : "Terminal margin",
              value:
                c.title === "Train"
                  ? isCompactMobile
                    ? projectionCard?.reason_text || "Rail stable"
                    : alternateTrainRow
                  : projectionCard?.advisory_state && projectionCard.advisory_state !== "live"
                  ? projectionCard?.confidence_band === "low"
                    ? "Early/advisory signal only"
                    : projectionCard?.reason_text || "Advisory-only signal"
                  : isCompactMobile
                  ? compactTerminalText
                  : terminalChipText || "Stable",
              subtle: true,
            },
          ];
          const cardAvailable = projectionCard?.available != null ? !!projectionCard.available : !!c.data;
          return (
          <div
            key={c.title}
            style={{
              borderRadius: 14,
              padding: isCompactMobile ? 10 : 12,
              minWidth: 0,
              background: c.title === recommendedMode ? "linear-gradient(180deg, #11263a 0%, #111827 58%)" : "linear-gradient(180deg, #10263d 0%, #0b1726 100%)",
              border: cardAvailable
                ? c.title === recommendedMode
                  ? "1px solid #22d3ee"
                  : "1px solid #47688d"
                : "1px dashed #334155",
              boxShadow:
                c.title === recommendedMode
                  ? "0 0 0 1px rgba(34,211,238,0.35), 0 12px 28px rgba(34,211,238,0.14)"
                  : "inset 0 0 0 1px rgba(125,211,252,0.2), 0 0 0 1px rgba(71,104,141,0.45), 0 2px 0 rgba(148,163,184,0.2), 0 10px 24px rgba(2,6,23,0.42)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#e2e8f0" }}>{c.title}</div>
              {c.title === recommendedMode && (
                <div style={{ padding: "2px 8px", borderRadius: 999, background: "#22d3ee33", color: "#06b6d4", fontSize: 11, fontWeight: 800 }}>
                  Recommended
                </div>
              )}
            </div>
            {!cardAvailable && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
                {projectionCard?.unavailability_message || projectionCard?.summary || (c.title === "Train" ? "Rail plan unavailable for this request." : "Waiting for data…")}
              </div>
            )}
            {cardAvailable && (
              <div style={{ marginTop: 10, display: "grid", gap: 4 }}>
                {fixedRows.map((row, idx) => (
                  <div
                    key={`${c.title}-row-${idx}`}
                    style={{
                      minHeight: isCompactMobile ? 18 : 20,
                      display: "grid",
                      gridTemplateColumns: isCompactMobile ? "92px 1fr" : "108px 1fr",
                      gap: 8,
                      alignItems: "start",
                      fontSize: isCompactMobile ? 11 : 12,
                      lineHeight: 1.35,
                    }}
                  >
                    <div style={{ color: row.tone || "#8fb4d9", fontWeight: row.strong ? 800 : 700, letterSpacing: 0.1 }}>
                      {row.label}
                    </div>
                    <div
                      style={{
                        color:
                          idx <= 1 && row.tone
                            ? row.tone
                            : row.subtle
                            ? c.title === "Train" && !trainSelectedWindow && idx === 5
                              ? "#94a3b8"
                              : "#cbd5e1"
                            : "#e2e8f0",
                        fontWeight: row.strong ? 800 : 600,
                      }}
                    >
                      {row.value || "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              style={{
                marginTop: 12,
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid #1f2937",
                background: "#0ea5e915",
                color: "#0ea5e9",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
                opacity: 1,
              }}
              onClick={() => toggleDetail(c.title)}
            >
              {openDetailMode === c.title ? "Hide details ↑" : "View details →"}
            </button>
            {openDetailMode === c.title && (
              <div data-ema-detail={`outbound-${modeDomId(c.title)}`} style={{ marginTop: 10, borderTop: "1px solid #1f2937", paddingTop: 10, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.45, border: "1px solid #1f2937", borderRadius: 8, padding: "6px 8px", background: "#0b1220" }}>
                  <div style={{ fontWeight: 700, color: "#cbd5e1" }}>Monitoring active (registered users, up to flight day)</div>
                  <div>• {monitoringItemsFor(c.title).join(" • ")}</div>
                </div>
                <InlineTimeline detail={cardDetails[c.title]} />
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function ReturnRecommendationSummary({ result, viewForm, journeyMeta }) {
  const [openDetailMode, setOpenDetailMode] = useState(null);
  const [openReturnRoadSectionId, setOpenReturnRoadSectionId] = useState(null);
  const isCompactMobile = useCompactMobile(768);
  const uiProjection = result?.ui_projection && typeof result.ui_projection === "object" ? result.ui_projection : null;
  const hasReturnProjection = String(uiProjection?.flow || "").toLowerCase() === "return";
  const projectionHero = uiProjection?.hero || {};
  const projectionCards = uiProjection?.cards || {};
  const projectionTimelines = uiProjection?.timelines || {};
  const projectionRecommendedMode = String(uiProjection?.recommended_mode || "").trim().toLowerCase();
  const projectionHeroStepAt = (id) =>
    Array.isArray(projectionHero?.time_steps)
      ? projectionHero.time_steps.find((step) => String(step?.id || "") === id)?.at || null
      : null;
  const projectionCardForTitle = (title) => {
    const key =
      title === "Train" ? "train" : title === "Self Drive" ? "self_drive" : title === "Taxi" ? "taxi" : null;
    return key ? projectionCards?.[key] || null : null;
  };
  const projectionTimelineForTitle = (title) => {
    const key =
      title === "Train" ? "train" : title === "Self Drive" ? "self_drive" : title === "Taxi" ? "taxi" : null;
    const tl = key ? projectionTimelines?.[key] || null : null;
    return Array.isArray(tl?.steps) ? tl.steps : null;
  };
  const projectionRoadSectionsForTitle = (title) => {
    const key =
      title === "Train" ? "train" : title === "Self Drive" ? "self_drive" : title === "Taxi" ? "taxi" : null;
    const tl = key ? projectionTimelines?.[key] || null : null;
    return Array.isArray(tl?.road_sections) ? tl.road_sections : null;
  };
  const rail = result?.rail || null;
  const taxi = result?.taxi || null;
  const drive = result?.drive || null;
  const sourceLevels = result?.source_levels || {};
  const monitorTargets = result?.monitor_targets || {};
  const policyState = result?.policy_state || {};
  const recommendation = result?.recommendation || null;
  const flight = result?.flight || null;
  const flightStatusBadge = hasReturnProjection
    ? projectionHero?.flight_status_badge || null
    : projectionHero?.flight_status_badge || flight?.status_badge || null;
  const flightAirlineName = String(flight?.airline?.name || "").trim();
  const flightOriginCity = String(flight?.origin?.city_name || "").trim();
  const flightOriginCode = String(flight?.origin?.airport_code || "").trim().toUpperCase();
  const airlinePretty = flightAirlineName
    ? flightAirlineName
        .replace(/\.COM$/i, "")
        .replace(/\b([A-Z]+)/g, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase())
    : "Inbound";
  const routeHeadline = hasReturnProjection
    ? String(projectionHero?.route_headline || "").trim() || null
    : String(projectionHero?.route_headline || "").trim() ||
      (flightOriginCity || flightOriginCode
        ? `${airlinePretty} arrival from ${flightOriginCity || "origin"}${flightOriginCode ? ` (${flightOriginCode})` : ""}`
        : null);
  const stationNames = result?.diagnostics?.station_names || {};
  const flightAnchor =
    projectionHeroStepAt("flight") || viewForm?.flight_anchor_utc || flight?.arrival || flight?.scheduled_arrival || null;
  const arrivalOps = result?.return_guidance?.arrival_ops || null;
  const disembarkMins = Number(arrivalOps?.disembark_mins_est ?? 12);
  const immigrationMins = Number(arrivalOps?.immigration_mins_est ?? 18);
  const baggageOpenMins = Number(arrivalOps?.baggage_open_after_mins ?? 14);
  const baggageReclaimMins = Number(arrivalOps?.baggage_reclaim_mins_est ?? 30);
  const terminalExitAt =
    projectionHeroStepAt("terminal") || result?.return_guidance?.terminal_exit_at || rail?.request?.terminal_exit_at || null;
  const addMins = (iso, mins) => {
    if (!iso || !Number.isFinite(Number(mins))) return null;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return null;
    return new Date(t + Number(mins) * 60000).toISOString();
  };
  const immigrationAt = addMins(flightAnchor, Math.max(8, disembarkMins));
  const immigrationEndAt = addMins(immigrationAt, Math.max(8, immigrationMins));
  const baggageAtRaw = addMins(flightAnchor, Math.max(10, baggageOpenMins));
  const baggageAt = (() => {
    const b = baggageAtRaw ? new Date(baggageAtRaw).getTime() : NaN;
    const i = immigrationAt ? new Date(immigrationAt).getTime() : NaN;
    if (!Number.isFinite(b) && !Number.isFinite(i)) return null;
    if (!Number.isFinite(i)) return baggageAtRaw;
    if (!Number.isFinite(b)) return addMins(immigrationAt, 7);
    const minAfterImmStart = i + 7 * 60000;
    return new Date(Math.max(b, minAfterImmStart)).toISOString();
  })();
  const baggageEndAt = addMins(baggageAt, Math.max(15, baggageReclaimMins));
  const normalizeStatusLevel = (level) => {
    const v = String(level || "").toLowerCase().trim();
    if (!v) return "unknown";
    if (v.includes("sched")) return "green";
    if (v.includes("cancel")) return "red";
    if (v.includes("delay")) return "amber";
    if (v.includes("light_green")) return "light_green";
    if (v.includes("green")) return "green";
    if (v.includes("amber")) return "amber";
    if (v.includes("red")) return "red";
    return v;
  };
  const statusColor = (level) => {
    if (level === "green") return "#16a34a";
    if (level === "light_green") return "#22c55e";
    if (level === "amber") return "#f59e0b";
    if (level === "red") return "#dc2626";
    return "#94a3b8";
  };
  const canonicalRoadLevel = normalizeStatusLevel(
    sourceLevels?.road || drive?.road?.disruption?.level || drive?.road?.status || taxi?.road?.disruption?.level || taxi?.road?.status
  );
  const canonicalRailLevel = normalizeStatusLevel(
    sourceLevels?.rail || rail?.overall_disruption?.level || rail?.best_candidate?.disruption?.level
  );
  const canonicalFlightLevel = normalizeStatusLevel(sourceLevels?.flight || flightStatusBadge?.level || flight?.status || flight?.raw?.status);
  const canonicalWeatherLevel = normalizeStatusLevel(
    sourceLevels?.weather || rail?.weather?.risk?.level || drive?.weather?.risk?.level || taxi?.weather?.risk?.level
  );
  const canonicalTflLevel = normalizeStatusLevel(sourceLevels?.tfl || "unknown");
  const severityRank = (lvl) => {
    const v = normalizeStatusLevel(lvl);
    if (v === "critical_red" || v === "red") return 4;
    if (v === "amber") return 3;
    if (v === "light_green") return 2;
    if (v === "green") return 1;
    return 0;
  };
  const worstLevel = (...levels) =>
    levels
      .map((l) => normalizeStatusLevel(l))
      .sort((a, b) => severityRank(b) - severityRank(a))[0] || "unknown";
  const terminalOpsLevel = (() => {
    const w = worstLevel(canonicalFlightLevel, canonicalWeatherLevel);
    if (w === "red") return "amber";
    return w === "unknown" ? "green" : w;
  })();
  const checksStateLabel = (source, level) => {
    const lvl = String(level || "").toLowerCase();
    const hasRun = !!policyState?.last_monitor_at || result?.source_monitor_execution_id != null;
    if (source === "tfl" && (policyState?.tfl_relevant === false || !Array.isArray(monitorTargets?.tfl_crs) || monitorTargets.tfl_crs.length === 0)) {
      return "N/A";
    }
    if (!hasRun) return lvl && lvl !== "unknown" ? "Evaluated" : "Seeded";
    if (lvl === "amber" || lvl === "red") return "Change";
    return "Verified";
  };
  const checksRows = Array.isArray(projectionHero?.checks_rows) && projectionHero.checks_rows.length
    ? projectionHero.checks_rows.map((row) => ({
        key: row.key,
        label: row.label,
        level: row.level,
      }))
    : [
    { key: "road", label: "Road", level: canonicalRoadLevel },
    { key: "rail", label: "Rail", level: canonicalRailLevel },
    { key: "flight", label: "Flight", level: canonicalFlightLevel },
    { key: "weather", label: "Weather", level: canonicalWeatherLevel },
    { key: "tfl", label: "TfL", level: canonicalTflLevel },
  ];
  const tflRelevant =
    policyState?.tfl_relevant === true ||
    (Array.isArray(monitorTargets?.tfl_crs) && monitorTargets.tfl_crs.length > 0);
  const displayedChecksRows = checksRows.filter((row) => (row.key === "tfl" ? tflRelevant : true));
  const checksSummaryLine =
    String(projectionHero?.checks_summary_line || "").trim() ||
    displayedChecksRows.map((row) => `${row.label} ${checksStateLabel(row.key, row.level)}`).join(" • ");
  const roadsLine = Array.isArray(monitorTargets?.roads) && monitorTargets.roads.length ? monitorTargets.roads.join(", ") : null;
  const crsLine = Array.isArray(monitorTargets?.crs) && monitorTargets.crs.length ? monitorTargets.crs.join(", ") : null;
  const weatherLineTargets =
    Array.isArray(monitorTargets?.weather_locations) && monitorTargets.weather_locations.length
      ? monitorTargets.weather_locations.join(" + ")
      : null;
  const nextCheckDue = policyState?.next_check_due_at || policyState?.next_notify_due_at || null;
  const lastMonitorAt = policyState?.last_monitor_at || null;
  const dueSlotHours = policyState?.due_slot_hours;
  const phaseLabel = (() => {
    const slot = Number(dueSlotHours);
    if (!Number.isFinite(slot)) return null;
    if (slot <= 0) return "arrival window";
    return `${slot}h`;
  })();
  const hasContinuousMonitoring = !!journeyMeta?.email_verified;
  const toTitle = (s) =>
    String(s || "")
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  const ROAD_SIGNIFICANT_MIN_MINS = 5;
  const summarizeDisruptionReasons = (road) => {
    const d = road?.disruption || null;
    const reasons = Array.isArray(d?.reasons) ? d.reasons : [];
    const parts = [];
    if (d?.delay_mins != null && Number(d.delay_mins) > 0) {
      parts.push(`Delay +${Math.round(Number(d.delay_mins))} mins`);
    }
    const reasonText = reasons
      .filter((r) => String(r || "").toLowerCase() !== "normal_flow")
      .slice(0, 3)
      .map((r) =>
        String(r || "")
          .replace(/^nh_/, "NH ")
          .replace(/^google_/, "Google ")
          .replace(/_/g, " ")
      )
      .map((r) => toTitle(r))
      .join(" • ");
    if (reasonText) parts.push(reasonText);
    if (!parts.length && (d?.level === "amber" || d?.level === "red")) {
      parts.push("Live traffic slower than baseline");
    }
    return parts.join(" • ") || null;
  };
  const roadStatusWithLocation = (road, primaryRoad) => {
    const delay = Number(road?.disruption?.delay_mins);
    const hasDelay = Number.isFinite(delay) && delay > 0;
    const roadLabel = String(primaryRoad || "").trim() || "Route";
    if (hasDelay) return `${roadLabel}: +${Math.round(delay)} mins`;
    const generic = summarizeDisruptionReasons(road);
    return generic ? `${roadLabel}: ${generic}` : null;
  };
  const extractRoadTokens = (text = "") => {
    const matches = String(text || "").toUpperCase().match(/\b(?:M\d{1,3}[A-Z]?|A\d{1,4}[A-Z]?)\b/g) || [];
    return [...new Set(matches)];
  };
  const buildRoadSummary = (road) => {
    const durationByRoad = new Map();
    (road?.route_detail?.steps || []).forEach((step) => {
      const roadsInStep = extractRoadTokens(`${step?.instruction || ""} ${step?.name || ""}`);
      if (!roadsInStep.length) return;
      const uniqueRoads = [...new Set(roadsInStep)];
      const dur = Number(step?.duration_sec);
      const weight = Number.isFinite(dur) && dur > 0 ? dur / uniqueRoads.length : 0;
      uniqueRoads.forEach((r) => durationByRoad.set(r, (durationByRoad.get(r) || 0) + weight));
    });
    let roads = [...durationByRoad.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([roadCode]) => roadCode);
    if (!roads.length) {
      const motorways = Array.isArray(road?.corridor?.motorways_used) ? road.corridor.motorways_used : [];
      const aRoads = Array.isArray(road?.corridor?.a_roads_used) ? road.corridor.a_roads_used : [];
      roads = [...new Set([...motorways, ...aRoads])];
    }
    if (!roads.length) {
      const fallbackRoads = new Set();
      extractRoadTokens(road?.route_detail?.summary || "").forEach((r) => fallbackRoads.add(r));
      (road?.route_detail?.steps || []).forEach((s) =>
        extractRoadTokens(`${s?.instruction || ""} ${s?.name || ""}`).forEach((r) => fallbackRoads.add(r))
      );
      roads = [...fallbackRoads];
    }
    return {
      roads,
      mainRoad: roads[0] || null,
    };
  };
  const buildPrimaryRoadLeg = (road) => {
    const steps = Array.isArray(road?.route_detail?.steps) ? road.route_detail.steps : [];
    if (!steps.length) {
      const fallbackRoad = buildRoadSummary(road).mainRoad;
      if (!fallbackRoad) return null;
      return { road: fallbackRoad, enter_after_mins: 0, duration_mins: null };
    }
    const stats = new Map();
    let elapsedSec = 0;
    steps.forEach((step) => {
      const roads = extractRoadTokens(`${step?.instruction || ""} ${step?.name || ""}`);
      const stepSecRaw = Number(step?.duration_sec);
      const stepSec = Number.isFinite(stepSecRaw) && stepSecRaw > 0 ? stepSecRaw : 0;
      if (roads.length) {
        const unique = [...new Set(roads)];
        const share = stepSec > 0 ? stepSec / unique.length : 0;
        unique.forEach((r) => {
          const prev = stats.get(r) || { total_sec: 0, first_enter_sec: elapsedSec };
          prev.total_sec += share;
          if (elapsedSec < prev.first_enter_sec) prev.first_enter_sec = elapsedSec;
          stats.set(r, prev);
        });
      }
      elapsedSec += stepSec;
    });
    if (!stats.size) {
      const fallbackRoad = buildRoadSummary(road).mainRoad;
      if (!fallbackRoad) return null;
      return { road: fallbackRoad, enter_after_mins: 0, duration_mins: null };
    }
    const [roadCode, top] = [...stats.entries()].sort((a, b) => b[1].total_sec - a[1].total_sec)[0];
    return {
      road: roadCode,
      enter_after_mins: Math.max(0, Math.round(Number(top.first_enter_sec || 0) / 60)),
      duration_mins: Number.isFinite(Number(top.total_sec)) ? Math.max(1, Math.round(Number(top.total_sec) / 60)) : null,
    };
  };
  const bestMode = (() => {
    const m = hasReturnProjection ? projectionRecommendedMode : projectionRecommendedMode || String(recommendation?.recommended_mode || "").toLowerCase();
    if (m === "rail" || m === "train") return "Train";
    if (m === "self_drive") return "Self Drive";
    if (m === "taxi") return "Taxi";
    return null;
  })();
  const recommendedModeLevel = (() => {
    const m = String(recommendation?.recommended_mode || "").toLowerCase();
    if (m === "rail" || m === "train") return canonicalRailLevel;
    if (m === "self_drive" || m === "taxi") return canonicalRoadLevel;
    return null;
  })();
  const computedOverallFromSources = worstLevel(
    canonicalRoadLevel,
    canonicalRailLevel,
    canonicalTflLevel,
    canonicalWeatherLevel,
    canonicalFlightLevel
  );
  const level = normalizeStatusLevel(
    hasReturnProjection
      ? projectionHero?.journey_status || uiProjection?.overall_level || sourceLevels?.overall || "unknown"
      : projectionHero?.journey_status ||
          recommendation?.overall_disruption?.level ||
          sourceLevels?.overall ||
          recommendedModeLevel ||
          computedOverallFromSources ||
          "unknown"
  );
  const badgeColor = statusColor(level);
  const stationLabel = (crsOrLabel) => {
    const raw = String(crsOrLabel || "").trim();
    if (!raw) return "—";
    const key = raw.toUpperCase();
    if (/^[A-Z]{3}$/.test(key) && stationNames[key]) return `${stationNames[key]} (${key})`;
    return raw;
  };
  const stationNameFallback = {
    CRE: "Crewe",
    DBY: "Derby",
    TAM: "Tamworth",
    STP: "London St Pancras International",
    FST: "London Fenchurch Street",
    BSO: "Basildon",
    SHF: "Sheffield",
    BEE: "Beeston",
    VXH: "Vauxhall",
    IPS: "Ipswich",
    NRW: "Norwich",
    EMD: "East Midlands Parkway",
    LGE: "Long Eaton",
    NOT: "Nottingham",
    LIV: "Liverpool Lime Street",
    BHM: "Birmingham New Street",
    PAD: "London Paddington",
    WHC: "Walthamstow Central",
    LBG: "London Bridge",
    COV: "Coventry",
    LEI: "Leicester",
    NUN: "Nuneaton",
    TOM: "Tottenham Hale",
    ELY: "Ely",
    CBG: "Cambridge",
    PRY: "Perry Barr",
    WWA: "Woolwich Arsenal",
    WWC: "Woolwich (Elizabeth line)",
  };
  const stationLabelFriendly = (crsOrLabel) => {
    const raw = String(crsOrLabel || "").trim();
    if (!raw) return "—";
    const key = raw.toUpperCase();
    if (/^[A-Z]{3}$/.test(key)) {
      const name = stationNames[key] || stationNameFallback[key] || null;
      if (!name) return key;
      const clean = String(name).replace(/\bRail Station\b/i, "").trim();
      return clean;
    }
    const mCode = key.match(/\b([A-Z]{3})\b/);
    if (mCode?.[1]) {
      const code = mCode[1];
      const name = stationNames[code] || stationNameFallback[code] || null;
      if (name) return String(name).replace(/\bRail Station\b/i, "").trim();
    }
    return raw;
  };
  const stationLabelCompact = (crsOrLabel) => {
    const raw = String(crsOrLabel || "").trim();
    if (!raw) return "—";
    const key = raw.toUpperCase();
    if (/^[A-Z]{3}$/.test(key)) {
      const name = stationNames[key] || stationNameFallback[key] || null;
      return name ? String(name).replace(/\bRail Station\b/i, "").trim() : key;
    }
    const m = raw.match(/\(([A-Z]{3})\)/);
    if (m?.[1]) {
      const name = stationNames[m[1]] || stationNameFallback[m[1]] || null;
      return name ? String(name).replace(/\bRail Station\b/i, "").trim() : m[1];
    }
    return raw;
  };
  const normalizePlace = (v) =>
    String(v || "")
      .toLowerCase()
      .replace(/\brailway\b/g, "rail")
      .replace(/\bstation\b/g, "")
      .replace(/[^a-z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const stationAliasesForCrs = (crs) => {
    const key = String(crs || "").trim().toUpperCase();
    const aliases = [
      key,
      stationNames[key],
      stationLabelFriendly(key),
    ]
      .map(normalizePlace)
      .filter(Boolean);
    return [...new Set(aliases)];
  };
  const stopMatchesCrs = (stopName, crs) => {
    const key = String(crs || "").trim().toUpperCase();
    if (!key) return false;
    const raw = String(stopName || "");
    if (new RegExp(`\\(${key}\\)`, "i").test(raw)) return true;
    const stopNorm = normalizePlace(raw);
    if (!stopNorm) return false;
    return stationAliasesForCrs(key).some((alias) => {
      if (!alias) return false;
      return stopNorm === alias || stopNorm.includes(alias) || alias.includes(stopNorm);
    });
  };
  const deriveTransitSummaryFromExtractedLegs = (legsInput) => {
    const legs = Array.isArray(legsInput) ? legsInput : [];
    if (!legs.length) return null;
    const isRail = (l) => String(l?.type || "").toUpperCase() === "RAIL";
    const isTransit = (l) => String(l?.type || "").toUpperCase() !== "WALK";
    const firstRailIdx = legs.findIndex(isRail);
    let lastRailIdx = -1;
    for (let i = legs.length - 1; i >= 0; i -= 1) {
      if (isRail(legs[i])) {
        lastRailIdx = i;
        break;
      }
    }
    const sum = (arr, field) =>
      arr.reduce((s, l) => s + (Number.isFinite(Number(l?.[field])) ? Number(l[field]) : 0), 0) || null;
    const firstMileLegs = firstRailIdx > 0 ? legs.slice(0, firstRailIdx) : [];
    const lastMileLegs = lastRailIdx >= 0 ? legs.slice(lastRailIdx + 1) : [];
    const firstMileTransit = firstMileLegs.find(isTransit) || null;
    const lastMileTransit = [...lastMileLegs].find(isTransit) || null;
    const firstRailLeg = firstRailIdx >= 0 ? legs[firstRailIdx] : null;
    const lastRailLeg = lastRailIdx >= 0 ? legs[lastRailIdx] : null;
    const transitLegs = legs.filter(isTransit);
    const interchange_hubs = [];
    for (let i = 0; i < transitLegs.length - 1; i += 1) {
      const prev = transitLegs[i];
      const next = transitLegs[i + 1];
      const prevTo = String(prev?.to || "").trim();
      const nextFrom = String(next?.from || "").trim();
      if (!prevTo || !nextFrom || prevTo.toLowerCase() !== nextFrom.toLowerCase()) continue;
      interchange_hubs.push({
        hub: prevTo,
        from_mode: prev?.type || null,
        to_mode: next?.type || null,
        from_route: prev?.route || null,
        to_route: next?.route || null,
        arrive_at_utc: prev?.arrival_time_utc || null,
        depart_at_utc: next?.departure_time_utc || null,
      });
    }
    const railLegs = transitLegs.filter(isRail);
    return {
      rail_changes: Math.max(0, railLegs.length - 1),
      first_mile: {
        duration_mins: sum(firstMileLegs, "duration_mins"),
        distance_meters: sum(firstMileLegs, "distance_meters"),
        mode: firstMileTransit?.type || (firstMileLegs.length ? "WALK" : null),
        route: firstMileTransit?.route || null,
        agency: firstMileTransit?.agency || null,
        headsign: firstMileTransit?.headsign || null,
        from: firstMileLegs[0]?.from || null,
        to: firstRailLeg?.from || firstMileLegs[firstMileLegs.length - 1]?.to || null,
        from_stop_code: firstMileTransit?.from_stop_code || null,
        to_stop_code: firstMileTransit?.to_stop_code || null,
        depart_at_utc: firstMileTransit?.departure_time_utc || null,
        arrive_at_utc: firstRailLeg?.departure_time_utc || firstMileTransit?.arrival_time_utc || null,
      },
      last_mile: {
        duration_mins: sum(lastMileLegs, "duration_mins"),
        distance_meters: sum(lastMileLegs, "distance_meters"),
        mode: lastMileTransit?.type || (lastMileLegs.length ? "WALK" : null),
        route: lastMileTransit?.route || null,
        agency: lastMileTransit?.agency || null,
        headsign: lastMileTransit?.headsign || null,
        from: lastRailLeg?.to || lastMileLegs[0]?.from || null,
        to: lastMileLegs[lastMileLegs.length - 1]?.to || null,
        from_stop_code: lastMileTransit?.from_stop_code || null,
        to_stop_code: lastMileTransit?.to_stop_code || null,
        depart_at_utc: lastMileTransit?.departure_time_utc || lastRailLeg?.arrival_time_utc || null,
        arrive_at_utc: lastMileTransit?.arrival_time_utc || null,
      },
      interchange_hubs,
    };
  };
  const googleTransitSummary =
    rail?.google_transit?.snapshot?.timeline_summary ||
    deriveTransitSummaryFromExtractedLegs(rail?.google_transit?.snapshot?.extracted_legs) ||
    result?.diagnostics?.google_transit || null;
  const firstMileSummaryRaw = googleTransitSummary?.first_mile || null;
  const lastMileSummaryRaw = googleTransitSummary?.last_mile || null;
  const interchangeHubSummary = Array.isArray(googleTransitSummary?.interchange_hubs) ? googleTransitSummary.interchange_hubs : [];
  const buildTransitLegNotes = (leg, fallbackDurationMins = null) => {
    if (!leg) {
      return Number.isFinite(Number(fallbackDurationMins)) ? [`${Number(fallbackDurationMins)} mins`] : [];
    }
    const notes = [];
    const mode = String(leg?.mode || "").trim();
    const route = String(leg?.route || "").trim();
    const agency = String(leg?.agency || "").trim();
    const headsign = String(leg?.headsign || "").trim();
    const extractStopCode = (name) => {
      const m = String(name || "").trim().match(/\(([^)]+)\)\s*$/);
      return m?.[1] ? String(m[1]).trim() : null;
    };
    const fromStopCode = String(leg?.from_stop_code || extractStopCode(leg?.from) || "").trim();
    const toStopCode = String(leg?.to_stop_code || extractStopCode(leg?.to) || "").trim();
    const fromStopName = String(leg?.from || "").trim();
    const toStopName = String(leg?.to || "").trim();
    const departAt = leg?.depart_at_utc || null;
    const arriveAt = leg?.arrive_at_utc || null;
    const durationMins = Number.isFinite(Number(leg?.duration_mins))
      ? Number(leg.duration_mins)
      : Number.isFinite(Number(fallbackDurationMins))
      ? Number(fallbackDurationMins)
      : null;
    const modeText = mode ? toTitle(mode) : "Transit";
    const lineText = route ? `${modeText} ${route}` : modeText;
    if (lineText) notes.push(agency ? `${lineText} (${agency})` : lineText);
    if (fromStopName) notes.push(`Board at: ${fromStopName}${fromStopCode && !fromStopName.includes(`(${fromStopCode})`) ? ` (${fromStopCode})` : ""}`);
    if (toStopName) notes.push(`Alight at: ${toStopName}${toStopCode && !toStopName.includes(`(${toStopCode})`) ? ` (${toStopCode})` : ""}`);
    if (headsign) notes.push(`Direction: ${headsign}`);
    if (departAt || arriveAt) {
      notes.push(`Times: ${formatTime(departAt)} → ${formatTime(arriveAt)}`);
    }
    if (durationMins != null) notes.push(`${durationMins} mins`);
    return notes.filter(Boolean);
  };
  const findPrimaryInterchange = () => {
    if (!interchangeHubSummary.length) return null;
    const withWait = interchangeHubSummary
      .filter((h) => Number.isFinite(Number(h?.change_wait_mins)))
      .sort((a, b) => Number(b.change_wait_mins) - Number(a.change_wait_mins));
    return withWait[0] || interchangeHubSummary[0] || null;
  };

  const railSegRail = rail?.segments?.find((s) => s.id === "rail");
  const railSegHop = rail?.segments?.find((s) => s.id === "first_mile") || rail?.segments?.find((s) => s.id === "terminal_to_gateway");
  const railSegLast = rail?.segments?.find((s) => s.id === "airport_hop") || rail?.segments?.find((s) => s.id === "last_mile");
  const railJourney = rail?.best_candidate?.journeys?.[0] || null;
  const gatewayCrs =
    rail?.request?.gateway_crs ||
    rail?.request?.resolved_dest_crs ||
    rail?.request?.dest_crs ||
    rail?.best_candidate?.selected_station ||
    railSegHop?.to ||
    "Gateway";
    const homeStation =
      railSegRail?.to ||
      railJourney?.legs?.[railJourney?.legs?.length - 1]?.to ||
      railJourney?.legs?.[railJourney?.legs?.length - 1]?.to_crs ||
      "Home CRS";
  const firstMileStationAligned = firstMileSummaryRaw
    ? firstMileSummaryRaw?.gateway_name_match === true || stopMatchesCrs(firstMileSummaryRaw?.to, gatewayCrs)
    : false;
  const firstMileTimeAligned = (() => {
    if (!firstMileSummaryRaw?.arrive_at_utc || !railJourney?.depart) return true;
    const tArr = new Date(firstMileSummaryRaw.arrive_at_utc).getTime();
    const tDep = new Date(railJourney.depart).getTime();
    if (!Number.isFinite(tArr) || !Number.isFinite(tDep)) return true;
    return tArr <= tDep - 2 * 60000;
  })();
  const firstMileSummary = firstMileSummaryRaw && firstMileStationAligned && firstMileTimeAligned ? firstMileSummaryRaw : null;
  const lastMileStationAligned = lastMileSummaryRaw
    ? stopMatchesCrs(lastMileSummaryRaw?.from, homeStation)
    : false;
  const lastMileTimeAligned = (() => {
    if (!lastMileSummaryRaw?.depart_at_utc || !railJourney?.arrive) return true;
    const tDep = new Date(lastMileSummaryRaw.depart_at_utc).getTime();
    const tArr = new Date(railJourney.arrive).getTime();
    if (!Number.isFinite(tDep) || !Number.isFinite(tArr)) return true;
    return tDep >= tArr - 2 * 60000;
  })();
  const lastMileSummary = lastMileSummaryRaw && lastMileStationAligned && lastMileTimeAligned ? lastMileSummaryRaw : null;
  const railFare = rail?.best_candidate?.fare?.total_gbp != null ? Number(rail.best_candidate.fare.total_gbp) : null;
  const railEta = railJourney?.duration_mins != null ? Number(railJourney.duration_mins) + Number(railSegHop?.eta_safe_mins || 0) + Number(railSegLast?.eta_safe_mins || 0) : null;

  const taxiEta = taxi?.road?.eta_safe_mins ?? taxi?.road?.eta_best_mins ?? null;
  const taxiFare = taxi?.fare?.price != null ? Number(taxi.fare.price) : null;
  const taxiArriveHome = terminalExitAt && taxiEta != null ? addMins(terminalExitAt, taxiEta) : null;

  const driveTransferRaw = Number(drive?.parking?.all?.[0]?.transfer_minutes ?? 10);
  const driveTransfer = Number.isFinite(driveTransferRaw) && driveTransferRaw > 0 ? driveTransferRaw : 5;
  const driveRoadEta = drive?.road?.eta_safe_mins ?? drive?.road?.eta_best_mins ?? null;
  const driveEta = driveRoadEta != null ? Number(driveRoadEta) + Number(driveTransfer || 0) : null;
  const driveFuel = drive?.fuel?.fuel?.fuelCost != null ? Number(drive.fuel.fuel.fuelCost) : null;
  const driveFuelFallback = (() => {
    const km = drive?.road?.distance_km != null ? Number(drive.road.distance_km) : null;
    if (!Number.isFinite(km) || km <= 0) return null;
    const ppl = 1.45;
    const mpg = 45;
    const litresPer100 = (4.54609 / (mpg / 0.621371)) * 100;
    const cost = (litresPer100 / 100) * km * ppl;
    return Number.isFinite(cost) ? Number(cost.toFixed(2)) : null;
  })();
  const resolvedDriveFuel = driveFuel ?? driveFuelFallback;
  const driveArriveHome = terminalExitAt && driveEta != null ? addMins(terminalExitAt, driveEta) : null;

  const normalizeWeatherError = (msg) => {
    const s = String(msg || "").trim();
    if (!s) return null;
    if (/(timeout|timed out|exceeded|failed|error|econn|enotfound|network)/i.test(s)) {
      return "Weather data temporarily unavailable";
    }
    return s;
  };
  const weatherLine = hasReturnProjection
    ? projectionHero?.weather_line || "Weather data temporarily unavailable"
    : projectionHero?.weather_line ||
      rail?.weather?.ema?.summary ||
      rail?.weather?.home?.summary ||
      taxi?.weather?.ema?.summary ||
      taxi?.weather?.home?.summary ||
      drive?.weather?.ema?.summary ||
      drive?.weather?.home?.summary ||
      rail?.weather?.risk?.advice ||
      taxi?.weather?.risk?.advice ||
      drive?.weather?.risk?.advice ||
      normalizeWeatherError(rail?.weather?.error) ||
      normalizeWeatherError(taxi?.weather?.error) ||
      normalizeWeatherError(drive?.weather?.error) ||
      "Weather data temporarily unavailable";
  const driveRoadSummary = buildRoadSummary(drive?.road);
  const taxiRoadSummary = buildRoadSummary(taxi?.road);
  const drivePrimaryRoadLeg = buildPrimaryRoadLeg(drive?.road);
  const taxiPrimaryRoadLeg = buildPrimaryRoadLeg(taxi?.road);
  const drivePrimaryRoad = drivePrimaryRoadLeg?.road || driveRoadSummary.mainRoad || null;
  const taxiPrimaryRoad = taxiPrimaryRoadLeg?.road || taxiRoadSummary.mainRoad || null;
  const driveReason = summarizeDisruptionReasons(drive?.road);
  const taxiReason = summarizeDisruptionReasons(taxi?.road);
  const driveReasonLocalized = roadStatusWithLocation(drive?.road, drivePrimaryRoad);
  const taxiReasonLocalized = roadStatusWithLocation(taxi?.road, taxiPrimaryRoad);
  const trainSegments = (() => {
    if (!rail) return null;
    const railLevel = rail?.overall_disruption?.level || "unknown";
    return [
      { label: "EMA", level: railLevel },
      { label: stationLabelCompact(gatewayCrs), level: railLevel },
      { label: stationLabelCompact(homeStation), level: railLevel },
      { label: viewForm?.pickup_postcode || "Home", level: railLevel },
    ];
  })();
  const driveSegments = (() => {
    if (!drive) return null;
    const level = drive?.road?.disruption?.level || drive?.road?.status || "unknown";
    const parkingLabel = drive?.parking?.all?.[0]?.product_name || "Parking";
    const affected = level === "amber" || level === "red";
    return [
      { label: "EMA", level: affected ? "light_green" : level },
      { label: parkingLabel, level: affected ? "light_green" : level },
      ...(drivePrimaryRoad ? [{ label: drivePrimaryRoad, level }] : []),
      { label: viewForm?.pickup_postcode || "Home", level: affected ? "light_green" : level },
    ];
  })();
  const taxiSegments = (() => {
    if (!taxi) return null;
    const level = taxi?.road?.disruption?.level || taxi?.road?.status || "unknown";
    const affected = level === "amber" || level === "red";
    return [
      { label: "EMA", level: affected ? "light_green" : level },
      ...(taxiPrimaryRoad ? [{ label: taxiPrimaryRoad, level }] : []),
      { label: viewForm?.pickup_postcode || "Home", level: affected ? "light_green" : level },
    ];
  })();
  const segmentRow = (segments) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
      {segments?.map((s, idx) => (
        <React.Fragment key={`${s.label}_${idx}`}>
          {idx > 0 && <span style={{ color: "#94a3b8" }}>→</span>}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#cbd5e1", fontSize: 11, whiteSpace: "nowrap" }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: statusColor(s.level) }} />
            <span>{s.label}</span>
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  const timelineFor = (mode) => {
    const projectedTimeline = projectionTimelineForTitle(mode);
    const projectedRoadSections = projectionRoadSectionsForTitle(mode);
    if (projectedRoadSections?.length) {
      return {
        roadSections: projectedRoadSections,
        timeline: Array.isArray(projectedTimeline)
          ? projectedTimeline.map((step, idx) => ({
              key: step?.id || `${String(mode || "").toLowerCase()}_${idx + 1}`,
              at: step?.at || null,
              title: step?.title || null,
              status: step?.status || "unknown",
              from: step?.from || null,
              to: step?.to || null,
              durationMins: Number.isFinite(Number(step?.duration_mins)) ? Number(step.duration_mins) : null,
              notes: Array.isArray(step?.notes) ? step.notes : [],
            }))
          : [],
      };
    }
    if (projectedTimeline) {
      return { timeline: projectedTimeline.map((step, idx) => ({
        key: step?.id || `${String(mode || "").toLowerCase()}_${idx + 1}`,
        at: step?.at || null,
        title: step?.title || null,
        status: step?.status || "unknown",
        from: step?.from || null,
        to: step?.to || null,
        notes: Array.isArray(step?.notes) ? step.notes : [],
      })) };
    }
    if (hasReturnProjection) {
      const projectionCard = projectionCardForTitle(mode);
      return { timeline: [
        {
          key: `${String(mode || "").toLowerCase()}_projection_unavailable`,
          at: null,
          title: `${mode} details unavailable`,
          status: "unknown",
          from: null,
          to: null,
          notes: [
            projectionCard?.unavailability_message ||
              projectionCard?.summary ||
              "Projection details are temporarily unavailable for this mode.",
          ],
        },
      ] };
    }
    const flightNo = String(viewForm?.flight_number || "Flight").toUpperCase();
    const flightOrigin = String(flightOriginCity || "Origin").toUpperCase();
    const flightState = (() => {
      const s = String(flightStatusBadge?.label || "").toLowerCase();
      if (s.includes("delay")) return "Delayed";
      if (s.includes("cancel")) return "Cancelled";
      return "On time";
    })();
    const sortByAt = (steps) =>
      [...steps].sort((a, b) => {
        const ta = a?.at ? new Date(a.at).getTime() : Number.POSITIVE_INFINITY;
        const tb = b?.at ? new Date(b.at).getTime() : Number.POSITIVE_INFINITY;
        if (!Number.isFinite(ta) && !Number.isFinite(tb)) return 0;
        if (!Number.isFinite(ta)) return 1;
        if (!Number.isFinite(tb)) return -1;
      if (ta !== tb) return ta - tb;
      const priority = {
        land: 1,
        immigration: 2,
        baggage: 3,
        exit: 4,
        drive_to_parking: 5,
        first_mile: 5,
        rail_depart_0: 6,
        drive_depart: 6,
        taxi_depart: 6,
      };
        const pa = Number.isFinite(priority[a?.key]) ? priority[a.key] : 999;
        const pb = Number.isFinite(priority[b?.key]) ? priority[b.key] : 999;
        return pa - pb;
      });
    const minsBetween = (a, b) => {
      if (!a || !b) return null;
      const ta = new Date(a).getTime();
      const tb = new Date(b).getTime();
      if (!Number.isFinite(ta) || !Number.isFinite(tb) || tb <= ta) return null;
      return Math.round((tb - ta) / 60000);
    };
    const buildRoadMilestones = (road) => {
      const out = [];
      const steps = Array.isArray(road?.route_detail?.steps) ? road.route_detail.steps : [];
      if (!steps.length) return out;
      const seen = new Set();
      const stats = new Map();
      let elapsedSec = 0;
      steps.forEach((step) => {
        const tokens = extractRoadTokens(`${step?.instruction || ""} ${step?.name || ""}`);
        const uniq = [...new Set(tokens)];
        const relevant = uniq.filter((r) => /^M\d/i.test(r) || /^A\d/i.test(r));
        const durSecRaw = Number(step?.duration_sec);
        const durSec = Number.isFinite(durSecRaw) && durSecRaw > 0 ? durSecRaw : 0;
        if (relevant.length) {
          const share = durSec > 0 ? durSec / relevant.length : 0;
          relevant.forEach((r, idx) => {
            const firstSeenSec =
              elapsedSec + (durSec > 0 ? (idx * durSec) / Math.max(1, relevant.length) : idx * 60);
            const prev = stats.get(r) || { totalSec: 0, firstSec: firstSeenSec };
            prev.totalSec += share;
            if (firstSeenSec < prev.firstSec) prev.firstSec = firstSeenSec;
            stats.set(r, prev);
          });
          relevant.forEach((r) => {
            if (!seen.has(r)) {
              seen.add(r);
              out.push(r);
            }
          });
        }
        elapsedSec += durSec;
      });
      const byFirstSeen = out
        .map((roadCode) => {
          const d = stats.get(roadCode);
          return {
            road: roadCode,
            enterAfterMins: Number.isFinite(Number(d?.firstSec)) ? Math.max(0, Math.floor(Number(d.firstSec) / 60)) : 0,
            durationMins: Number.isFinite(Number(d?.totalSec)) ? Math.max(1, Math.round(Number(d.totalSec) / 60)) : null,
          };
        })
        .filter((r) => Number.isFinite(Number(r.durationMins)) && Number(r.durationMins) >= ROAD_SIGNIFICANT_MIN_MINS)
        .sort((a, b) => a.enterAfterMins - b.enterAfterMins);
      // If two roads still resolve to the same minute bucket, nudge later items to keep
      // a strict chronological sequence in the timeline UI.
      const tieSafe = [];
      let lastMinute = -1;
      byFirstSeen.forEach((m) => {
        const nextMinute = m.enterAfterMins <= lastMinute ? lastMinute + 1 : m.enterAfterMins;
        tieSafe.push({ ...m, enterAfterMins: nextMinute });
        lastMinute = nextMinute;
      });
      const finalApproach = [...stats.entries()]
        .map(([roadCode, d]) => ({
          road: roadCode,
          enterAfterMins: Number.isFinite(Number(d?.firstSec)) ? Math.max(0, Math.floor(Number(d.firstSec) / 60)) : 0,
          durationMins: Number.isFinite(Number(d?.totalSec)) ? Math.max(1, Math.round(Number(d.totalSec) / 60)) : null,
        }))
        .filter((r) => /^A\d/i.test(r.road))
        .sort((a, b) => b.enterAfterMins - a.enterAfterMins)[0];
      return { byFirstSeen: tieSafe, finalApproach };
    };
    const returnGatewayDefaults = (() => {
      const g = String(gatewayCrs || "").toUpperCase();
      if (g === "EMD") return { mins: 12, fare: 32 };
      if (g === "DBY") return { mins: 22, fare: 45 };
      if (g === "NOT") return { mins: 30, fare: 46 };
      if (g === "LGE") return { mins: 35, fare: 36 };
      return { mins: null, fare: null };
    })();
    const common = [
      {
        key: "land",
        at: flightAnchor,
        title: `${flightNo} lands from ${flightOrigin}`,
        status: flightStatusBadge?.level || "green",
        notes: [flightState],
      },
      {
        key: "immigration",
        at: immigrationAt,
        title: "Passport control",
        status: terminalOpsLevel === "green" ? "light_green" : terminalOpsLevel,
        notes: ["Allow 15-30 mins"],
      },
      {
        key: "baggage",
        at: baggageAt,
        title: "Baggage collection",
        status:
          arrivalOps?.carousel_source === "live"
            ? terminalOpsLevel
            : terminalOpsLevel === "green"
            ? "light_green"
            : terminalOpsLevel,
        notes: [arrivalOps?.likely_carousel ? `${arrivalOps.likely_carousel}` : "Carousel: check airport screens"],
      },
      {
        key: "exit",
        at: terminalExitAt,
        title: "Terminal exit",
        status: terminalOpsLevel,
        notes: ["Ready to leave terminal"],
      },
    ];
    if (mode === "Train") {
      const trainSteps = [];
      const googleHopMins = Number(firstMileSummary?.duration_mins);
      const useGoogleFirstMile = !!firstMileSummary && Number.isFinite(googleHopMins) && googleHopMins > 0 && googleHopMins <= 75;
      const hopMins = useGoogleFirstMile
        ? googleHopMins
        : Number.isFinite(Number(returnGatewayDefaults.mins))
        ? Number(returnGatewayDefaults.mins)
        : null;
      const firstMileDepartAt = useGoogleFirstMile ? firstMileSummary?.depart_at_utc || terminalExitAt || railJourney?.depart || null : terminalExitAt || railJourney?.depart || null;
      const firstMode = String(firstMileSummary?.mode || "").toUpperCase();
      const firstRoute = String(firstMileSummary?.route || "").toLowerCase();
      const firstAction =
        useGoogleFirstMile && firstMode === "BUS" && firstRoute.includes("skylink")
          ? "Skylink bus to"
          : useGoogleFirstMile && firstMode === "WALK"
          ? "Walk to"
          : "Taxi to";
      const gatewayStation = stationLabelFriendly(gatewayCrs);
      if (firstMileDepartAt) {
        const firstDurationMins =
          Number.isFinite(Number(hopMins)) ? Number(hopMins) : Number.isFinite(Number(returnGatewayDefaults.mins)) ? Number(returnGatewayDefaults.mins) : null;
        const firstTitle = `${firstAction} ${gatewayStation}${Number.isFinite(Number(firstDurationMins)) ? ` • ${formatDuration(Number(firstDurationMins))}` : ""}`;
        trainSteps.push({
          key: "first_mile",
          at: firstMileDepartAt,
          title: firstTitle,
          status: canonicalRailLevel,
          notes: [
            `EMA Terminal -> ${gatewayStation}`,
            firstAction.startsWith("Taxi")
              ? Number.isFinite(Number(railSegHop?.cost_est_gbp))
                ? `Est. fare ~£${Math.round(Number(railSegHop.cost_est_gbp))}`
                : Number.isFinite(Number(returnGatewayDefaults.fare))
                ? `Est. fare ~£${Math.round(Number(returnGatewayDefaults.fare))}`
                : null
              : null,
          ],
        });
      }
      const legs = Array.isArray(railJourney?.legs) ? railJourney.legs : [];
      legs.forEach((leg, idx) => {
        const dep = leg?.sched_dep || leg?.dep || leg?.departure || null;
        const arr = leg?.sched_arr || leg?.arr || leg?.arrival || null;
        const fromStation = stationLabelFriendly(leg?.from || leg?.from_crs || gatewayCrs);
        const toStation = stationLabelFriendly(leg?.to || leg?.to_crs || homeStation);
        const durationMins = Number.isFinite(Number(leg?.duration_mins)) ? Number(leg.duration_mins) : minsBetween(dep, arr);
        trainSteps.push({
          key: `rail_depart_${idx}`,
          at: dep,
          title: `Depart ${fromStation}`,
          status: canonicalRailLevel,
          notes: [`Train to ${toStation}${Number.isFinite(Number(durationMins)) ? ` • ${formatDuration(Number(durationMins))}` : ""}`],
        });
        if (idx < legs.length - 1) {
          const nextDep = legs[idx + 1]?.sched_dep || legs[idx + 1]?.dep || legs[idx + 1]?.departure || null;
          const waitMins = minsBetween(arr, nextDep);
          trainSteps.push({
            key: `rail_arrive_${idx}`,
            at: arr,
            title: `Arrive ${toStation}${Number.isFinite(Number(waitMins)) ? ` (change ${waitMins} mins)` : ""}`,
            status: canonicalRailLevel,
            notes: [],
          });
        }
      });
      const lastMins = lastMileSummary?.duration_mins ?? railSegLast?.eta_safe_mins ?? railSegLast?.walk_mins ?? null;
      const lastMileDepartAt = lastMileSummary?.depart_at_utc || railJourney?.arrive || null;
      const lastFrom = stationLabelFriendly(lastMileSummary?.from || homeStation);
      const lastMode = String(lastMileSummary?.mode || "").toUpperCase();
      const lastTo = formatUkPostcode(viewForm?.pickup_postcode || "") || "Home";
      trainSteps.push({
        key: "last_mile",
        at: lastMileDepartAt,
        title:
          lastMode === "WALK"
            ? `Walk to home${Number.isFinite(Number(lastMins)) && Number(lastMins) > 0 ? ` • ${formatDuration(Number(lastMins))}` : ""}`
            : lastMode === "BUS"
            ? `Bus to home${Number.isFinite(Number(lastMins)) && Number(lastMins) > 0 ? ` • ${formatDuration(Number(lastMins))}` : ""}`
            : `Taxi to home${Number.isFinite(Number(lastMins)) && Number(lastMins) > 0 ? ` • ${formatDuration(Number(lastMins))}` : ""}`,
        status: canonicalRailLevel,
        notes: [
          `${lastFrom} -> ${lastTo}`,
          lastMode === "WALK" || lastMode === "BUS"
            ? null
            : Number.isFinite(Number(railSegLast?.cost_est_gbp))
            ? `Est. fare ~£${Math.round(Number(railSegLast.cost_est_gbp))}`
            : null,
        ].filter(Boolean),
      });
      return { timeline: sortByAt([...trainSteps, ...common]) };
    }
    if (mode === "Self Drive") {
      const steps = [];
      const roadEtaMins = Number.isFinite(Number(driveRoadEta)) ? Number(driveRoadEta) : null;
      const transferMins =
        Number.isFinite(Number(driveTransfer)) && Number(driveTransfer) > 0 ? Number(driveTransfer) : 3;
      const departFromParkingAt = terminalExitAt ? addMins(terminalExitAt, transferMins) : null;
      const arriveHomeAt = departFromParkingAt && Number.isFinite(Number(roadEtaMins)) ? addMins(departFromParkingAt, roadEtaMins) : null;
      const milestones = buildRoadMilestones(drive?.road);
      const parkingLabel = drive?.parking?.all?.[0]?.product_name || "Short Stay 1";
      steps.push({
        key: "drive_to_parking",
        at: terminalExitAt,
        title: `Walk to parking${Number.isFinite(Number(transferMins)) && Number(transferMins) > 0 ? ` • ${formatDuration(Number(transferMins))}` : ""}`,
        status: terminalOpsLevel,
        notes: ["EMA Terminal -> parking"],
      });
      steps.push({
        key: "drive_depart",
        at: departFromParkingAt,
        title: "Depart EMA (self-drive)",
        status: canonicalRoadLevel,
        notes: [`EMA -> ${formatUkPostcode(viewForm?.pickup_postcode || "") || "Home"}${Number.isFinite(Number(roadEtaMins)) ? ` • ${formatDuration(Number(roadEtaMins))}` : ""}`],
      });
      milestones.byFirstSeen.forEach((m, idx) => {
        steps.push({
          key: `drive_road_${idx}`,
          at: departFromParkingAt ? addMins(departFromParkingAt, m.enterAfterMins) : null,
          title: `On ${m.road}${Number.isFinite(Number(m.durationMins)) && Number(m.durationMins) > 0 ? ` • ${formatDuration(Number(m.durationMins))}` : ""}`,
          status: canonicalRoadLevel,
          notes: [],
        });
      });
      if (milestones.finalApproach?.road) {
        steps.push({
          key: "drive_final_approach",
          at: departFromParkingAt ? addMins(departFromParkingAt, milestones.finalApproach.enterAfterMins) : null,
          title: `Final approach to home via ${milestones.finalApproach.road}`,
          status: canonicalRoadLevel,
          notes: [`${milestones.finalApproach.road} -> ${formatUkPostcode(viewForm?.pickup_postcode || "") || "Home"}`],
        });
      }
      steps.push({
        key: "drive_arrive_home",
        at: arriveHomeAt,
        title: "Arrive home",
        status: canonicalRoadLevel,
        notes: [formatUkPostcode(viewForm?.pickup_postcode || "") || "Home"],
      });
      return { timeline: sortByAt([...steps, ...common]) };
    }
    const steps = [];
    const roadEtaMins = Number.isFinite(Number(taxiEta)) ? Number(taxiEta) : null;
    const departEmaAt = terminalExitAt || null;
    const arriveHomeAt = departEmaAt && Number.isFinite(Number(roadEtaMins)) ? addMins(departEmaAt, roadEtaMins) : null;
    const milestones = buildRoadMilestones(taxi?.road);
    steps.push({
      key: "taxi_depart",
      at: departEmaAt,
      title: "Depart EMA (taxi)",
      status: canonicalRoadLevel,
      notes: [`EMA -> ${formatUkPostcode(viewForm?.pickup_postcode || "") || "Home"}${Number.isFinite(Number(roadEtaMins)) ? ` • ${formatDuration(Number(roadEtaMins))}` : ""}`],
    });
    milestones.byFirstSeen.forEach((m, idx) => {
      steps.push({
        key: `taxi_road_${idx}`,
        at: departEmaAt ? addMins(departEmaAt, m.enterAfterMins) : null,
        title: `On ${m.road}${Number.isFinite(Number(m.durationMins)) && Number(m.durationMins) > 0 ? ` • ${formatDuration(Number(m.durationMins))}` : ""}`,
        status: canonicalRoadLevel,
        notes: [],
      });
    });
    if (milestones.finalApproach?.road) {
      steps.push({
        key: "taxi_final_approach",
        at: departEmaAt ? addMins(departEmaAt, milestones.finalApproach.enterAfterMins) : null,
        title: `Final approach to home via ${milestones.finalApproach.road}`,
        status: canonicalRoadLevel,
        notes: [`${milestones.finalApproach.road} -> ${formatUkPostcode(viewForm?.pickup_postcode || "") || "Home"}`],
      });
    }
    steps.push({
      key: "taxi_arrive_home",
      at: arriveHomeAt,
      title: "Arrive home",
      status: canonicalRoadLevel,
      notes: [formatUkPostcode(viewForm?.pickup_postcode || "") || "Home"],
    });
    return { timeline: sortByAt([...steps, ...common]) };
  };
  const routeRoadLabels = (summary, primary) => {
    const labels = [];
    if (primary?.road) labels.push(primary.road);
    (summary?.roads || []).forEach((r) => {
      if (!r || labels.includes(r)) return;
      labels.push(r);
    });
    return labels.slice(0, 2);
  };
  const monitoringItemsFor = (mode) => {
    if (mode === "Train") {
      const railLeg = gatewayCrs && homeStation ? `${gatewayCrs} -> ${homeStation}` : "gateway -> home CRS";
      return [
        "Flight arrival status",
        `EMA -> ${gatewayCrs} rail disruption feed`,
        `${railLeg} live rail status`,
        "Station departure windows",
        "Final-mile traffic",
      ];
    }
    const roadLabels =
      mode === "Self Drive"
        ? routeRoadLabels(driveRoadSummary, drivePrimaryRoadLeg)
        : routeRoadLabels(taxiRoadSummary, taxiPrimaryRoadLeg);
    const roadIncidentsLine =
      roadLabels.length > 0
        ? `${roadLabels.join(" / ")} incident feed (NH)`
        : "Motorway/A-road incident feed (NH)";
    return [
      "Flight arrival status",
      "Baggage/terminal processing windows",
      roadIncidentsLine,
      "Live traffic flow (Google)",
      "Weather alerts",
    ];
  };

  const projectedCards = [
    { key: "train", title: "Train" },
    { key: "self_drive", title: "Self Drive" },
    { key: "taxi", title: "Taxi" },
  ]
    .map(({ key, title }) => {
      const card = projectionCards?.[key];
      if (!card) {
        return hasReturnProjection
          ? {
              title,
              body: null,
              status: "unknown",
              metric1: null,
              metric2: null,
              metric3: null,
              selectedTrain: null,
              reason: null,
              compactInsight: null,
              monitoringItems: null,
              segments: null,
              available: false,
            }
          : null;
      }
      return {
        title,
        body: card?.body || null,
        status: card?.display_level || card?.status || "unknown",
        metric1: card?.metric1 || null,
        metric2: card?.metric2 || null,
        metric3: card?.metric3 || null,
        selectedTrain: card?.selectedTrain || null,
        reason: card?.compact_insight || card?.reason || null,
        compactInsight: card?.compact_insight || null,
        monitoringItems: Array.isArray(card?.monitoring_items) ? card.monitoring_items : null,
        segments: Array.isArray(card?.segments) ? card.segments : null,
        available: card?.available !== false,
      };
    })
    .filter(Boolean);
  const cards = hasReturnProjection ? projectedCards : [
    {
      title: "Train",
      body: "EMA arrival to gateway CRS, rail to home CRS, then last mile home.",
      status: canonicalRailLevel,
      metric1: `Exit terminal: ${terminalExitAt ? formatTime(terminalExitAt) : "—"}`,
      metric2: `Fare: ${railFare != null ? `£${railFare.toFixed(2)}` : "—"}`,
      metric3: `Duration: ${railEta != null ? formatDuration(railEta) : "—"}`,
      selectedTrain:
        railJourney?.depart && railJourney?.arrive
          ? `${formatTime(railJourney.depart)} → ${formatTime(railJourney.arrive)} (${stationLabelCompact(gatewayCrs)} → ${stationLabelCompact(homeStation)})`
          : null,
      reason: null,
      segments: trainSegments,
      available: !!railJourney,
    },
    {
      title: "Self Drive",
      body: "Terminal to parking, then drive home with live road status.",
      status: canonicalRoadLevel,
      metric1: `Exit terminal: ${terminalExitAt ? formatTime(terminalExitAt) : "—"}`,
      metric2: `Cost: ${resolvedDriveFuel != null ? `£${resolvedDriveFuel.toFixed(2)}` : "—"}`,
      metric3: `Duration: ${driveEta != null ? formatDuration(driveEta) : "—"}`,
      reason: driveReasonLocalized || driveReason,
      segments: driveSegments,
      available: true,
    },
    {
      title: "Taxi",
      body: "Terminal pickup to home postcode with live disruption checks.",
      status: canonicalRoadLevel,
      metric1: `Exit terminal: ${terminalExitAt ? formatTime(terminalExitAt) : "—"}`,
      metric2: `Fare: ${taxiFare != null ? `£${taxiFare.toFixed(2)}` : "—"}`,
      metric3: `Duration: ${taxiEta != null ? formatDuration(taxiEta) : "—"}`,
      reason: taxiReasonLocalized || taxiReason,
      segments: taxiSegments,
      available: true,
    },
  ];
  const [selectedMode, setSelectedMode] = useState(bestMode || cards[0]?.title || "Train");
  useEffect(() => {
    const fallback = bestMode || "Train";
    setSelectedMode(fallback);
    setOpenDetailMode(null);
  }, [bestMode]);
  const visibleReturnCards = isCompactMobile ? cards.filter((c) => c.title === selectedMode) : cards;
  const modeDomId = (mode) => String(mode || "").toLowerCase().replace(/\s+/g, "-");
  const toggleDetail = (mode) => {
    setOpenDetailMode((prev) => {
      const next = prev === mode ? null : mode;
      if (next && typeof document !== "undefined") {
        setTimeout(() => {
          const detailEl = document.querySelector(`[data-ema-detail="return-${modeDomId(mode)}"]`);
          detailEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
      }
      return next;
    });
  };
  const openHeroTimeChainDetail = (mode) => {
    const targetMode = mode || bestMode || selectedMode || "Train";
    setSelectedMode(targetMode);
    setOpenDetailMode(targetMode);
    if (typeof document !== "undefined") {
      setTimeout(() => {
        const detailEl = document.querySelector(`[data-ema-detail="return-${modeDomId(targetMode)}"]`);
        detailEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };
  const railReachHomeAt =
    hasReturnProjection
      ? projectionHeroStepAt("home") || null
      : projectionHeroStepAt("home") ||
        lastMileSummary?.arrive_at_utc ||
        (railJourney?.arrive && Number.isFinite(Number(railSegLast?.eta_safe_mins))
          ? addMins(railJourney.arrive, Number(railSegLast.eta_safe_mins))
          : railJourney?.arrive || null);
  const heroPrimaryMode = bestMode || selectedMode || "Train";
  const heroReachHomeAt = hasReturnProjection
    ? projectionHeroStepAt("home") || null
    : heroPrimaryMode === "Train"
      ? railReachHomeAt
      : heroPrimaryMode === "Self Drive"
      ? driveArriveHome
      : taxiArriveHome;
  const heroTerminalAt = hasReturnProjection ? projectionHeroStepAt("terminal") || null : terminalExitAt || null;
  const heroFlightAt = hasReturnProjection ? projectionHeroStepAt("flight") || uiProjection?.anchor_utc || null : flightAnchor || null;
  const heroTimeSteps = Array.isArray(projectionHero?.time_steps) && projectionHero.time_steps.length
    ? projectionHero.time_steps.map((step) => ({
        id: step.id,
        icon: step.id === "flight" ? "✈️" : step.id === "terminal" ? "🎯" : "🏠",
        label: step.label,
        time: step.at,
      }))
    : hasReturnProjection
      ? [
          {
            id: "flight",
            icon: "✈️",
            label: "Flight arrives",
            time: heroFlightAt,
          },
          {
            id: "terminal",
            icon: "🎯",
            label: "Terminal exit",
            time: heroTerminalAt,
          },
          {
            id: "home",
            icon: "🏠",
            label: "Reach home",
            time: heroReachHomeAt,
          },
        ]
      : [
    {
      id: "flight",
      icon: "✈️",
      label: "Flight arrives",
      time: heroFlightAt,
    },
    {
      id: "terminal",
      icon: "🎯",
      label: "Terminal exit",
      time: heroTerminalAt,
    },
    {
      id: "home",
      icon: "🏠",
      label: "Reach home",
      time: heroReachHomeAt,
    },
  ];
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: isCompactMobile ? 12 : 16, background: "#0f172a", color: "#e2e8f0", width: "100%", maxWidth: "100%", boxSizing: "border-box", margin: 0 }}>
      <div style={{ fontWeight: 800, fontSize: 20, color: "#e2e8f0" }}>Live Travel Recommendation</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span>
          {hasReturnProjection
            ? projectionHero?.flight_line || `${viewForm?.flight_number || "Flight"}`
            : projectionHero?.flight_line || `${viewForm?.flight_number || "Flight"} — ${formatDate(flightAnchor)} ${formatTime(flightAnchor)}`}
        </span>
        {flightStatusBadge?.label && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 8px",
              borderRadius: 999,
              border: `1px solid ${statusColor(flightStatusBadge.level)}66`,
              color: statusColor(flightStatusBadge.level),
              fontWeight: 700,
              fontSize: 11,
              lineHeight: 1.1,
              background: `${statusColor(flightStatusBadge.level)}1A`,
            }}
            title={flightStatusBadge?.raw_status || ""}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor(flightStatusBadge.level) }} />
            {flightStatusBadge.label}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: `${badgeColor}22`, color: badgeColor, fontWeight: 700, fontSize: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: badgeColor }} />
          Journey status: {String(level || "unknown").replace("_", " ")}
        </div>
        <div style={{ fontSize: 13, color: "#cbd5e1" }}>Weather: {weatherLine}</div>
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}>
        {hasReturnProjection
          ? projectionHero?.recommendation_summary || "Return journey plan from EMA to home postcode."
          : projectionHero?.recommendation_summary || recommendation?.summary || "Return journey plan from EMA to home postcode."}
      </div>
      {routeHeadline && <div style={{ marginTop: 4, fontSize: 13, color: "#cbd5e1", fontWeight: 600 }}>🛬 {routeHeadline}</div>}
      {isCompactMobile ? (
        <button
          type="button"
          onClick={() => openHeroTimeChainDetail(heroPrimaryMode)}
          style={{
            marginTop: 8,
            width: "100%",
            border: "1px solid #334155",
            background: "#0b1220",
            color: "#cbd5e1",
            borderRadius: 10,
            padding: "8px 8px",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr auto 1fr",
            alignItems: "center",
            justifyItems: "center",
            columnGap: 6,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            textAlign: "center",
            overflow: "hidden",
          }}
          title="Open full timeline"
        >
          <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.1 }}>Flight arrives</span>
            <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 800 }}>✈️ {heroFlightAt ? formatTime(heroFlightAt) : "—"}</span>
          </span>
          <span style={{ color: "#475569", fontSize: 12 }}>→</span>
          <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.1 }}>Terminal exit</span>
            <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 800 }}>🎯 {heroTerminalAt ? formatTime(heroTerminalAt) : "—"}</span>
          </span>
          <span style={{ color: "#475569", fontSize: 12 }}>→</span>
          <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.1 }}>Reach home</span>
            <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 800 }}>🏠 {heroReachHomeAt ? formatTime(heroReachHomeAt) : "—"}</span>
          </span>
        </button>
      ) : (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", overflowX: "auto", paddingBottom: 2 }}>
          {heroTimeSteps.map((step, idx) => (
            <React.Fragment key={`hero-time-return-${step.id}`}>
              <button
                type="button"
                onClick={() => openHeroTimeChainDetail(heroPrimaryMode)}
                style={{
                  border: "1px solid #334155",
                  background: "#0b1220",
                  color: "#cbd5e1",
                  borderRadius: 999,
                  padding: "6px 10px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  width: "auto",
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                }}
                title="Open full timeline"
              >
                <span>{step.icon}</span>
                <span>{step.label}</span>
                <span style={{ color: "#e2e8f0" }}>{step.time ? formatTime(step.time) : "—"}</span>
              </button>
              {idx < heroTimeSteps.length - 1 && <span style={{ color: "#475569", fontSize: 12, flex: "0 0 auto" }}>→</span>}
            </React.Fragment>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10, border: "1px solid #1f2937", borderRadius: 10, background: "#0b1220", padding: "8px 10px", display: "grid", gap: 6 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color: "#a7f3d0" }}>
            {hasContinuousMonitoring ? "Checks Live" : "Current Snapshot"}
          </span>
          <div style={{ fontSize: 12, color: "#cbd5e1", fontWeight: 700 }}>
            {checksSummaryLine}
          </div>
        </div>
        <details>
          <summary style={{ cursor: "pointer", color: "#93c5fd", fontSize: 11, fontWeight: 700 }}>
            {hasContinuousMonitoring ? "View monitored routes" : "View route coverage"}
          </summary>
          <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {displayedChecksRows.map((row) => {
              const lvl = normalizeStatusLevel(row.level);
              const chip = statusColor(lvl);
              return (
                <span
                  key={`return-route-status-${row.key}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "2px 8px",
                    borderRadius: 999,
                    border: `1px solid ${chip}66`,
                    background: `${chip}22`,
                    color: chip,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "capitalize",
                  }}
                >
                  {row.label} {lvl.replace("_", " ")}
                </span>
              );
            })}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>
            {[
              roadsLine ? `Roads: ${roadsLine}` : null,
              crsLine ? `Rail CRS: ${crsLine}` : null,
              weatherLineTargets ? `Weather: ${weatherLineTargets}` : null,
            ]
              .filter(Boolean)
              .join(" • ") || "Journey targets seeded"}
          </div>
        </details>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          {[nextCheckDue ? `Next check: ${formatDate(nextCheckDue)} ${formatTime(nextCheckDue)}` : "Next check: pending", phaseLabel ? `Phase: ${phaseLabel}` : null, lastMonitorAt ? `Last run: ${formatDate(lastMonitorAt)} ${formatTime(lastMonitorAt)}` : null]
            .filter(Boolean)
            .join(" • ")}
        </div>
        {!hasContinuousMonitoring && (
          <div style={{ fontSize: 11, color: "#fcd34d", fontWeight: 700 }}>
            Verify your email to activate live journey monitoring and disruption alerts.
          </div>
        )}
      </div>
      {isCompactMobile && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 5, marginTop: 10, width: "100%" }}>
          {cards.map((c) => {
            const active = selectedMode === c.title;
            const tabLabel = c.title === "Self Drive" ? "Self Drive" : c.title;
            return (
              <button
                key={`tab-return-${c.title}`}
                type="button"
                onClick={() => {
                  setSelectedMode(c.title);
                  setOpenDetailMode(null);
                }}
                style={{
                  borderRadius: 999,
                  border: active ? "1px solid #22d3ee" : "1px solid #334155",
                  background: active ? "#0f2d45" : "#0b1626",
                  color: active ? "#67e8f9" : "#cbd5e1",
                  fontWeight: 800,
                  fontSize: 10.5,
                  padding: "7px 4px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                {tabLabel}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gap: isCompactMobile ? 8 : 10, gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(auto-fit,minmax(220px,1fr))", marginTop: 12, minWidth: 0 }}>
        {visibleReturnCards.map((c) => {
          const statusLabel = c.title === "Train" ? "Rail status" : "Road status";
          const statusLevel = c.status || "unknown";
          const exitLabel = hasReturnProjection ? c.metric1 || "Exit: —" : c.metric1 || `Exit: ${terminalExitAt ? formatTime(terminalExitAt) : "—"}`;
          const costLabel = hasReturnProjection ? c.metric2 || "Cost: —" : c.metric2 || "Cost: —";
          const durationLabel = String(hasReturnProjection ? c.metric3 || "Duration: —" : c.metric3 || "Duration: —").replace(/^Duration:\s*/i, "");
          const trainInsight =
            railJourney?.depart && railJourney?.arrive
              ? `Selected ${formatTime(railJourney.depart)}→${formatTime(railJourney.arrive)}`
              : rail?.no_rail_reason || "No rail options found for return journey.";
          const driveInsight =
            projectionCardForTitle(c.title)?.compact_insight ||
            (Number.isFinite(Number(driveTransfer))
              ? `Pickup parking: ${Number(driveTransfer)} mins walk (${drive?.parking?.all?.[0]?.product_name || "Short Stay 1"})`
              : "Pickup parking monitored");
          const taxiEtaSignal =
            statusLevel === "green" || statusLevel === "light_green"
              ? "ETA stable"
              : statusLevel === "amber"
              ? "possible delay"
              : statusLevel === "red"
              ? "disruption risk"
              : "ETA monitored";
          const taxiInsight = `${taxiPrimaryRoad ? `Route via ${taxiPrimaryRoad}` : "Route monitored"} • ${taxiEtaSignal}`;
          const compactInsight = hasReturnProjection
            ? c.compactInsight || c.reason || "Projection summary unavailable for this mode."
            : c.compactInsight ||
              (c.title === "Train" ? trainInsight : c.title === "Self Drive" ? driveInsight : taxiInsight);
          return (
          <div
            key={c.title}
            style={{
              borderRadius: 14,
              padding: isCompactMobile ? 10 : 12,
              minWidth: 0,
              background: bestMode === c.title ? "linear-gradient(180deg, #11263a 0%, #111827 58%)" : "linear-gradient(180deg, #10263d 0%, #0b1726 100%)",
              border: bestMode === c.title ? "1px solid #22d3ee" : "1px solid #47688d",
              boxShadow:
                bestMode === c.title
                  ? "0 0 0 1px rgba(34,211,238,0.35), 0 12px 28px rgba(34,211,238,0.14)"
                  : "inset 0 0 0 1px rgba(125,211,252,0.2), 0 0 0 1px rgba(71,104,141,0.45), 0 2px 0 rgba(148,163,184,0.2), 0 10px 24px rgba(2,6,23,0.42)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#e2e8f0" }}>{c.title}</div>
              {bestMode === c.title && (
                <div style={{ padding: "2px 8px", borderRadius: 999, background: "#22d3ee33", color: "#06b6d4", fontSize: 11, fontWeight: 800 }}>
                  Recommended
                </div>
              )}
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, background: `${statusColor(statusLevel)}22`, color: statusColor(statusLevel), fontWeight: 700, fontSize: 12 }}>
                {statusLabel}: {statusLevel}
              </span>
            </div>
            {!c.available && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
                {c.compactInsight || c.reason || (c.title === "Train" ? "Rail plan unavailable for this request." : "Projection data unavailable for this mode.")}
              </div>
            )}
            {c.available && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#cbd5e1", lineHeight: 1.6, display: "grid", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#e2e8f0" }}>
                  <span style={{ fontSize: 16 }}>🕒</span>
                  <span style={{ fontWeight: 800 }}>{exitLabel}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>💷</span>
                  <span>{costLabel}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>⏱️</span>
                  <span>Duration: {durationLabel}</span>
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: c.title === "Train" && !railJourney?.depart ? "#94a3b8" : "#cbd5e1" }}>
                  {compactInsight}
                </div>
              </div>
            )}
            <button
              type="button"
              style={{
                marginTop: 12,
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid #1f2937",
                background: "#0ea5e915",
                color: "#0ea5e9",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
              }}
              onClick={() => toggleDetail(c.title)}
            >
              {openDetailMode === c.title ? "Hide details ↑" : "View details →"}
            </button>
            {openDetailMode === c.title && (
              <div data-ema-detail={`return-${modeDomId(c.title)}`} style={{ marginTop: 10, borderTop: "1px solid #1f2937", paddingTop: 10, display: "grid", gap: 10 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.45, border: "1px solid #1f2937", borderRadius: 8, padding: "6px 8px", background: "#0b1220" }}>
                  <div style={{ fontWeight: 700, color: "#cbd5e1" }}>Monitoring active (registered users, up to flight day)</div>
                  <div>• {(Array.isArray(c.monitoringItems) && c.monitoringItems.length ? c.monitoringItems : monitoringItemsFor(c.title)).join(" • ")}</div>
                </div>
                {(() => {
                  const detail = timelineFor(c.title);
                  const roadSections = Array.isArray(detail?.roadSections) ? detail.roadSections : [];
                  const timelineRows = (detail?.timeline || []).filter((step) => {
                    if (!roadSections.length) return true;
                    const stepKeyNorm = String(step?.key || step?.id || "").toLowerCase();
                    return !(
                      stepKeyNorm.startsWith("drive_") ||
                      stepKeyNorm.startsWith("self_drive_") ||
                      stepKeyNorm.startsWith("taxi_") ||
                      stepKeyNorm === "walk_to_parking" ||
                      stepKeyNorm === "first_mile" ||
                      stepKeyNorm === "last_mile" ||
                      stepKeyNorm === "final_approach" ||
                      stepKeyNorm === "arrive_parking" ||
                      stepKeyNorm === "arrive_terminal" ||
                      stepKeyNorm === "arrive_home"
                    );
                  });
                  return (
                    <>
                      {timelineRows.map((s) => (
                    <div key={s.key} style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "start" }}>
                      <div style={{ fontWeight: 800, color: "#e2e8f0" }}>{s.at ? formatTime(s.at) : "—"}</div>
                      <div style={{ borderLeft: "2px solid #334155", paddingLeft: 10 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 800, fontSize: 13, color: "#e2e8f0" }}>{s.title}</span>
                          <span style={{ fontSize: 10, borderRadius: 999, border: `1px solid ${statusColor(s.status)}77`, color: statusColor(s.status), padding: "1px 7px", textTransform: "capitalize", fontWeight: 700 }}>
                            {s.status || "unknown"}
                          </span>
                        </div>
                        {(() => {
                          const sub = s.from && s.to ? `${s.from} -> ${s.to}` : s.from || s.to || "";
                          const txt = String(sub || "").trim();
                          if (!txt || txt === "— -> —") return null;
                          return <div style={{ fontSize: 12, color: "#cbd5e1" }}>{txt}</div>;
                        })()}
                        {s.notes?.length > 0 && <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>{s.notes.filter(Boolean).join(" • ")}</div>}
                      </div>
                    </div>
                      ))}
                      {roadSections.map((section, sectionIdx) => {
                        const sectionId = section?.id || `return_road_section_${sectionIdx}`;
                        const isOpen = openReturnRoadSectionId === sectionId;
                        const sectionSteps = Array.isArray(section?.steps) ? section.steps : [];
                        return (
                          <div key={sectionId} style={{ border: "1px solid #1f2937", borderRadius: 10, padding: "10px 12px", background: "#0b1220" }}>
                            <button
                              type="button"
                              onClick={() => setOpenReturnRoadSectionId(isOpen ? null : sectionId)}
                              style={{ width: "100%", background: "transparent", border: "none", padding: 0, textAlign: "left", cursor: "pointer" }}
                            >
                              <div style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "start" }}>
                                <div style={{ fontWeight: 800, color: "#e2e8f0" }}>{section?.at ? formatTime(section.at) : "—"}</div>
                                <div>
                                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: 800, fontSize: 13, color: "#e2e8f0" }}>{section?.title || `Road section ${sectionIdx + 1}`}</span>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        borderRadius: 999,
                                        border: `1px solid ${statusColor(section?.status || section?.display_level)}77`,
                                        color: statusColor(section?.status || section?.display_level),
                                        padding: "1px 7px",
                                        textTransform: "capitalize",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {String(section?.display_level || section?.status || "unknown").replace(/_/g, " ")}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 2 }}>
                                    {[section?.summary || null, Number.isFinite(Number(section?.duration_mins)) ? `${Math.round(Number(section.duration_mins))} mins` : null].filter(Boolean).join(" • ")}
                                  </div>
                                  <div style={{ marginTop: 4, fontSize: 11, color: "#93c5fd", fontWeight: 700 }}>{isOpen ? "Hide steps" : "View steps"}</div>
                                </div>
                              </div>
                            </button>
                            {isOpen && sectionSteps.length > 0 && (
                              <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                                {sectionSteps.map((step, stepIdx) => (
                                  <div key={step?.id || `return_road_step_${sectionIdx}_${stepIdx}`} style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "start" }}>
                                    <div style={{ fontWeight: 800, color: "#e2e8f0" }}>{step?.at ? formatTime(step.at) : "—"}</div>
                                    <div style={{ borderLeft: "2px solid #334155", paddingLeft: 10 }}>
                                      <div style={{ fontWeight: 800, fontSize: 13, color: "#e2e8f0" }}>
                                        {String(step?.instruction || "Step").replace("Continue through airport approach and parking access", "Airport access and parking buffer")}
                                      </div>
                                      <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                                        {[step?.primary_road || null, Number.isFinite(Number(step?.duration_mins)) ? `${Math.round(Number(step.duration_mins))} mins` : null].filter(Boolean).join(" • ")}
                                      </div>
                                      {Array.isArray(step?.notes) && step.notes.length > 0 && (
                                        <div style={{ marginTop: 2, fontSize: 11, color: "#94a3b8" }}>{step.notes.join(" • ")}</div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function SecureJourneyAccessStrip({
  journeyMeta,
  setJourneyMeta,
  verificationCode,
  setVerificationCode,
  onSendVerificationEmail,
  onVerifyCode,
}) {
  const isCompactMobile = useCompactMobile(768);
  const secureLink = journeyMeta.token
    ? `${window.location.origin}/east-midlands-airport/return-journey-monitoring?token=${encodeURIComponent(journeyMeta.token)}`
    : "";
  const stripState = journeyMeta.email_verified
    ? "verified"
    : journeyMeta.email_sent
    ? "code"
    : "email";
  const heading = stripState === "verified" ? "Live Monitoring Active" : "Enable Live Journey Monitoring";
  const subline =
    stripState === "verified"
      ? "Live alerts enabled for flight, rail and road changes."
      : "Get alerts for flight, rail and road changes on the journey home from EMA. Unverified journeys are not actively monitored.";

  const openSecureLink = () => {
    if (!secureLink) return;
    window.open(secureLink, "_blank", "noopener,noreferrer");
  };
  const copySecureLink = async () => {
    if (!secureLink) return;
    try {
      await navigator.clipboard.writeText(secureLink);
      setJourneyMeta((prev) => ({ ...prev, note: "Secure link copied." }));
    } catch {
      setJourneyMeta((prev) => ({ ...prev, note: "Copy failed. Please copy manually." }));
    }
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          border: "1px solid #1f2937",
          borderRadius: 12,
          background: "#0b1220",
          color: "#d1d5db",
          padding: isCompactMobile ? 8 : 10,
          display: "grid",
          gap: 6,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: "#a7f3d0", textTransform: "uppercase", letterSpacing: 0.4 }}>
          {heading}
        </div>
        <div style={{ fontSize: 12, color: stripState === "verified" ? "#cbd5e1" : "#fbbf24", fontWeight: stripState === "verified" ? 500 : 600 }}>{subline}</div>
        {stripState === "email" && (
          <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "minmax(220px,1fr) auto", gap: 8 }}>
            <input
              className="parking-input"
              placeholder="Enter your email"
              value={journeyMeta.email || ""}
              onChange={(e) => setJourneyMeta((prev) => ({ ...prev, email: e.target.value }))}
              style={{ minHeight: 40, borderRadius: 10, fontSize: isCompactMobile ? 14 : 16, paddingRight: 8, width: "100%" }}
            />
            <button
              type="button"
              onClick={onSendVerificationEmail}
              disabled={!journeyMeta.token || journeyMeta.emailing}
              style={{
                border: "1px solid #065f46",
                background: "#064e3b",
                color: "#d1fae5",
                borderRadius: 10,
                padding: isCompactMobile ? "0 8px" : "0 12px",
                minHeight: 40,
                minWidth: isCompactMobile ? 84 : 110,
                fontSize: isCompactMobile ? 11 : 12,
                fontWeight: 700,
                whiteSpace: "nowrap",
                width: isCompactMobile ? "100%" : "auto",
              }}
            >
              {journeyMeta.emailing ? "Sending..." : "Send code"}
            </button>
          </div>
        )}

        {stripState === "code" && (
          <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "minmax(0,1fr) auto" : "minmax(180px,1fr) auto auto", gap: 8 }}>
            <input
              className="parking-input"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              style={{ minHeight: 40, borderRadius: 10, fontSize: isCompactMobile ? 14 : 16, paddingRight: 8 }}
            />
            <button
              type="button"
              onClick={onVerifyCode}
              disabled={journeyMeta.verifying}
              style={{
                border: "1px solid #334155",
                background: "#1e293b",
                color: "#e2e8f0",
                borderRadius: 10,
                padding: isCompactMobile ? "0 8px" : "0 12px",
                minHeight: 40,
                minWidth: isCompactMobile ? 72 : 92,
                fontSize: isCompactMobile ? 11 : 12,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {journeyMeta.verifying ? "Verifying..." : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => {
                setVerificationCode("");
                setJourneyMeta((prev) => ({ ...prev, email_sent: false, note: "Re-enter email address." }));
              }}
              style={{
                border: "1px solid #334155",
                background: "#0f172a",
                color: "#cbd5e1",
                borderRadius: 10,
                minHeight: 34,
                padding: "0 10px",
                fontSize: 11,
                fontWeight: 700,
                gridColumn: isCompactMobile ? "1 / -1" : "auto",
              }}
            >
              Change email
            </button>
          </div>
        )}

        {stripState === "verified" && (
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ fontSize: 13, color: "#34d399", fontWeight: 700 }}>Email verified. Your secure journey link is active.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={copySecureLink}
                style={{ border: "1px solid #334155", background: "#0f172a", color: "#e2e8f0", borderRadius: 10, minHeight: 34, padding: "0 10px", fontSize: 11, fontWeight: 700 }}
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={openSecureLink}
                style={{ border: "1px solid #0ea5e9", background: "#0ea5e915", color: "#0ea5e9", borderRadius: 10, minHeight: 34, padding: "0 10px", fontSize: 11, fontWeight: 700 }}
              >
                Open journey
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
